"use client";

import Link from "next/link";
import { createPortal } from "react-dom";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type {
  AccessibilityFinding,
  AnalyzeResult,
  BestPracticeFinding,
  CapturedPage,
  HeuristicFinding,
  HeuristicSeverity,
  JobState,
  Shot,
  SitePresence,
  SitePresenceCheck,
  Viewport,
} from "../../../lib/audit-types";
import { copyShareLink, createShareLink } from "../../../lib/share";
import { track } from "../../../lib/track";
import { BRAND, START_AUDIT_HREF } from "../../../constants";
import {
  type CustomAnnotation as CustomAnnotationApi,
  type CustomAnnotationDraft,
  ApiError,
  appendPageToAudit,
  authorizeAuditPdfExport,
  createCustomAnnotation,
  deleteCustomAnnotation,
  fetchAuditLayout,
  listCustomAnnotations,
  saveAuditLayout,
  updateCustomAnnotation,
} from "../../../lib/api";
import { readToken } from "../../../lib/auth";
import { FeedbackDialog } from "./feedback-dialog";
import { ThemeToggle } from "../../../components/theme-toggle";
import { openUpgradeInNewTab } from "../../components/setting-label";
import { FigmaOriginChip } from "../../components/figma-origin-chip";

/* ================================================================== */
/*  Layout constants — mirror plugin/src/code.ts for consistent        */
/*  positioning between Figma render and web canvas.                    */
/* ================================================================== */

const PAGE_PAD = 80;
const SECTION_GAP = 200;

/* A "blocked" capture whose real cause was the SITE'S SERVER being down or
   asleep (5xx), not bot protection. Free-tier hosts (Render, Railway, Fly)
   nap after ~15 idle minutes and answer the wake-up visit with a 503, so
   these runs heal on their own — the copy must say "try again in a minute",
   never "bot protection blocked you" (shipped incident: a guest's first-ever
   run was told exactly that). New runs carry reasonCode; older stored runs
   only have the reason string the classifier has always written ("Server
   error (503)"), so match that as a fallback and old runs render honestly
   too. */
function isServerDownQuality(
  q: CapturedPage["captureQuality"] | undefined,
): boolean {
  if (!q || q.status !== "blocked") return false;
  if (q.reasonCode) return q.reasonCode === "server-error";
  return /^server error \(\d{3}\)/i.test(q.reason ?? "");
}
// Generous gap between the desktop shot and the findings panel now that
// the panel tucks directly under the desktop capture.
const ANALYSIS_GAP = 88;
const SUMMARY_WIDTH = 540;

const DESKTOP_SOLO_WIDTH = 900;
const MOBILE_SOLO_WIDTH = 360;
// Horizontal gap between side-by-side viewport blocks (desktop | mobile) in
// a multi-viewport audit, measured between one block's right callout column
// and the next block's left callout column.
const VIEWPORT_SIDE_GAP = 40;

const CALLOUT_CARD_WIDTH = 260;
const CALLOUT_GAP = 48;
const CALLOUT_STACK_GAP = 16;
const MAX_SCREENSHOT_ANNOTATIONS = 8;
const MAX_CALLOUTS_PER_SIDE = 5;

// The bottom analysis panel (the three finding columns) is a fixed width
// regardless of the shot's viewport. Widened so the three columns aren't
// cramped; it stays well left of the side-by-side mobile block (which
// starts ~1864px into the page block), so there's no collision.
const ANALYSIS_PANEL_LEFT = 200;
const ANALYSIS_PANEL_WIDTH = 1180;

const DOT_SIZE = 18;
const MIN_SCALE = 0.05;
const MAX_SCALE = 4;

// "1" once the one-time feedback prompt has been shown on this device —
// whether it auto-fired after 15s on a canvas or the user opened it from
// the left rail's "Give feedback" button. The automatic nudge never fires
// twice; the button keeps working regardless.
const FEEDBACK_PROMPT_KEY = "snap-site:cv-feedback-prompted";

// Triggers a browser download for a Blob (the server-rendered PDF).
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ================================================================== */
/*  Severity                                                            */
/* ================================================================== */

const SEVERITY_COLOR: Record<HeuristicSeverity, string> = {
  high: "#dc2626",
  medium: "#ed9417",
  low: "#2e78b8",
};

const SEVERITY_LABEL: Record<HeuristicSeverity, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

const SEVERITY_RANK: Record<HeuristicSeverity, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

/* ── First-pass framing ──────────────────────────────────────────────
   One shared vocabulary so every surface agrees: an audit is an
   "automated first pass" (what we run) → "manual review" (what a human
   adds) → compliance (neither alone). We don't advertise gaps on the
   page; the detail lives behind an ⓘ tooltip and in the export footer.
   "Accessibility (WCAG 2.2 A/AA)" and "usability" replace the jargon
   acronyms WCAG / NN/g so non-specialists can read it. */
const MANUAL_REVIEW_TIP =
  "This score reflects everything we can check automatically across " +
  "accessibility and usability. A human expert will still catch nuances " +
  "no tool can — so treat it as a strong starting point, not the final word.\n\n" +
  "---\n\n" +
  "Please note:\n" +
  "Some accessibility issues simply can't be found by any automatic " +
  "tool — only a real person can catch them. Worth checking by hand:\n\n" +
  "• Can you use the whole site with just a keyboard, no mouse?\n" +
  "• Are error messages easy to spot and understand?\n" +
  "• Does the page read clearly out loud for screen readers?";

const AUDIT_CAVEAT =
  "Automated first pass — not a compliance certification. Covers " +
  "accessibility (WCAG 2.2 A/AA) and usability; some criteria need manual review.";

type SeverityFilter = "all" | "high" | "medium" | "low";

/* ================================================================== */
/*  Types                                                               */
/* ================================================================== */

interface AuditAnnotation {
  viewport: Viewport;
  title: string;
  observation: string;
  severity: HeuristicSeverity;
  bbox: { x: number; y: number; width: number; height: number };
  source:
    | HeuristicFinding
    | AccessibilityFinding
    | BestPracticeFinding
    | CustomAnnotationApi;
  kind: "heuristic" | "accessibility" | "bestpractice" | "custom";
  /** Global top-to-bottom sequence index. Used for ordering math; not
   *  shown to users (the typed `label` is what they see on pins and
   *  cards). Retained because various sort/filter helpers compare on
   *  it and changing them all to use label would mean substring math. */
  number: number;
  /** Typed pin label rendered on the canvas dot, rail badge, and
   *  bottom-analysis card. Prefix is H for heuristic, A for
   *  accessibility, C for custom; the integer is the per-kind sequence
   *  in top-to-bottom order. Example: "H1", "A3", "C2". Distinguishing
   *  finding type at-a-glance is the whole point — readers can say
   *  "look at H3" without ambiguity. */
  label: string;
  /** Whether this annotation gets a callout card (text box + connector).
      Every bbox'd finding gets a numbered dot, but only the top picks per
      shot get a card to keep the canvas readable. */
  hasCard: boolean;
  /** Category derived from heuristic name or WCAG principle */
  category: string;
  /** AI-generated "why it matters" explanation */
  whyItMatters: string;
  /** AI-generated suggested fix */
  suggestedFix: string;
  /** Set when kind === "custom" — used to PATCH/DELETE on the backend. */
  customId?: string;
  /** Guest-teaser: true when the backend stripped this finding's text for a
      logged-out read. The panel + callout render a locked placeholder instead
      of title/observation, and clicks invoke onUnlock. */
  locked?: boolean;
}

interface ShotLayout {
  shot: Shot;
  width: number;
  height: number;
  isGridMode: boolean;
  annotations: AuditAnnotation[];
  /** Horizontal offset of this shot's wrapper within the page block.
      Multi-viewport audits sit side-by-side, each as a full solo block
      (shot + its own callout-card wings) so the cards never collide. */
  offsetX: number;
  /** Total height of this shot incl. its tallest callout-card column —
      the page block is as tall as the tallest shot. */
  blockHeight: number;
}

interface PageLayout {
  page: CapturedPage;
  index: number;
  x: number;
  y: number;
  shotsX: number;
  shotsY: number;
  shotsWidth: number;
  shotsHeight: number;
  /** Block-relative top for the analysis/findings panel. Anchored below
      the DESKTOP shot (not the tallest shot) so the panel sits in the
      desktop column instead of far below the much-taller mobile capture.
      Falls back to below-tallest for mobile-only audits. */
  analysisTop: number;
  width: number;
  height: number;
  shots: ShotLayout[];
  isSolo: boolean;
  hasAudit: boolean;
}

/* ================================================================== */
/*  Public component                                                    */
/* ================================================================== */

// Filter by finding category (the three AI categories) or "custom"; "all"
// = no filter. (Kept the name `sourceFilter` to avoid churn — it now carries
// the category rather than a coarse AI/Custom split.)
type SourceFilter =
  | "all"
  | "heuristic"
  | "accessibility"
  | "bestpractice"
  | "custom";

interface PendingPlacement {
  pageIndex: number;
  viewport: Viewport;
  bbox: { x: number; y: number; width: number; height: number };
}

interface FormSeed {
  title: string;
  severity: HeuristicSeverity;
  description: string;
  // Either a placement (create) or an existing custom (edit).
  placement?: PendingPlacement;
  editingId?: string;
}

export type CanvasMode = "owner" | "viewer" | "locked";

/**
 * Source/type label shown next to severity on every finding card and
 * callout, e.g. "High · Usability" or "Medium · Accessibility". Mapping
 * follows the audit-workspace spec:
 *
 *   - Custom annotations from users          → "Custom"
 *   - Accessibility findings (WCAG-driven)   → "Accessibility"
 *   - Heuristic findings: keyword match on   → most-specific category
 *     the annotation's category              → falls back to "Usability"
 *
 * Order of category checks matters — more specific terms win first.
 */
function sourceLabelFor(a: AuditAnnotation): string {
  if (a.kind === "custom") return "Custom";
  if (a.kind === "accessibility") return "Accessibility";
  if (a.kind === "bestpractice") return "Best practice";

  const cat = (a.category || "").toLowerCase();
  if (/\b(form|input|valid|label)/.test(cat)) return "Forms";
  if (/\b(nav|menu|footer|sitemap|structure)/.test(cat)) return "Navigation";
  if (/\b(trust|social proof|testimonial|review)/.test(cat)) return "Trust";
  if (/\b(cta|pricing|signup|conversion|action)/.test(cat)) return "Conversion";
  if (/\b(messag|copy|content|value prop|clarity|wording)/.test(cat)) return "Content";
  if (/\b(hierarchy|emphasis|spacing|layout|visual|prominence|contrast)/.test(cat)) return "Visual hierarchy";
  if (/\b(mobile|desktop|responsive|breakpoint|viewport)/.test(cat)) return "Responsive";
  return "Usability";
}

/**
 * The "Heuristic" line in the expanded finding detail. For usability
 * findings this is the named heuristic from Nielsen Norman Group's
 * canonical list. For accessibility findings it's the WCAG rule.
 * Custom annotations have no underlying rule.
 */
function heuristicLineFor(a: AuditAnnotation): string | null {
  if (a.kind === "heuristic") {
    const src = a.source as HeuristicFinding;
    return src.heuristicName
      ? `${src.heuristicName} (NN/g heuristic)`
      : null;
  }
  if (a.kind === "accessibility") {
    const src = a.source as AccessibilityFinding;
    if (src.wcagName && src.wcagId) {
      return `${src.wcagName} (WCAG ${src.wcagId})`;
    }
    return src.wcagName || null;
  }
  if (a.kind === "bestpractice") {
    const src = a.source as BestPracticeFinding;
    return src.practiceName
      ? `${src.practiceName} (UX best practice)`
      : null;
  }
  return null;
}

/* sourceTypeLabel removed — the rail no longer renders an inline
   source label on each finding row. Source is now carried by the
   sub-section header above each source group inside a page
   section. */

/** CSS-friendly slug for the source label, used as a data attribute. */
function sourceSlugFor(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

/**
 * Derive a short, plain-language issue title from the AI's longer
 * observation text. The title shown in the rail card is meant to
 * answer "what's wrong" at a glance — e.g., "Value proposition is
 * not scannable" — not "Clarity of value proposition" (the heuristic
 * name) which already lives in the expanded Heuristic meta line.
 *
 * Strategy: take the first sentence, capping at 100 chars; fall back
 * to the original text if it's already short. Empty input returns
 * the provided fallback (typically the heuristic / WCAG name).
 */
function deriveIssueTitle(observation: string, fallback: string): string {
  const text = (observation || "").trim();
  if (!text) return fallback;

  // First clause — stop at the first sentence-ending OR clause break
  // (comma / semicolon / em-dash) so we get a headline, not a paragraph.
  const match = text.match(/^[^.!?,;—]+/);
  let candidate = (match ? match[0] : text).trim();

  // Drop a leading article so it reads like a headline ("Tap targets too
  // small" not "The tap targets are too small").
  candidate = candidate.replace(/^(the|a|an)\s+/i, "");

  // Drop trailing punctuation.
  candidate = candidate.replace(/[.!?,;:—\s]+$/, "").trim();

  // Cap at 52 chars on a word boundary.
  if (candidate.length > 52) {
    candidate = candidate.slice(0, 49).replace(/\s+\S*$/, "") + "…";
  }

  // Capitalize the first character.
  if (candidate.length > 0) {
    candidate = candidate[0].toUpperCase() + candidate.slice(1);
  }

  return candidate || fallback;
}

export function CanvasViewer({
  job,
  mode = "owner",
  canShare = true,
  plan = "free",
  creditBalance = null,
  creatorEmail,
  onJobUpdate,
  onUnlock,
}: {
  job: JobState;
  /** "owner" = full edit affordances. "viewer" = read-only public share view.
      "locked" = logged-out guest teaser: real canvas + score + pins, but all
      findings except a few free samples across the captured pages are locked behind
      sign-in. */
  mode?: CanvasMode;
  /** Whether this user is allowed to create share links (gated by plan). */
  canShare?: boolean;
  /** Viewer's plan. Gates the "Copy findings" export dialog (Pro/Agency
      only). Defaults to "free"; public share viewers get export regardless
      via the isViewer override since the share itself was a paid action. */
  plan?: "free" | "pro" | "agency";
  /** Owner's current credit balance — drives the Add-page modal's balance
      display and the insufficient-credits gate. null when unknown (public
      viewer, or before entitlements load), in which case the modal falls
      back to the backend's 402 safety net. */
  creditBalance?: number | null;
  /** Creator's email — surfaced in the left-rail meta footer in owner
      mode. Undefined in viewer (public share) mode, where the footer
      collapses to just the creation date. */
  creatorEmail?: string;
  /** Owner-only: invoked when the canvas mutates the underlying job state
      (e.g. appending a new page) so the parent can re-render with fresh
      data without a full refetch. */
  onJobUpdate?: (next: JobState) => void;
  /** Locked-mode only: invoked when a guest interacts with anything gated
      (a locked finding, the "Unlock" CTA, Share/Export). The parent opens the
      sign-in flow. No-op in owner/viewer modes. */
  onUnlock?: () => void;
}) {
  const isViewer = mode === "viewer";
  const isOwner = mode === "owner";
  // Locked = logged-out guest teaser. It's read-only like viewer (no saves,
  // no custom-annotation fetch, no edit affordances) but additionally hides
  // the text of every finding except a few free samples across the captured pages.
  const isLocked = mode === "locked";
  const isReadOnly = !isOwner;
  // Accepts an optional source label for the "Unlock Opened" funnel event
  // so Plausible can show WHICH affordance converts (topbar, rail, summary
  // sheet, or a locked element on the canvas). Guarded to a string because
  // legacy call sites pass this straight to onClick, which hands it a
  // MouseEvent — those count as "canvas". Only tracks when an unlock
  // handler is actually wired (i.e. locked guest mode).
  const handleUnlock = useCallback(
    (source?: unknown) => {
      if (!onUnlock) return;
      track("Unlock Opened", {
        source: typeof source === "string" ? source : "canvas",
      });
      onUnlock();
    },
    [onUnlock],
  );
  const result = job.result as AnalyzeResult;
  const stageRef = useRef<HTMLDivElement>(null);
  const stageInnerRef = useRef<HTMLDivElement>(null);

  const layoutBase = useMemo(() => buildLayout(result), [result]);

  const [customs, setCustoms] = useState<CustomAnnotationApi[]>([]);
  const [, setCustomsLoaded] = useState(false);
  const layout = useMemo(
    () => decorateLayoutWithCustoms(layoutBase, customs),
    [layoutBase, customs]
  );
  const allFindings = useMemo(
    () => collectFindings(layout, result.viewports ?? ["desktop"]),
    [layout, result.viewports]
  );

  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [grabbing, setGrabbing] = useState(false);
  const [activeFindingId, setActiveFindingId] = useState<string | null>(null);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("all");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [bookmarkedOnly, setBookmarkedOnly] = useState(false);
  const [expandedFindingId, setExpandedFindingId] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [pinTool, setPinTool] = useState(false);
  const [formSeed, setFormSeed] = useState<FormSeed | null>(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // After saving a pinned custom finding, hold its id here until
  // the finding gets pulled into allFindings via the custom-
  // annotation decorator; then a useEffect below flies the canvas
  // to it. Confirms to the user "yes, that's where it landed".
  const [pendingFlyToCustomId, setPendingFlyToCustomId] = useState<string | null>(null);
  // Delete-confirm modal — replaces the native window.confirm() that
  // didn't match the app's theme/design system. We hold both the id
  // (so the API call knows what to delete) and a deleting flag so
  // the dialog can show a "Deleting…" state instead of an instant
  // disappear that leaves users unsure whether anything happened.
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  // "Copy findings" export dialog (plain text / markdown). Pro/Agency
  // only — see canExportFindings below.
  const [exportOpen, setExportOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Add-page modal — captures another same-domain page and appends it
  // to the existing audit via POST /api/jobs/:id/append-page.
  const [addPageOpen, setAddPageOpen] = useState(false);
  const [addPageSaving, setAddPageSaving] = useState(false);
  const [addPageError, setAddPageError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "error" | "success" | "warn" | "info" } | null>(null);
  const showToast = useCallback(
    (
      message: string,
      type: "error" | "success" | "warn" | "info" = "error",
      durationMs = 4000,
    ) => {
      setToast({ message, type });
      // durationMs <= 0 → sticky: the toast stays until the user dismisses
      // it. Used for important one-time hints that shouldn't flash past.
      if (durationMs > 0) {
        setTimeout(() => setToast(null), durationMs);
      }
    },
    [],
  );

  // Dismissible chrome — first-visit hints that become ambient noise
  // after the user has the canvas figured out. State lives in
  // localStorage so the dismissal sticks across visits without
  // needing a server-side preference. Both default to "show" until
  // the user actively dismisses.
  const [helpHidden, setHelpHidden] = useState(false);
  const [aiNoteHidden, setAiNoteHidden] = useState(false);
  // "First pass" explainer modal — opened by clicking the pill next to
  // the grade. Carries the manual-review guidance that used to live in
  // a tooltip (which collided with the score ⓘ's tooltip).
  const [firstPassModalOpen, setFirstPassModalOpen] = useState(false);
  // One-time capture-failure dialog. When the whole audit came back blank
  // it's a dead-end the owner needs explained up front — what happened,
  // that the credits were returned, and the one useful next step (Upload
  // image). Shown once per job (localStorage) and owner-only (the credit
  // line is meaningless to a public viewer).
  const [captureFailModalOpen, setCaptureFailModalOpen] = useState(false);
  // Mobile-only "better on desktop" banner. The canvas is a pan/zoom
  // workspace built for wide screens; on a phone it's usable but cramped.
  // Surface a gentle, dismissible heads-up. Hidden state persists to
  // localStorage so we only nudge once per device.
  const [desktopHintHidden, setDesktopHintHidden] = useState(false);
  // Rail collapse — power users want more canvas room. Persisted
  // to localStorage so the choice sticks across visits.
  const [leftRailCollapsed, setLeftRailCollapsed] = useState(false);
  const [rightRailCollapsed, setRightRailCollapsed] = useState(false);
  // Mobile sheet state — at ≤900 px the rails are hidden by default
  // (they're 280 + 420 px of fixed chrome that won't fit). Two
  // floating pills bottom-left / bottom-right re-surface them as
  // full-screen sheets instead. Not persisted: mobile users open a
  // sheet for a specific task, then dismiss it back to the canvas.
  const [mobileSheet, setMobileSheet] = useState<"left" | "right" | null>(null);
  const openLeftSheet = useCallback(() => setMobileSheet("left"), []);
  const openRightSheet = useCallback(() => setMobileSheet("right"), []);
  const closeMobileSheet = useCallback(() => setMobileSheet(null), []);
  // Close the mobile sheet on Escape so keyboard users on a tablet
  // (or anyone with a Bluetooth keyboard) can dismiss without
  // hunting for the X button.
  useEffect(() => {
    if (!mobileSheet) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileSheet(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileSheet]);
  // Mobile summary sheet — the first read on a phone for the two public
  // surfaces. The canvas is a pan/zoom workspace sized for desks; at phone
  // widths the fit-to-screen view is a field of tiny thumbnails, so the
  // payoff (score, findings count) wasn't visible anywhere on first paint.
  //   locked (/try): a guest just waited a minute for exactly these
  //     numbers → sheet leads with them + the unlock CTA.
  //   viewer (/s): demo cards from the homepage and shared reports get the
  //     same readable summary; CTA pitches "Try Snap Site" instead (same
  //     destination the viewer topbar CTA already points at).
  // Owner mode never shows it. CSS hides it above 900 px so desktop is
  // untouched even while the state is true.
  const [lockedSummaryOpen, setLockedSummaryOpen] = useState(
    mode === "locked" || mode === "viewer",
  );
  useEffect(() => {
    if (!lockedSummaryOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLockedSummaryOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lockedSummaryOpen]);
  // Funnel: the sheet only actually SHOWS at phone widths (CSS hides it
  // above 900 px even while the state is open), so only count it there —
  // otherwise every desktop visit would inflate the step.
  const sheetShownRef = useRef(false);
  useEffect(() => {
    if (sheetShownRef.current || !lockedSummaryOpen) return;
    if (mode !== "locked" && mode !== "viewer") return;
    if (typeof window === "undefined" || window.innerWidth > 900) return;
    sheetShownRef.current = true;
    track("Summary Sheet Shown", { mode, demo: job.isDemo ?? false });
  }, [lockedSummaryOpen, mode, job.isDemo]);
  // Mobile topbar menu — at narrow widths the action cluster
  // (zoom controls + Share + Export) doesn't fit. We hide the
  // overflowing items via CSS and surface them in this popover
  // instead. Owner-only — the public-viewer topbar uses its own
  // simpler layout.
  const [topbarMenuOpen, setTopbarMenuOpen] = useState(false);
  useEffect(() => {
    if (!topbarMenuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTopbarMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [topbarMenuOpen]);
  useEffect(() => {
    try {
      if (window.localStorage.getItem("snap-site:cv-help-hidden") === "1") {
        setHelpHidden(true);
      }
      if (window.localStorage.getItem("snap-site:cv-ai-note-hidden") === "1") {
        setAiNoteHidden(true);
      }
      if (window.localStorage.getItem("snap-site:cv-desktop-hint-hidden") === "1") {
        setDesktopHintHidden(true);
      }
      if (window.localStorage.getItem("snap-site:cv-left-collapsed") === "1") {
        setLeftRailCollapsed(true);
      }
      if (window.localStorage.getItem("snap-site:cv-right-collapsed") === "1") {
        setRightRailCollapsed(true);
      }
    } catch { /* private mode etc — keep defaults */ }
  }, []);
  const toggleLeftRail = useCallback(() => {
    setLeftRailCollapsed((v) => {
      const next = !v;
      try { window.localStorage.setItem("snap-site:cv-left-collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const toggleRightRail = useCallback(() => {
    setRightRailCollapsed((v) => {
      const next = !v;
      try { window.localStorage.setItem("snap-site:cv-right-collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const dismissHelp = useCallback(() => {
    setHelpHidden(true);
    try { window.localStorage.setItem("snap-site:cv-help-hidden", "1"); } catch { /* ignore */ }
  }, []);
  /** Bring the gesture-shortcuts hint back. Lets a user who clicked
   *  the × on the help line undo that choice without having to wipe
   *  browser data (the previous tooltip's only escape hatch). Wired
   *  to the small "?" button rendered in the same canvas slot when
   *  the help is dismissed. */
  const showHelp = useCallback(() => {
    setHelpHidden(false);
    try { window.localStorage.removeItem("snap-site:cv-help-hidden"); } catch { /* ignore */ }
  }, []);
  const dismissAiNoteSilently = useCallback(() => {
    // Silent (non-button) dismissal — used when the user expands
    // their first finding, which is the strongest signal that
    // they understand the workflow ("AI suggested, verify it").
    // The note quietly fades away without needing an explicit ×.
    setAiNoteHidden(true);
    try { window.localStorage.setItem("snap-site:cv-ai-note-hidden", "1"); } catch { /* ignore */ }
  }, []);
  const dismissAiNote = useCallback(() => {
    setAiNoteHidden(true);
    try { window.localStorage.setItem("snap-site:cv-ai-note-hidden", "1"); } catch { /* ignore */ }
  }, []);
  const dismissDesktopHint = useCallback(() => {
    setDesktopHintHidden(true);
    try { window.localStorage.setItem("snap-site:cv-desktop-hint-hidden", "1"); } catch { /* ignore */ }
  }, []);

  // ── One-time feedback prompt ────────────────────────────────────
  // Once per user (per device, via localStorage): the first time they
  // spend 15s of visible time on ANY audit canvas, slide in the
  // feedback card (FeedbackDialog). Deliberately not tied to their
  // first audit — that would skip every existing user, plus anyone who
  // bailed on their first audit in under 15s. The timer only counts
  // foreground time: switching tabs pauses it, so "15 seconds on the
  // canvas" means actually looking at it. The left rail's perpetual
  // "Give feedback" button opens the same dialog on demand (and counts
  // as the one-time showing).
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // True while any other dialog/overlay is up — synced by an effect
  // further down (after exportPhase is declared). The prompt waits its
  // turn rather than stacking on top of a share/export/add-page flow.
  const feedbackBusyRef = useRef(false);
  const openFeedback = useCallback(() => {
    // Manual open from the left rail's button. Counts as "prompted" so
    // the automatic nudge never fires after the user has already found it.
    try { window.localStorage.setItem(FEEDBACK_PROMPT_KEY, "1"); } catch { /* ignore */ }
    setFeedbackOpen(true);
  }, []);
  useEffect(() => {
    if (!isOwner || job.isDemo) return;
    try {
      if (window.localStorage.getItem(FEEDBACK_PROMPT_KEY)) return;
    } catch {
      // No localStorage → we couldn't remember a dismissal, so the
      // prompt would nag on every visit. Skip instead.
      return;
    }
    if (!readToken()) return;

    let cancelled = false;
    let timer: number | null = null;
    let remaining = 15_000;
    let startedAt = 0;

    const fire = () => {
      timer = null;
      if (cancelled) return;
      if (feedbackBusyRef.current) {
        remaining = 5_000;
        arm();
        return;
      }
      // Mark as shown the moment it appears — ignoring it once is an
      // answer too; we never re-prompt.
      try { window.localStorage.setItem(FEEDBACK_PROMPT_KEY, "1"); } catch { /* ignore */ }
      setFeedbackOpen(true);
    };
    const arm = () => {
      if (cancelled || timer !== null || document.visibilityState !== "visible") return;
      startedAt = performance.now();
      timer = window.setTimeout(fire, remaining);
    };
    const pause = () => {
      if (timer === null) return;
      window.clearTimeout(timer);
      timer = null;
      remaining = Math.max(1_000, remaining - (performance.now() - startedAt));
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") arm();
      else pause();
    };

    document.addEventListener("visibilitychange", onVisibility);
    arm();

    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isOwner, job.id, job.isDemo]);

  // Auto-dismiss the AI-suggested note once the user has expanded
  // their first finding — that's the strongest signal they've
  // started reviewing AI output, so the explanatory note has done
  // its job. Sticks via the same localStorage key so it stays
  // dismissed on subsequent visits.
  useEffect(() => {
    if (expandedFindingId !== null && !aiNoteHidden) {
      dismissAiNoteSilently();
    }
  }, [expandedFindingId, aiNoteHidden, dismissAiNoteSilently]);

  // Per-annotation pixel offsets for the floating callout cards.
  //
  // Persistence flow:
  //  - On mount, prime from localStorage so the canvas paints with the
  //    last-known arrangement immediately, then merge in whatever the
  //    backend has (the backend always wins on conflict).
  //  - On every change, write through to localStorage (instant) and
  //    debounce a PUT to the backend (~600ms after the last drag) so
  //    the share view and the same user on other devices stay in sync.
  //  - Viewers (mode="viewer") skip the save call entirely.
  const cardOffsetsKey = `snap-site:card-offsets:${job.id}`;
  const [cardOffsets, setCardOffsets] = useState<
    Record<string, { x: number; y: number }>
  >(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(cardOffsetsKey);
      return raw ? (JSON.parse(raw) as Record<string, { x: number; y: number }>) : {};
    } catch {
      return {};
    }
  });
  // Suppress the first save-after-hydrate so we don't push the
  // localStorage cache back over fresher backend state.
  const layoutHydratedRef = useRef(false);

  // Bookmarked + solved finding ids. Persisted server-side alongside
  // cardOffsets in the same audit_layout row.
  const [bookmarked, setBookmarked] = useState<Set<string>>(new Set());
  const [solved, setSolved] = useState<Set<string>>(new Set());
  // Per-finding dot location overrides ({x, y} as % 0-100 of the shot).
  // When present, the dot renders at the override instead of the AI-
  // detected bbox center, and the connector line follows.
  const [pinOverrides, setPinOverrides] = useState<
    Record<string, { x: number; y: number }>
  >({});

  // Hydrate from backend once on mount.
  useEffect(() => {
    // Locked guests have no saved layout and no token — the layout endpoint
    // would 403. Skip it; the canvas renders from the AI-detected positions.
    if (isLocked) {
      layoutHydratedRef.current = true;
      return;
    }
    let cancelled = false;
    fetchAuditLayout(job.id, readToken())
      .then((layout) => {
        if (cancelled) return;
        layoutHydratedRef.current = true;
        if (
          layout.cardOffsets &&
          Object.keys(layout.cardOffsets).length > 0
        ) {
          setCardOffsets(layout.cardOffsets);
        }
        setPinOverrides(layout.pinOverrides ?? {});
        setBookmarked(new Set(layout.bookmarked ?? []));
        setSolved(new Set(layout.solved ?? []));
      })
      .catch(() => {
        layoutHydratedRef.current = true;
      });
    return () => {
      cancelled = true;
    };
  }, [job.id, isLocked]);
  // Mirror offsets to localStorage immediately on every change (cheap, sync).
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(cardOffsetsKey, JSON.stringify(cardOffsets));
    } catch {
      // localStorage full / private mode — drop silently
    }
  }, [cardOffsets, cardOffsetsKey]);
  // Debounced backend save for offsets (~600ms — drag pulses fire fast).
  useEffect(() => {
    if (isReadOnly) return;
    if (!layoutHydratedRef.current) return;
    const token = readToken();
    if (!token) return;
    const t = window.setTimeout(() => {
      saveAuditLayout(token, job.id, { cardOffsets }).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[canvas] saveAuditLayout(cardOffsets) failed", err);
      });
    }, 600);
    return () => window.clearTimeout(t);
  }, [cardOffsets, job.id, isReadOnly]);
  // Debounced save for dot-position overrides — same shape, same cadence.
  useEffect(() => {
    if (isReadOnly) return;
    if (!layoutHydratedRef.current) return;
    const token = readToken();
    if (!token) return;
    const t = window.setTimeout(() => {
      saveAuditLayout(token, job.id, { pinOverrides }).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[canvas] saveAuditLayout(pinOverrides) failed", err);
      });
    }, 600);
    return () => window.clearTimeout(t);
  }, [pinOverrides, job.id, isReadOnly]);
  // Save bookmarked + solved on change. Toggle is a deliberate gesture
  // so a 200ms debounce just coalesces accidental double-clicks.
  useEffect(() => {
    if (isReadOnly) return;
    if (!layoutHydratedRef.current) return;
    const token = readToken();
    if (!token) return;
    const t = window.setTimeout(() => {
      saveAuditLayout(token, job.id, {
        bookmarked: Array.from(bookmarked),
        solved: Array.from(solved),
      }).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[canvas] saveAuditLayout(bookmark/solved) failed", err);
        showToast("Could not save your changes. Check your connection and try again.", "warn");
      });
    }, 200);
    return () => window.clearTimeout(t);
  }, [bookmarked, solved, job.id, isReadOnly]);

  const moveCard = useCallback((id: string, x: number, y: number) => {
    setCardOffsets((prev) => ({ ...prev, [id]: { x, y } }));
  }, []);
  const movePin = useCallback((id: string, x: number, y: number) => {
    setPinOverrides((prev) => ({ ...prev, [id]: { x, y } }));
  }, []);
  const toggleBookmark = useCallback((id: string) => {
    setBookmarked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const toggleSolved = useCallback((id: string) => {
    setSolved((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Hydrate annotations from the server. The backend gates this endpoint by
  // ownership (free-tier owners' audits 403 for non-owners), so send the
  // auth token when we have one — share-token viewers fall through with no
  // header, which is fine for paid-tier authors' public audits.
  useEffect(() => {
    // Locked guests have no account and no custom annotations; skip the fetch.
    if (isLocked) {
      setCustomsLoaded(true);
      return;
    }
    let cancelled = false;
    listCustomAnnotations(job.id, readToken())
      .then((list) => {
        if (!cancelled) {
          setCustoms(list);
          setCustomsLoaded(true);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[canvas] failed to load custom annotations", err);
        if (!cancelled) setCustomsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [job.id, isLocked]);

  // Measure stage
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const e of entries) {
        const { width, height } = e.contentRect;
        setStageSize({ width, height });
      }
    });
    observer.observe(el);
    setStageSize({ width: el.clientWidth, height: el.clientHeight });
    return () => observer.disconnect();
  }, []);

  const totalWidth = layout.totalWidth;
  const totalHeight = layout.totalHeight;

  const fitToScreen = useCallback(() => {
    if (!stageSize.width || !stageSize.height) return;
    const scaleX = (stageSize.width - 80) / totalWidth;
    const scaleY = (stageSize.height - 80) / totalHeight;
    const scale = Math.max(MIN_SCALE, Math.min(1, Math.min(scaleX, scaleY)));
    const x = (stageSize.width - totalWidth * scale) / 2;
    const y = (stageSize.height - totalHeight * scale) / 2;
    setTransform({ x, y, scale });
  }, [stageSize, totalWidth, totalHeight]);

  // Initial view. Desktop: fit the whole canvas (unchanged). Phone-width
  // stages: fit-all renders every page as a thumbnail adrift in whitespace
  // — the "tiny floating rectangles" first paint. Land on the SUMMARY CARD
  // instead — the one canvas artifact written to be read at a glance —
  // with a sliver of the first captured page peeking in from the right,
  // so "there's more, swipe left" is shown rather than told. The Fit
  // button and the "0" shortcut still fit-all on every device.
  const initialFit = useCallback(() => {
    if (!stageSize.width || !stageSize.height) return;
    if (stageSize.width <= 900) {
      const pad = 12;
      // Page tease: how much of the first page should breach the right
      // edge, in screen px. The scale then fits summary + section gap in
      // the remaining width, which by construction leaves exactly this
      // sliver of page one on screen (when the audit has pages at all).
      const tease = Math.max(28, stageSize.width * 0.1);
      const scale = Math.max(
        MIN_SCALE,
        Math.min(
          1,
          (stageSize.width - pad - tease) / (SUMMARY_WIDTH + SECTION_GAP),
        ),
      );
      const x = pad - layout.summaryX * scale;
      const y = pad - PAGE_PAD * scale; // summary card top sits at PAGE_PAD
      setTransform({ x, y, scale });
      return;
    }
    fitToScreen();
  }, [stageSize, layout.summaryX, fitToScreen]);

  useEffect(() => {
    initialFit();
  }, [initialFit]);

  useEffect(() => {
    const syncFullscreen = () => setIsFullscreen(Boolean(document.fullscreenElement));
    syncFullscreen();
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () => document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    // Branched error paths — the previous single catch said
    // "Could not switch fullscreen mode" for both enter and exit
    // failures, which read as "switch INTO fullscreen" when the user
    // was trying to leave it. Now the message names the actual
    // operation that failed.
    const wasFullscreen = !!document.fullscreenElement;
    try {
      if (wasFullscreen) {
        await document.exitFullscreen();
        return;
      }
      if (!document.documentElement.requestFullscreen) {
        showToast("Fullscreen is not available in this browser.", "warn");
        return;
      }
      await document.documentElement.requestFullscreen();
    } catch {
      showToast(
        wasFullscreen
          ? "Could not exit fullscreen. Try pressing Esc, or use your browser controls."
          : "Could not enter fullscreen. Try using your browser controls.",
        "warn",
      );
    }
  }, [showToast]);

  const flyToPage = useCallback(
    (idx: number) => {
      const target = layout.pages[idx];
      if (!target || !stageSize.width) return;
      const scale = Math.max(0.4, Math.min(0.9, transform.scale));
      const x = stageSize.width / 2 - (target.x + target.width / 2) * scale;
      const y = stageSize.height / 3 - (target.y + Math.min(target.height, 600) / 2) * scale;
      setTransform({ x, y, scale });
      setActivePageIndex(idx);
    },
    [layout.pages, stageSize, transform.scale],
  );

  const flyToFinding = useCallback(
    (pin: AuditAnnotation, pageIdx: number, shotIdx: number) => {
      const page = layout.pages[pageIdx];
      const shot = page?.shots[shotIdx];
      if (!page || !shot) return;
      const id = `${pageIdx}-${shotIdx}-${pin.kind}-${pin.number}`;
      // Mirror the dot's actual on-screen position: a manual override wins,
      // otherwise the AI-detected bbox center.
      const center = pinOverrides[id] ?? {
        x: pin.bbox.x + pin.bbox.width / 2,
        y: pin.bbox.y + pin.bbox.height / 2,
      };
      // Shots sit side-by-side: horizontal per-shot offset is offsetX, no
      // vertical offset between viewports.
      const dotX = page.shotsX + shot.offsetX + (center.x / 100) * shot.width;
      const dotY = page.shotsY + (center.y / 100) * shot.height;
      const scale = Math.max(0.6, Math.min(1.6, transform.scale));
      const x = stageSize.width / 2 - dotX * scale;
      const y = stageSize.height / 2 - dotY * scale;
      setTransform({ x, y, scale });
      setActiveFindingId(id);
      setExpandedFindingId(id);
      setActivePageIndex(pageIdx);
    },
    [layout.pages, stageSize, transform.scale, pinOverrides],
  );

  // After saving a pinned custom finding, fly the canvas to the
  // new pin so the user sees where it landed. Runs after the
  // setCustoms update has been folded into allFindings — at that
  // point we can find the new entry by its customId.
  useEffect(() => {
    if (!pendingFlyToCustomId) return;
    const entry = allFindings.find(
      (f) =>
        f.annotation.kind === "custom" &&
        f.annotation.customId === pendingFlyToCustomId,
    );
    if (entry && entry.placed) {
      flyToFinding(entry.annotation, entry.pageIndex, entry.shotIndex);
      setActiveFindingId(entry.id);
      setExpandedFindingId(entry.id);
    }
    setPendingFlyToCustomId(null);
  }, [pendingFlyToCustomId, allFindings, flyToFinding]);

  const setZoom = useCallback(
    (next: number, cx?: number, cy?: number) => {
      setTransform((prev) => {
        const target = Math.max(MIN_SCALE, Math.min(MAX_SCALE, next));
        const focusX = cx ?? stageSize.width / 2;
        const focusY = cy ?? stageSize.height / 2;
        const ratio = target / prev.scale;
        const x = focusX - (focusX - prev.x) * ratio;
        const y = focusY - (focusY - prev.y) * ratio;
        return { x, y, scale: target };
      });
    },
    [stageSize],
  );

  // Figma-style wheel handling: pinch / cmd+wheel zooms, everything else pans.
  // Attached as a non-passive listener so preventDefault() actually stops the
  // browser's page-level pinch zoom.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const isZoom = e.ctrlKey || e.metaKey;
      if (isZoom) {
        const delta = -e.deltaY * 0.015;
        const factor = Math.exp(delta);
        const rect = el.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        setTransform((prev) => {
          const target = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev.scale * factor));
          const ratio = target / prev.scale;
          return {
            x: cx - (cx - prev.x) * ratio,
            y: cy - (cy - prev.y) * ratio,
            scale: target,
          };
        });
      } else {
        // Two-finger trackpad scroll or mouse wheel → pan.
        // Shift+wheel on a mouse swaps to horizontal pan (browser convention).
        const dx = e.shiftKey && e.deltaX === 0 ? e.deltaY : e.deltaX;
        const dy = e.shiftKey && e.deltaX === 0 ? 0 : e.deltaY;
        setTransform((prev) => ({ ...prev, x: prev.x - dx, y: prev.y - dy }));
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const dragRef = useRef<{
    x: number;
    y: number;
    tx: number;
    ty: number;
    moved: boolean;
    target: HTMLElement | null;
  } | null>(null);

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest("[data-canvas-interactive]")) return;
      if (e.button !== 0 && e.button !== 1) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        x: e.clientX,
        y: e.clientY,
        tx: transform.x,
        ty: transform.y,
        moved: false,
        target: e.target as HTMLElement,
      };
      setGrabbing(true);
    },
    [transform.x, transform.y],
  );

  const handlePointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    if (!drag.moved && Math.hypot(dx, dy) > 4) {
      drag.moved = true;
    }
    setTransform((prev) => ({ ...prev, x: drag.tx + dx, y: drag.ty + dy }));
  }, []);

  const handlePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      e.currentTarget.releasePointerCapture(e.pointerId);
      // Pin tool: pointerup with no significant movement on a shot wrap →
      // open the create-annotation form anchored at that location. Letting
      // the drag start first means the user can still pan the canvas in
      // pin mode by dragging — they just can't accidentally place a pin.
      // Viewer mode never reaches this branch (the toggle isn't rendered),
      // but gate explicitly so a smuggled pinTool=true can't open the form.
      if (isOwner && pinTool && !drag.moved && drag.target) {
        const anchor = drag.target.closest<HTMLElement>("[data-shot-anchor]");
        if (anchor) {
          const pageIndex = Number(anchor.dataset.shotPageIndex);
          const viewport = anchor.dataset.shotViewport as Viewport | undefined;
          if (Number.isFinite(pageIndex) && (viewport === "desktop" || viewport === "mobile")) {
            const rect = anchor.getBoundingClientRect();
            const xPct = ((e.clientX - rect.left) / rect.width) * 100;
            const yPct = ((e.clientY - rect.top) / rect.height) * 100;
            const PIN_BBOX_PCT = 4;
            const placement = {
              pageIndex,
              viewport,
              bbox: {
                x: Math.max(0, Math.min(100 - PIN_BBOX_PCT, xPct - PIN_BBOX_PCT / 2)),
                y: Math.max(0, Math.min(100 - PIN_BBOX_PCT, yPct - PIN_BBOX_PCT / 2)),
                width: PIN_BBOX_PCT,
                height: PIN_BBOX_PCT,
              },
            };
            // Single-button flow: the form modal is already open
            // (the user clicked "Pick spot on canvas" from inside
            // it). Update its placement instead of replacing the
            // whole seed so the user's typed-in values survive
            // the pin-pick step. Fallback (no existing seed) is
            // the legacy "click without modal open" path — keeps
            // the API surface backwards-compatible.
            setFormSeed((prev) => prev
              ? { ...prev, placement }
              : { title: "", severity: "medium", description: "", placement });
            setPinTool(false);
            setFormError(null);
          }
        }
      }
      dragRef.current = null;
      setGrabbing(false);
    },
    [pinTool, isOwner],
  );

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      const inField = tag === "INPUT" || tag === "TEXTAREA";
      // Escape works everywhere — including inside the form's inputs — so
      // the user can dismiss the modal without leaving the field first.
      if (e.key === "Escape") {
        if (formSeed) { setFormSeed(null); return; }
        if (pinTool) { setPinTool(false); return; }
      }
      if (inField) return;
      if (e.key === "0") { e.preventDefault(); fitToScreen(); }
      else if (e.key === "1") { e.preventDefault(); setZoom(1); }
      else if ((e.key === "+" || e.key === "=") && !e.shiftKey) { e.preventDefault(); setZoom(transform.scale * 1.2); }
      else if (e.key === "-") { e.preventDefault(); setZoom(transform.scale / 1.2); }
      else if (e.key === "[") { e.preventDefault(); toggleLeftRail(); }
      else if (e.key === "]") { e.preventDefault(); toggleRightRail(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fitToScreen, setZoom, transform.scale, pinTool, formSeed, toggleLeftRail, toggleRightRail]);

  // Save / delete handlers for custom annotations.
  const handleSaveAnnotation = useCallback(
    async (data: { title: string; severity: HeuristicSeverity; description: string; clearPlacement: boolean }) => {
      if (!formSeed) return;
      const token = readToken();
      if (!token) {
        // The owner-mode UI is gated on a `mode` prop the parent
        // passes in — it doesn't actually re-verify the auth token
        // on every click. So you can land in this branch if the
        // localStorage token expired or was never set in this
        // browser (e.g. signed in via cookie elsewhere).
        //
        // Critically: do NOT tell the user to refresh — the modal
        // is full of their typed work (title, description) which a
        // refresh would wipe. Instead, walk them through the
        // sign-in-elsewhere path that preserves their input, and
        // the modal renders a Copy button next to this error so
        // they can rescue their text if they want a safety net.
        setFormError(
          "Your session has expired. Your text is safe here — open a new tab, sign back in, then come back and click Save. Or copy your text first to be safe.",
        );
        return;
      }
      setFormSaving(true);
      setFormError(null);
      try {
        if (formSeed.editingId) {
          const updated = await updateCustomAnnotation(token, job.id, formSeed.editingId, {
            title: data.title,
            severity: data.severity,
            description: data.description,
          });
          setCustoms((prev) =>
            prev.map((c) => (c.id === updated.id ? updated : c)),
          );
        } else {
          // Create path. Two cases:
          //   • Pinned — user picked a spot on the canvas, so
          //     placement carries a real bbox.
          //   • General — user chose "General" in the modal, so
          //     placement is undefined. We anchor to the
          //     currently-focused page (or the homepage when in
          //     Summary view) and use a sentinel placeholder bbox
          //     at the top of the page; the canvas treats this as
          //     an unplaced finding via the `placed: false`
          //     branch in the FindingEntry build, and the rail
          //     shows it with the "General" tag.
          const placement = formSeed.placement && !data.clearPlacement
            ? formSeed.placement
            : {
                pageIndex: activePageIndex >= 0 ? activePageIndex : 0,
                viewport: (result.viewports?.[0] ?? "desktop") as Viewport,
                bbox: { x: 0, y: 0, width: 0, height: 0 },
              };
          const draft: CustomAnnotationDraft = {
            pageIndex: placement.pageIndex,
            viewport: placement.viewport,
            title: data.title,
            severity: data.severity,
            description: data.description,
            bbox: placement.bbox,
          };
          const created = await createCustomAnnotation(token, job.id, draft);
          setCustoms((prev) => [...prev, created]);
          // One-time heads-up for Figma-origin audits: custom notes are a
          // web-only feature and won't sync back to the plugin. Gated via
          // localStorage so it shows once, not on every annotation.
          if (job.origin === "plugin") {
            try {
              const HINT_KEY = "snap-site:figma-annotation-hint";
              if (!window.localStorage.getItem(HINT_KEY)) {
                window.localStorage.setItem(HINT_KEY, "1");
                showToast(
                  "Note added. Custom notes are web-only — they won't appear in the Figma plugin.",
                  "info",
                  0,
                );
              }
            } catch {
              /* localStorage blocked — skip the hint rather than fail the save. */
            }
          }
          // Queue a fly-to once the new finding lands in
          // allFindings (next render). Only for actually-pinned
          // findings — general ones don't have a spot to fly to.
          if (placement.bbox.width > 0) {
            setPendingFlyToCustomId(created.id);
          }
        }
        setFormSeed(null);
        setPinTool(false);
      } catch (err) {
        setFormError(err instanceof Error ? err.message : "Could not save annotation.");
      } finally {
        setFormSaving(false);
      }
    },
    [formSeed, job.id, job.origin, activePageIndex, result.viewports, showToast],
  );

  // Click handler on Delete in FindingActions — opens the themed
  // confirm dialog rather than firing the destructive call right
  // away. The actual delete runs from confirmDelete below.
  const handleDelete = useCallback(
    (annoId: string) => {
      const token = readToken();
      if (!token) {
        showToast("Sign in required.", "warn");
        return;
      }
      // Use the finding title (if we still have it) in the dialog
      // body so the user can verify they're deleting the right one
      // — generic "this finding" copy is too easy to misclick.
      const target = customs.find((c) => c.id === annoId);
      setDeleteTarget({ id: annoId, title: target?.title ?? "this finding" });
    },
    [customs, showToast],
  );

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const token = readToken();
    if (!token) {
      setDeleteTarget(null);
      showToast("Sign in required.", "warn");
      return;
    }
    setDeleting(true);
    try {
      await deleteCustomAnnotation(token, job.id, deleteTarget.id);
      setCustoms((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[canvas] delete annotation failed", err);
      showToast("Couldn't delete the finding. Try again.", "error");
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, job.id, showToast]);

  const zoomPct = Math.round(transform.scale * 100);

  // Viewport-deduped view of the findings. Drives the headline count, the
  // rail list, and the export so they all agree with the (also deduped) score.
  // The canvas dots still come from `layout`/`allFindings`, so each viewport
  // keeps its own pins.
  const dedupedFindings = useMemo(
    () => dedupeFindingEntries(allFindings),
    [allFindings],
  );

  // Stats
  const totalPages = 1 + (result.pages?.length ?? 0);
  const totalFindings2 = dedupedFindings.length;
  const highCount = dedupedFindings.filter((f) => f.annotation.severity === "high").length;
  const medCount = dedupedFindings.filter((f) => f.annotation.severity === "medium").length;
  const lowCount = dedupedFindings.filter((f) => f.annotation.severity === "low").length;
  // Capture health — aggregate the per-page captureQuality verdicts the
  // backend attaches (see capture-health.ts). A "blank"/"blocked"/
  // "notfound" page is unscorable: its findings are dropped and it's
  // excluded from the score. So a 0-finding result on failed captures is
  // NOT a clean pass — without this the green "Clean first pass" card and
  // the per-page green checks make a blank capture look like a healthy
  // audit. Returns null when every page captured fine (the common case).
  const captureHealth = useMemo(() => {
    const pages = [result.homepage, ...(result.pages ?? [])].filter(
      Boolean,
    ) as CapturedPage[];
    if (pages.length === 0) return null;
    const failed = pages.filter(
      (p) => p.captureQuality && p.captureQuality.scorable === false,
    );
    if (failed.length === 0) return null;
    // Dominant failure status drives the wording.
    const counts = new Map<string, number>();
    for (const p of failed) {
      const s = p.captureQuality!.status;
      counts.set(s, (counts.get(s) ?? 0) + 1);
    }
    let status = "blank";
    let max = 0;
    for (const [s, n] of counts) if (n > max) { max = n; status = s; }
    const allFailed = failed.length === pages.length;
    // "Blocked" splits in two: a real bot wall vs the site's own server
    // being down/asleep (5xx). They need OPPOSITE advice — a wall never
    // heals on retry, a napping server almost always does — so only treat
    // the run as server-down when every blocked page says so.
    const blockedPages = failed.filter(
      (p) => p.captureQuality!.status === "blocked",
    );
    const serverDown =
      status === "blocked" &&
      blockedPages.length > 0 &&
      blockedPages.every((p) => isServerDownQuality(p.captureQuality));
    // Copy rules for this card:
    //   1. Never say "re-run the audit" — a blocked/blank site captures the
    //      same wall on a fresh run (and burns another credit). The real fix
    //      is "Upload image" mode: the user screenshots the page in their own
    //      browser (where it renders, logged in if needed) and uploads it.
    //      EXCEPTION — server-down (5xx) runs: the server was asleep or
    //      restarting, retrying IS the fix, and the failed run was refunded,
    //      so "run it again" is honest and costs the user nothing net.
    //   2. Never devalue the product ("to get a REAL audit…" implied the run
    //      they just did was fake). The obstacle is the SITE's bot wall; the
    //      screenshot path is the same full audit, one step away. The copy's
    //      job is to bring the user back, not apologize them out the door.
    // Outcome first, mechanism second, next step last — and short. An
    // earlier draft led with a cheerful mechanism story ("your browser
    // walks right past it…") that read like everything went fine; on a
    // failed run the user needs the plain fact before anything else:
    // nothing was audited.
    const COPY: Record<string, { title: string; detail: string }> = {
      blank: {
        title: allFailed
          ? "This page didn't load for our capture"
          : "Some pages didn't load",
        detail: allFailed
          ? "Nothing rendered for our capture, so there are no findings yet. It may just need a real browser — you can still audit it from a screenshot of the page."
          : "Those pages rendered nothing for our capture, so they're not in the score yet. You can audit them from a screenshot taken in your browser.",
      },
      blocked: {
        title: allFailed
          ? "This site has bot protection"
          : "Some pages have bot protection",
        detail: allFailed
          ? "It blocks automated visitors, so our capture couldn't open the page — that's why there are no findings yet. It loads fine in a normal browser, so you can still audit it from a screenshot."
          : "Bot protection kept our capture out of those pages, so they're not in the score yet. You can audit them from a screenshot taken in your browser.",
      },
      // Server-down (5xx), NOT bot protection. Copy rules here: plain fact
      // first (nothing audited), then mechanism WITHOUT presuming the cause
      // (a 503 can be idle-wake, a deploy, or a crash — never blame the
      // user's hosting), say that WE already retried (so "run it again"
      // reads as fresh advice, not the tool handing its job back), then the
      // next step. No whimsy: playful copy at a failure moment reads
      // flippant.
      server: {
        title: allFailed
          ? "The site's server didn't respond"
          : "The server didn't respond for some pages",
        detail: allFailed
          ? "The server returned a temporary 'unavailable' response instead of the page, even after we retried for about a minute — most likely it was waking up or mid-restart. Give it a minute and run the audit again."
          : "Those pages returned a temporary 'unavailable' response even after retries — likely caught mid-restart — so they're not in the score yet. Run the audit again in a minute to capture them.",
      },
      notfound: {
        title: allFailed
          ? "We couldn't reach this page"
          : "Some pages couldn't be reached",
        detail: allFailed
          ? "The page returned an error or doesn't exist, so there was nothing to audit yet. Double-check the link, or audit a screenshot of the page you meant."
          : "Those pages returned errors. Double-check the links, or audit a screenshot of each page.",
      },
    };
    const copy = (serverDown ? COPY.server : COPY[status]) ?? COPY.blank;
    // Score-exclusion is a subset of capture-failure: `notfound` pages fail
    // capture but still COUNT in the score (they carry a deterministic
    // broken-link finding — see computeScore), so the coverage caveat must
    // not claim they were left out. Only blocked/blank are truly excluded.
    const excluded = failed.filter(
      (p) => p.captureQuality!.status !== "notfound",
    );
    return {
      failedCount: failed.length,
      totalCount: pages.length,
      status,
      allFailed,
      serverDown,
      excludedCount: excluded.length,
      excludedNames: excluded.map((p) => p.name),
      // Drives the score-hero coverage caveat: a failed homepage is called
      // out by name (it's the page readers assume the score describes).
      homepageExcluded:
        result.homepage?.captureQuality?.scorable === false &&
        result.homepage?.captureQuality?.status !== "notfound",
      ...copy,
    };
  }, [result]);

  // Open the capture-failure dialog once per job, owner-only, when the whole
  // audit came back blank. Backend refunds an all-blank run (settleBlankCapture
  // in the pipeline), so the dialog can truthfully say the credits were returned.
  const captureFailModalKey = `snap-site:capture-fail-modal:${job.id}`;
  useEffect(() => {
    if (!isOwner || !captureHealth?.allFailed) return;
    if (typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem(captureFailModalKey) === "1") return;
    } catch {
      /* ignore */
    }
    setCaptureFailModalOpen(true);
  }, [isOwner, captureHealth, captureFailModalKey]);

  const closeCaptureFailModal = useCallback(() => {
    setCaptureFailModalOpen(false);
    try {
      window.localStorage.setItem(captureFailModalKey, "1");
    } catch {
      /* ignore */
    }
  }, [captureFailModalKey]);
  // Snap Site Score — already computed by computeScore() for the Summary
  // card; hoist it up here so the left-rail overview can show it too.
  const overviewBreakdown = computeScore(result);
  const overviewScore = overviewBreakdown?.overall;
  const overviewGrade = overviewScore != null ? scoreToGrade(overviewScore) : null;
  // Category tallies behind the headline score, used to build the hero
  // tooltip. INFORMATIONAL: the overall is the average of per-page pooled
  // scores (see computeScore); these group the same findings by category so
  // the tooltip can show where the deductions came from. First impression
  // is shown in its own panel and doesn't feed the score.
  const scoreAreas = overviewBreakdown
    ? [
        { label: "Usability", b: overviewBreakdown.ux },
        { label: "Accessibility", b: overviewBreakdown.a11y },
        ...(overviewBreakdown.bestPractices.findings > 0
          ? [{ label: "Best practices", b: overviewBreakdown.bestPractices }]
          : []),
      ]
    : [];
  const customCount = dedupedFindings.filter((f) => f.annotation.kind === "custom").length;
  // Source split: NN/g (heuristic) vs Accessibility (WCAG) vs Custom.
  // Surfaced in the left rail, right rail, and Summary stage so
  // users can read the provenance of the findings at a glance —
  // the old display only had a total, which buried the difference
  // between heuristic and accessibility work.
  const heuristicCount = dedupedFindings.filter((f) => f.annotation.kind === "heuristic").length;
  const accessibilityCount = dedupedFindings.filter((f) => f.annotation.kind === "accessibility").length;
  const bestPracticeCount = dedupedFindings.filter((f) => f.annotation.kind === "bestpractice").length;
  const viewportLabel = (result.viewports ?? ["desktop"]).includes("mobile")
    ? result.viewports?.length === 2 ? "Desktop + Mobile" : "Mobile"
    : "Desktop";

  // Copy-findings export (plain text / markdown) is a Pro/Agency feature.
  // Public share viewers get it regardless — the share link itself was a
  // paid action, and a polished export is a funnel touchpoint, not the
  // thing we're gating the free *creator* on.
  const canExportFindings =
    isViewer || plan === "pro" || plan === "agency";
  // PDF export is a Pro/Agency feature too — free owners get a locked
  // button + upsell instead of a download. Public viewers keep it via
  // the isViewer override: a shared audit belongs to a paid owner, so
  // its PDF export stays open (matching the Copy-findings gate above).
  const canExport =
    isViewer || plan === "pro" || plan === "agency";
  // Adding pages to an existing audit is Pro/Agency too. No isViewer
  // override — the button is owner-only, so the viewer escape hatch the
  // export gates need doesn't apply here.
  const canAddPage = plan === "pro" || plan === "agency";

  // Cross-page recurrence: how many pages each issue signature appears on.
  // Feeds the "on N pages" chip — the honest framing for a template issue
  // is "one fix improves N pages", not N separate list entries.
  const findingPageSpread = useMemo(() => {
    const m = new Map<string, Set<number>>();
    for (const e of dedupedFindings) {
      if (e.annotation.kind === "custom") continue;
      const sig = findingEntrySig(e);
      const set = m.get(sig) ?? new Set<number>();
      set.add(e.pageIndex);
      m.set(sig, set);
    }
    return m;
  }, [dedupedFindings]);

  // "Fix these first" — the top open findings by score impact, plus where
  // the score lands if they're fixed. The projection runs the REAL model:
  // each removed finding returns its penalty to its own page (clamped at
  // 100), the site score re-averages, and the 89 grade cap lifts only when
  // no high-severity finding remains. Null when there's nothing open or
  // no meaningful gain to promise.
  const fixFirst = useMemo(() => {
    if (!overviewBreakdown || overviewScore == null) return null;
    const open = dedupedFindings.filter(
      (e) => e.annotation.kind !== "custom" && !solved.has(e.id),
    );
    if (open.length === 0) return null;
    const rank = { high: 0, medium: 1, low: 2 } as const;
    const top = [...open]
      .sort(
        (a, b) => rank[a.annotation.severity] - rank[b.annotation.severity],
      )
      .slice(0, 3);
    const returnedByPage = new Map<number, number>();
    for (const e of top) {
      returnedByPage.set(
        e.pageIndex,
        (returnedByPage.get(e.pageIndex) ?? 0) +
          SCORE_PENALTY[e.annotation.severity],
      );
    }
    const projPages = overviewBreakdown.pageScores.map(({ pageIndex, score }) =>
      Math.min(100, score + (returnedByPage.get(pageIndex) ?? 0)),
    );
    const projMean = Math.round(
      projPages.reduce((s, v) => s + v, 0) / (projPages.length || 1),
    );
    const removedIds = new Set(top.map((e) => e.id));
    const anyHighLeft = dedupedFindings.some(
      (e) =>
        e.annotation.kind !== "custom" &&
        !removedIds.has(e.id) &&
        e.annotation.severity === "high",
    );
    const projected = anyHighLeft ? Math.min(projMean, 89) : projMean;
    if (projected <= overviewScore) return null;
    return { items: top, projected };
  }, [dedupedFindings, solved, overviewBreakdown, overviewScore]);

  // Header metadata for the export — built once and reused by the dialog.
  // Mirrors the left-rail overview so a copied report carries the same
  // context the canvas shows.
  const exportMeta = useMemo<FindingsExportMeta>(
    () => ({
      siteName: result.siteName || hostnameOf(result.sourceUrl),
      url: result.sourceUrl,
      score: overviewScore ?? null,
      grade: overviewGrade?.label ?? null,
      total: totalFindings2,
      high: highCount,
      medium: medCount,
      low: lowCount,
      coverage:
        captureHealth && captureHealth.excludedCount > 0 && !captureHealth.allFailed
          ? `Scored from ${captureHealth.totalCount - captureHealth.excludedCount} of ${captureHealth.totalCount} pages — couldn't capture: ${captureHealth.excludedNames.join(", ")}`
          : null,
      generatedBy: "Findings generated by Snap Site",
    }),
    [
      result.siteName,
      result.sourceUrl,
      overviewScore,
      overviewGrade,
      totalFindings2,
      highCount,
      medCount,
      lowCount,
      captureHealth,
    ],
  );

  // Helper: stable id for a finding row (matches activeFindingId,
  // bookmarked / solved sets, and the dot click contract).
  const findingId = useCallback((f: FindingEntry) => f.id, []);

  // Page name lookup for the page-name pill on each finding card.
  // Built once per result so it's cheap to read inside the filteredFindings
  // map below and the JSX render of each card. Index space matches
  // buildLayout — homepage at 0, then result.pages in order.
  const pageNameByIndex = useMemo(() => {
    const map: Record<number, string> = {};
    const allPages = [result.homepage, ...(result.pages ?? [])].filter(
      Boolean,
    ) as CapturedPage[];
    allPages.forEach((p, i) => {
      map[i] = p.name || hostnameOf(p.url) || `Page ${i + 1}`;
    });
    return map;
  }, [result.homepage, result.pages]);

  // Only use findings the server has explicitly left readable. Keep the
  // preview together so guests get value before encountering locked rows.
  const freeFindings = useMemo(() => isLocked
    ? dedupedFindings.filter((f) => !f.annotation.locked).sort((a, b) => {
      const priority = { high: 0, medium: 1, low: 2 };
      return priority[a.annotation.severity] - priority[b.annotation.severity];
    }).slice(0, 3)
    : [], [isLocked, dedupedFindings]);
  const showFreeFindings = freeFindings.length > 0 && severityFilter === "all" && sourceFilter === "all" && !bookmarkedOnly;

  // Filtered findings:
  //  • severity, source, and bookmark-only filters all apply (AND).
  //  • Sorted by [page, source group, finding number] so same-source
  //    siblings are adjacent within each page section. This is what
  //    lets the rail render NN/g and Accessibility as sub-groups
  //    inside each page section — and avoids the visual confusion
  //    of two findings with number "1" sitting next to each other
  //    (numbers are unique within a source-per-page bucket).
  //  • Open findings first, solved pinned to the bottom (partition
  //    happens AFTER sort so each partition stays sorted).
  const filteredFindings = useMemo(() => {
    const base = dedupedFindings.filter((f) => {
      if (severityFilter !== "all" && f.annotation.severity !== severityFilter) return false;
      // sourceFilter now carries the finding category (or "custom"); the
      // value matches the annotation's kind directly. "all" = no filter.
      if (sourceFilter !== "all" && f.annotation.kind !== sourceFilter) return false;
      if (bookmarkedOnly && !bookmarked.has(findingId(f))) return false;
      return true;
    });

    // Sort order — most-actionable findings first within each
    // source group:
    //   1. Page  — keeps each page section together
    //   2. Source group — NN/g, then Accessibility, then Custom
    //   3. Placed (numbered) before unplaced (page-level), so
    //      findings the user can locate on the screenshot lead;
    //      page-wide observations sit at the bottom of each
    //      source group
    //   4. Severity — High → Medium → Low so the eye lands on
    //      the most urgent rows first
    //   5. Number — stable tiebreaker so the rail order matches
    //      the canvas pin numbering
    const sourceOrder = (kind: AuditAnnotation["kind"]): number =>
      kind === "heuristic"
        ? 0
        : kind === "accessibility"
          ? 1
          : kind === "bestpractice"
            ? 2
            : 3;
    const severityOrder = (s: AuditAnnotation["severity"]): number =>
      s === "high" ? 0 : s === "medium" ? 1 : 2;
    base.sort((a, b) => {
      if (a.pageIndex !== b.pageIndex) return a.pageIndex - b.pageIndex;
      const ag = sourceOrder(a.annotation.kind);
      const bg = sourceOrder(b.annotation.kind);
      if (ag !== bg) return ag - bg;
      if (a.placed !== b.placed) return a.placed ? -1 : 1;
      const as = severityOrder(a.annotation.severity);
      const bs = severityOrder(b.annotation.severity);
      if (as !== bs) return as - bs;
      return (a.annotation.number || 0) - (b.annotation.number || 0);
    });

    // Stable partition: open first, solved at bottom.
    const open: typeof base = [];
    const closed: typeof base = [];
    for (const f of base) {
      (solved.has(findingId(f)) ? closed : open).push(f);
    }
    return [...open, ...closed];
  }, [dedupedFindings, severityFilter, sourceFilter, bookmarkedOnly, bookmarked, solved, findingId]);

  const railFindings = useMemo(() => showFreeFindings
    ? filteredFindings.filter((finding) => !freeFindings.some((sample) => sample.id === finding.id))
    : filteredFindings, [showFreeFindings, filteredFindings, freeFindings]);

  const totalBookmarked = bookmarked.size;
  // Index of the first solved item in the filtered list — used to
  // inject a visual separator between open and resolved findings.
  const firstSolvedIdx = useMemo(() => {
    for (let i = 0; i < filteredFindings.length; i++) {
      if (solved.has(findingId(filteredFindings[i]))) return i;
    }
    return -1;
  }, [filteredFindings, solved, findingId]);

  // Per-page severity breakdown for the rail's section headers — keyed
  // by pageIndex, recomputed from the filtered list so it reflects what
  // the user currently sees (severity / source / bookmark filters all
  // apply). Used to render "Home · 2 findings · 1 high, 1 medium".
  const pageBreakdownByIndex = useMemo(() => {
    const map = new Map<number, { total: number; high: number; medium: number; low: number }>();
    for (const f of filteredFindings) {
      const entry = map.get(f.pageIndex) ?? { total: 0, high: 0, medium: 0, low: 0 };
      entry.total += 1;
      if (f.annotation.severity === "high") entry.high += 1;
      else if (f.annotation.severity === "medium") entry.medium += 1;
      else entry.low += 1;
      map.set(f.pageIndex, entry);
    }
    return map;
  }, [filteredFindings]);

  // PDF export. The server authorizes the paid feature, then the browser
  // captures the exact live canvas it is already rendering. That preserves
  // screenshots, pins, callout cards, custom annotations, and saved layout
  // without launching a fragile second browser in a serverless function.
  const [exportPhase, setExportPhase] = useState<
    "idle" | "prepare" | "capture" | "compile" | "save" | "done"
  >("idle");
  const exporting = exportPhase !== "idle" && exportPhase !== "done";
  // Keep the feedback prompt out of the way of every other dialog/overlay
  // (see the first-audit feedback effect above — it polls this ref when
  // its timer fires and waits if something else is up).
  useEffect(() => {
    feedbackBusyRef.current = !!(
      formSeed ||
      deleteTarget ||
      exportOpen ||
      addPageOpen ||
      shareModalOpen ||
      mobileSheet ||
      exportPhase !== "idle"
    );
  }, [formSeed, deleteTarget, exportOpen, addPageOpen, shareModalOpen, mobileSheet, exportPhase]);
  // Holds the in-flight export request so Cancel can abort it. Cleared
  // when the export settles (success, error, or cancel).
  const exportAbortRef = useRef<AbortController | null>(null);
  const exportCancelledRef = useRef(false);
  const handleExport = useCallback(async () => {
    if (exporting) return;
    const stageInner = stageInnerRef.current;
    if (!stageInner) {
      showToast("The canvas is not ready to export yet.", "error");
      return;
    }
    const controller = new AbortController();
    exportAbortRef.current = controller;
    exportCancelledRef.current = false;
    setExportPhase("prepare");
    try {
      await authorizeAuditPdfExport(
        job.id,
        readToken(),
        undefined,
        controller.signal,
      );
      if (exportCancelledRef.current) return;

      // Keep the sizeable capture libraries out of the audit's initial
      // bundle. They load only after the entitlement check passes.
      const { buildCanvasPdfBlob, suggestedFilename } = await import(
        "../../../lib/export-pdf"
      );
      const blob = await buildCanvasPdfBlob({
        stageInner,
        width: totalWidth,
        height: totalHeight,
        siteName: result.siteName ?? "",
        sourceUrl: result.sourceUrl,
        onProgress: (phase) => {
          if (!exportCancelledRef.current) setExportPhase(phase);
        },
        isCancelled: () => exportCancelledRef.current,
      });
      if (exportCancelledRef.current) return;
      downloadBlob(blob, suggestedFilename(result.siteName ?? "", result.sourceUrl));
      setExportPhase("done");
      // Brief "Done!" confirmation before the overlay snaps out.
      window.setTimeout(() => setExportPhase("idle"), 900);
    } catch (err) {
      // User cancelled — the fetch was aborted. Dismiss quietly, no error.
      if (
        exportCancelledRef.current ||
        controller.signal.aborted ||
        (err instanceof Error &&
          (err.name === "AbortError" || err.name === "ExportCancelled"))
      ) {
        setExportPhase("idle");
        return;
      }
      // eslint-disable-next-line no-console
      console.error("[canvas] PDF export failed", err);
      const status = (err as { status?: number })?.status;
      showToast(
        status === 403
          ? "Exporting to PDF is on the Pro plan."
          : "Could not capture the canvas as a PDF. Please try again.",
        "error",
      );
      setExportPhase("idle");
    } finally {
      exportAbortRef.current = null;
    }
  }, [exporting, job.id, result.siteName, result.sourceUrl, showToast, totalHeight, totalWidth]);

  // Cancel an in-progress export: abort the request and drop the overlay
  // right away. If the request already finished, this is a no-op beyond
  // hiding the overlay.
  const handleCancelExport = useCallback(() => {
    exportCancelledRef.current = true;
    exportAbortRef.current?.abort();
    setExportPhase("idle");
  }, []);

  // Share now opens a modal (ShareModal) that surfaces the link in a
  // selectable field with an explicit Copy button, rather than copying
  // silently on click. Showing the URL first lets the user see exactly
  // what they're sharing (and grab it manually if the clipboard API is
  // blocked) before anything lands on their clipboard.

  return (
    <div
      className="cv-shell"
      data-mode={mode}
      data-mobile-sheet={mobileSheet ?? undefined}
    >
      {/* Mobile-only orientation note — the canvas is a wide pan/zoom
          workspace, so at phone widths we say what this view is and how to
          drive it, then point at the bigger-screen experience. Framed as
          orientation, not apology: the visitor should feel they're in a
          compact mode of something powerful, not the wrong place. CSS hides
          this above 900 px; dismissible and persisted via localStorage.
          Hidden behind the locked-mode summary sheet, which carries the same
          message in its footnote — one voice at a time. */}
      {!desktopHintHidden && !(isLocked && lockedSummaryOpen) ? (
        <div className="cv-desktop-hint" role="note">
          <svg
            className="cv-desktop-hint-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M8 21h8M12 17v4" />
          </svg>
          <span className="cv-desktop-hint-text">
            You&rsquo;re in the compact view — pinch to zoom, drag to pan.
            The full workspace opens on a bigger screen.
          </span>
          <button
            type="button"
            className="cv-desktop-hint-dismiss"
            onClick={dismissDesktopHint}
            aria-label="Dismiss"
            data-tip="Dismiss"
          >
            ×
          </button>
        </div>
      ) : null}
      {/* ─── Top bar ─── */}
      {isReadOnly ? (
        <header className="cv-topbar cv-topbar-public">
          <div className="cv-public-left">
            {/* Back arrow — gives demo visitors an obvious path back
                to the homepage demo gallery. The brand-logo link
                next to it also goes home, but the logo doesn't read
                as a back button; this matches the chevron-in-circle
                affordance the owner topbar already uses (`.cv-back`).
                `#demos` jumps straight to the gallery section so
                they can pick another demo without scrolling. Locked
                guests came from the hero, not the gallery, so theirs
                goes home. */}
            <Link
              href={isLocked ? "/" : "/#demos"}
              className="cv-back cv-public-back"
              aria-label={isLocked ? "Back to home" : "Back to demos"}
              data-tip={isLocked ? "Back to home" : "Back to the demo gallery"}
            >
              <ChevronLeftIcon />
            </Link>
            <Link href="/" className="cv-public-brand" aria-label="Snap Site home">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/brand/snap-site-mark.svg" alt="" width={28} height={28} />
              <span className="cv-public-wordmark">Snap Site</span>
            </Link>
            <span className="cv-public-divider" aria-hidden="true" />
            <span className="cv-public-badge">
              {isLocked ? "Free preview" : "Shared audit"}
            </span>
          </div>

          <div className="cv-public-center">
            <h1
              className="cv-public-title"
              title={result.siteName || hostnameOf(result.sourceUrl)}
            >
              {result.siteName || hostnameOf(result.sourceUrl)}
            </h1>
            {result.runMode === "imageAudit" ? (
              <span className="cv-topbar-url cv-topbar-url-static">
                Uploaded image
              </span>
            ) : (
              <a
                href={result.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cv-public-url"
              >
                {hostnameOf(result.sourceUrl)} <ExternalLinkIcon />
              </a>
            )}
            <div className="cv-public-stats">
              {/* Viewport + page count only — the finding count
                  used to live here too, but the right-rail
                  "Findings (N)" header and the left-rail score
                  block both surface it already. Three places for
                  one number was noisy chrome. */}
              <span className="cv-stat"><MonitorIcon /> {viewportLabel}</span>
              <span className="cv-stat"><PagesIcon /> {totalPages} page{totalPages !== 1 ? "s" : ""}</span>
            </div>
          </div>

          <div className="cv-public-right">
            <div className="cv-zoom-controls cv-zoom-inline">
              <button
                type="button"
                className="cv-zoom-btn cv-zoom-labeled"
                onClick={fitToScreen}
                aria-label="Fit screenshot to screen"
                data-tip="Fit to screen"
              >
                <FitIcon /> <span className="cv-zoom-label-text">Fit</span>
              </button>
              <button type="button" className="cv-zoom-btn" onClick={() => setZoom(transform.scale / 1.25)} aria-label="Zoom out" data-tip="Zoom out">−</button>
              <button
                type="button"
                className="cv-zoom-pct cv-zoom-pct-btn"
                onClick={() => setZoom(1)}
                aria-label={`Current zoom ${zoomPct}% — click to reset to 100%`}
                data-tip="Click to reset zoom to 100%"
              >
                {zoomPct}%
              </button>
              <button type="button" className="cv-zoom-btn" onClick={() => setZoom(transform.scale * 1.25)} aria-label="Zoom in" data-tip="Zoom in">+</button>
              <button
                type="button"
                className="cv-zoom-btn cv-zoom-labeled"
                aria-label={isFullscreen ? "Exit full-screen mode" : "Enter full-screen mode"}
                data-tip={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                onClick={toggleFullscreen}
              >
                {isFullscreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
                <span className="cv-zoom-label-text">{isFullscreen ? "Exit" : "Full"}</span>
              </button>
            </div>
            <span className="cv-topbar-divider" aria-hidden="true" />
            <ThemeToggle />
            {/* Locked guests have already seen their own audit run — they're
                warm, so the CTA opens sign-in directly to unlock the full
                report (and claim it). Public share viewers haven't been
                pitched yet, so theirs goes to marketing home instead. */}
            {isLocked ? (
              /* "— free" everywhere: one action, one label. The topbar,
                 the rail button, the mobile summary sheet, and the /try
                 unlock bar all read "Unlock full report — free" so the
                 guest meets one promise, not three competing ones. */
              <button
                type="button"
                className="cv-action-btn cv-action-primary"
                onClick={() => handleUnlock("topbar")}
              >
                <LockIcon />
                {/* ONE span for the whole label: .cv-action-btn is flex
                    with gap 6px, and a separate suffix span became its
                    own flex item — "report␣␣— free". Suffix still drops
                    at ≤560 px (CSS); down there the summary sheet +
                    unlock bar carry the full wording. */}
                <span>
                  Unlock full report
                  <span className="cv-unlock-free">&nbsp;— free</span>
                </span>
              </button>
            ) : (
              <>
                {canExport ? (
                  <button
                    type="button"
                    className="cv-action-btn cv-action-btn-tertiary cv-public-export-btn"
                    onClick={handleExport}
                    disabled={exporting}
                    aria-label="Download this audit canvas as a PDF"
                    data-tip="Download this audit canvas as a PDF"
                  >
                    <ExportIcon />
                    <span className="cv-public-export-label">
                      {exporting ? "Preparing…" : "Download PDF"}
                    </span>
                  </button>
                ) : null}
                <Link
                  href={START_AUDIT_HREF}
                  className="cv-action-btn cv-action-primary"
                >
                  Try Snap Site
                </Link>
              </>
            )}
          </div>
        </header>
      ) : (
        <header className="cv-topbar">
          <div className="cv-topbar-left">
            <Link href="/dashboard" className="cv-back" aria-label="Back to dashboard" data-tip="Go back to your dashboard">
              <ChevronLeftIcon />
            </Link>
            <div className="cv-topbar-info">
              <div className="cv-topbar-title-row">
                <h1 className="cv-topbar-title">
                  {result.siteName || hostnameOf(result.sourceUrl)}
                </h1>
                {/* Provenance — surfaced inline with the title when the
                    audit was created in the Figma plugin. */}
                {job.origin === "plugin" ? <FigmaOriginChip /> : null}
              </div>
              {result.runMode === "imageAudit" ? (
                <span className="cv-topbar-url cv-topbar-url-static">
                  Uploaded image
                </span>
              ) : (
                <a
                  href={result.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cv-topbar-url"
                >
                  {hostnameOf(result.sourceUrl)} <ExternalLinkIcon />
                </a>
              )}
            </div>
          </div>

          {/* The three audit stats (viewport / page count / finding
              count) used to sit here as pill chips. They've moved to
              the left rail's "Audit Overview" block where they belong
              with the rest of the audit context — the top bar is now
              focused on the title and primary actions only. */}

          <div className="cv-topbar-actions">
            {/* Zoom cluster — merged from the old sub-bar into the
                topbar so the canvas gets a full extra row of height. */}
            <div className="cv-zoom-controls cv-zoom-inline">
              <button
                type="button"
                className="cv-zoom-btn cv-zoom-labeled"
                onClick={fitToScreen}
                aria-label="Fit screenshot to screen"
                data-tip="Fit to screen"
              >
                <FitIcon /> Fit
              </button>
              <button type="button" className="cv-zoom-btn" onClick={() => setZoom(transform.scale / 1.25)} aria-label="Zoom out" data-tip="Zoom out">−</button>
              <button
                type="button"
                className="cv-zoom-pct cv-zoom-pct-btn"
                onClick={() => setZoom(1)}
                aria-label={`Current zoom ${zoomPct}% — click to reset to 100%`}
                data-tip="Click to reset zoom to 100%"
              >
                {zoomPct}%
              </button>
              <button type="button" className="cv-zoom-btn" onClick={() => setZoom(transform.scale * 1.25)} aria-label="Zoom in" data-tip="Zoom in">+</button>
              <button
                type="button"
                className="cv-zoom-btn cv-zoom-labeled"
                aria-label={isFullscreen ? "Exit full-screen mode" : "Enter full-screen mode"}
                data-tip={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                onClick={toggleFullscreen}
              >
                {isFullscreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
                {isFullscreen ? "Exit" : "Full"}
              </button>
            </div>
            <span className="cv-topbar-divider" aria-hidden="true" />
            <ThemeToggle />
            {/* Blank-capture audits hide Share + Export: there's nothing
                worth sharing or exporting (a blank PDF / empty public
                page), and offering them on a dead-end screen is the
                "what's the point of these CTAs" problem. */}
            {captureHealth?.allFailed ? null : canShare ? (
              <button type="button" className="cv-action-btn cv-action-btn-tertiary" onClick={() => setShareModalOpen(true)} aria-label="Get a public link so anyone can view this audit" data-tip="Get a public link so anyone can view this audit">
                <ShareIcon />
                Share
              </button>
            ) : (
              // Locked share for free users — was a plain anchor that
              // silently opened pricing in a new tab. Users whose
              // browser didn't focus the new tab saw nothing happen
              // after their click, which read as "the button is
              // broken." Now it's a real button that (1) fires a
              // visible toast confirming the action and explaining
              // the gate, and (2) routes through the rate-limited
              // openUpgradeInNewTab so multiple clicks don't spawn
              // multiple tabs (consistent with the locked Pro chips
              // on /audit/new).
              <button
                type="button"
                className="cv-action-btn cv-action-btn-tertiary cv-action-locked"
                onClick={() => {
                  showToast(
                    "Sharing public links is on the Pro plan. We opened your plan options in a new tab.",
                    "warn",
                  );
                  openUpgradeInNewTab();
                }}
                aria-label="Share a public link to this audit — available on the Pro plan"
                data-tip="Share a public link to this audit — available on the Pro plan"
              >
                <ShareIcon />
                Share <span className="cv-pro-pill">Pro</span>
              </button>
            )}
            {captureHealth?.allFailed ? null : canExport ? (
              <button
                type="button"
                className="cv-action-btn cv-action-btn-tertiary"
                onClick={handleExport}
                disabled={exporting}
                aria-label="Download this audit canvas as a PDF" data-tip="Download this audit canvas as a PDF"
              >
                <ExportIcon />
                {exporting ? "Preparing…" : "Download PDF"}
              </button>
            ) : (
              // Locked PDF export for free users — mirrors the Share
              // gate: a visible toast explaining the gate plus the
              // rate-limited openUpgradeInNewTab so repeat clicks don't
              // spawn multiple tabs.
              <button
                type="button"
                className="cv-action-btn cv-action-btn-tertiary cv-action-locked"
                onClick={() => {
                  showToast(
                    "Exporting to PDF is on the Pro plan. We opened your plan options in a new tab.",
                    "warn",
                  );
                  openUpgradeInNewTab();
                }}
                aria-label="Export this audit as a PDF — available on the Pro plan"
                data-tip="Export this audit as a PDF — available on the Pro plan"
              >
                <ExportIcon />
                Download PDF <span className="cv-pro-pill">Pro</span>
              </button>
            )}
            {/* Mobile overflow trigger — at ≤640 px the zoom cluster,
                Share, and Export are hidden by CSS to keep the topbar
                under the viewport width. This button reveals them in
                a popover. Hidden above 640 px so the inline buttons
                remain the only path on desktop. */}
            <div className="cv-topbar-overflow-wrap">
              <button
                type="button"
                className="cv-action-btn cv-action-btn-tertiary cv-topbar-overflow-btn"
                onClick={() => setTopbarMenuOpen((v) => !v)}
                aria-label={topbarMenuOpen ? "Close actions menu" : "Open actions menu"}
                aria-haspopup="menu"
                aria-expanded={topbarMenuOpen}
                data-tip="More actions"
              >
                <MoreIcon />
              </button>
              {topbarMenuOpen && (
                <>
                  <div
                    className="cv-topbar-overflow-backdrop"
                    onClick={() => setTopbarMenuOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="cv-topbar-overflow-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { fitToScreen(); setTopbarMenuOpen(false); }}
                    >
                      <FitIcon /> Fit to screen
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setZoom(transform.scale / 1.25); }}
                    >
                      <span aria-hidden="true">−</span> Zoom out
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setZoom(1); }}
                    >
                      <span aria-hidden="true">{zoomPct}%</span> Reset zoom
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setZoom(transform.scale * 1.25); }}
                    >
                      <span aria-hidden="true">+</span> Zoom in
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { toggleFullscreen(); setTopbarMenuOpen(false); }}
                    >
                      {isFullscreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
                      {isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                    </button>
                    <div className="cv-topbar-overflow-divider" aria-hidden="true" />
                    {canShare ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setShareModalOpen(true); setTopbarMenuOpen(false); }}
                      >
                        <ShareIcon />
                        Share
                      </button>
                    ) : (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          showToast(
                            "Sharing public links is on the Pro plan. We opened your plan options in a new tab.",
                            "warn",
                          );
                          openUpgradeInNewTab();
                          setTopbarMenuOpen(false);
                        }}
                      >
                        <ShareIcon />
                        Share <span className="cv-pro-pill">Pro</span>
                      </button>
                    )}
                    {canExport ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { handleExport(); setTopbarMenuOpen(false); }}
                        disabled={exporting}
                      >
                        <ExportIcon />
                        {exporting ? "Preparing…" : "Download PDF"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          showToast(
                            "Exporting to PDF is on the Pro plan. We opened your plan options in a new tab.",
                            "warn",
                          );
                          openUpgradeInNewTab();
                          setTopbarMenuOpen(false);
                        }}
                      >
                        <ExportIcon />
                        Download PDF <span className="cv-pro-pill">Pro</span>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Sub-bar removed — zoom controls now live in the topbar. */}

      {/* ─── Three-column body ─── */}
      {/* Mobile sheet triggers — only visible at ≤900 px where the
          rails are hidden by default. Two fixed-position pills sit
          over the canvas; tapping either re-surfaces the
          corresponding rail as a full-screen sheet (controlled via
          the `data-mobile-sheet` attribute on `.cv-shell`). The
          counts come straight off the existing layout/result data
          so they stay in sync with what's actually in the rails. */}
      <div className="cv-mobile-triggers" aria-hidden={mobileSheet !== null}>
        <button
          type="button"
          className="cv-mobile-trigger cv-mobile-trigger-left"
          onClick={openLeftSheet}
          aria-label="Open audit overview"
        >
          <span className="cv-mobile-trigger-label">Overview</span>
          <span className="cv-mobile-trigger-meta">{totalPages} page{totalPages !== 1 ? "s" : ""}</span>
        </button>
        <button
          type="button"
          className="cv-mobile-trigger cv-mobile-trigger-right"
          onClick={openRightSheet}
          aria-label="Open findings list"
        >
          <span className="cv-mobile-trigger-label">Findings</span>
          <span className="cv-mobile-trigger-meta">{dedupedFindings.length}</span>
        </button>
      </div>

      {/* Backdrop for the mobile sheets. Click anywhere off the
          sheet to close. Pointer-events / opacity are CSS-driven
          off the `data-mobile-sheet` attribute so the backdrop
          fades in only when a sheet is open. */}
      <div
        className="cv-mobile-sheet-backdrop"
        onClick={closeMobileSheet}
        aria-hidden="true"
      />

      {/* ── Locked-mode mobile summary sheet ──
          The first thing a guest sees on a phone when their run completes:
          the score, the findings count, and the unlock CTA — the payoff
          they waited for, readable without pinching into the canvas. On
          desktop the left rail already does this job (CSS hides the sheet
          above 900 px). Dismissing it lands on the canvas with the
          persistent unlock bar still available below.

          PORTALED to <body>: .cv-shell is a fixed stacking context at
          z-index 100, so anything rendered inside it — whatever its own
          z-index — paints BELOW fixed siblings like the guest unlock bar
          (z-130) and the sign-in overlay (z-200). Rendered in place, the
          unlock bar bled through the sheet. On body, the sheet's own
          z-150/160 layers land where they say they do. (Same pattern as
          TooltipManager. Locked mode only mounts client-side — the job
          arrives via polling — so the document guard never desyncs SSR.) */}
      {(isLocked || isViewer) &&
      lockedSummaryOpen &&
      typeof document !== "undefined" ? (
        createPortal(
        <div
          className="cv-locked-sheet-layer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cv-locked-sheet-title"
        >
          <button
            type="button"
            className="cv-locked-sheet-backdrop"
            aria-label="Dismiss audit summary"
            onClick={() => setLockedSummaryOpen(false)}
          />
          <section className="cv-locked-sheet">
            <p className="cv-locked-sheet-eyebrow">
              {isLocked
                ? "Your audit is ready"
                : job.isDemo
                  ? "Live demo audit"
                  : "Shared audit"}
            </p>
            <div className="cv-locked-sheet-hero">
              {overviewScore != null && overviewGrade ? (
                <div
                  className="cv-locked-sheet-ring"
                  data-tone={overviewGrade.tone}
                >
                  <ScoreRing
                    score={overviewScore}
                    tone={overviewGrade.tone}
                    size={68}
                  />
                </div>
              ) : null}
              <div className="cv-locked-sheet-meta">
                <h2 id="cv-locked-sheet-title">
                  {totalFindings2 > 0 ? (
                    <>
                      {totalFindings2} finding
                      {totalFindings2 === 1 ? "" : "s"} on{" "}
                      {result.siteName || hostnameOf(result.sourceUrl)}
                    </>
                  ) : (
                    <>
                      {result.siteName || hostnameOf(result.sourceUrl)},
                      audited
                    </>
                  )}
                </h2>
                {/* Grade pill (same tones as the summary card) + a quiet
                    label. The ring already shows the number — repeating
                    "33" in text made the hero read twice as loud. */}
                {overviewGrade ? (
                  <p className="cv-locked-sheet-grade">
                    <span
                      className="cv-locked-sheet-grade-pill"
                      data-tone={overviewGrade.tone}
                    >
                      {overviewGrade.label}
                    </span>
                    Snap Site Score
                  </p>
                ) : null}
                {totalFindings2 > 0 ? (
                  <div
                    className="cv-locked-sheet-sevs"
                    aria-label={`${highCount} high, ${medCount} medium, ${lowCount} low`}
                  >
                    {highCount > 0 ? (
                      <span data-sev="high">{highCount} high</span>
                    ) : null}
                    {medCount > 0 ? (
                      <span data-sev="medium">{medCount} medium</span>
                    ) : null}
                    {lowCount > 0 ? (
                      <span data-sev="low">{lowCount} low</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
            {/* Capture honesty — when a page couldn't be captured (blocked,
                blank…), say so here. Without it, a low finding count on a
                famous site reads as either "site is perfect" or "tool is
                broken"; with it, the number has a reason. */}
            {captureHealth ? (
              <p className="cv-locked-sheet-capture">
                {captureHealth.title} ({captureHealth.failedCount} of{" "}
                {captureHealth.totalCount}) — those pages are excluded from
                the score.
              </p>
            ) : null}
            {isLocked ? (
              <button
                type="button"
                className="cv-locked-sheet-unlock"
                onClick={() => {
                  if (freeFindings.length > 0) {
                    setLockedSummaryOpen(false);
                    openRightSheet();
                    track("Guest Preview Opened", { source: "summary" });
                  } else handleUnlock("sheet");
                }}
              >
                {freeFindings.length > 0 ? `Read your ${freeFindings.length} free findings` : "Unlock full report — free"}
              </button>
            ) : (
              <Link
                className="cv-locked-sheet-unlock"
                href={START_AUDIT_HREF}
                onClick={() =>
                  track("Try From Demo", { demo: job.isDemo ?? false })
                }
              >
                Try Snap Site on your site — free
              </Link>
            )}
            <button
              type="button"
              className="cv-locked-sheet-explore"
              onClick={() => {
                setLockedSummaryOpen(false);
                if (isLocked && freeFindings.length > 0) handleUnlock("sheet");
                if (isViewer && totalFindings2 > 0) {
                  track("Demo Findings Viewed", {
                    demo: job.isDemo ?? false,
                    findings: totalFindings2,
                  });
                  openRightSheet();
                }
              }}
            >
              {isViewer && totalFindings2 > 0
                ? `See the ${totalFindings2} finding${totalFindings2 === 1 ? "" : "s"}`
                : isLocked && freeFindings.length > 0 ? "Unlock the full report — free" : "Preview the audit"}
            </button>
            <p className="cv-locked-sheet-note">
              {isLocked ? (
                <>
                  Your preview is ready to read here. A free account unlocks
                  every finding and saves your report for later.
                </>
              ) : (
                <>
                  Open the findings to review each issue and why it matters.
                  The full workspace opens on a bigger screen.
                </>
              )}
            </p>
          </section>
        </div>,
        document.body,
        )
      ) : null}

      <div className="cv-body">
        {/* ── Left: Audit overview + Captured Pages ── */}
        <aside
          className={`cv-sidebar-left${leftRailCollapsed ? " is-collapsed" : ""}`}
          aria-label="Audit navigation"
        >
          <button
            type="button"
            className="cv-rail-toggle cv-rail-toggle-left"
            onClick={toggleLeftRail}
            aria-label={leftRailCollapsed ? "Show audit navigation" : "Hide audit navigation"}
            aria-expanded={!leftRailCollapsed}
          >
            <PanelToggleIcon side="left" collapsed={leftRailCollapsed} />
          </button>
          {/* Mobile-only close button (visible at ≤900 px via CSS).
              The chevron toggle above is desktop chrome — on mobile
              the rail is rendered as a full-screen sheet, so users
              need a clear X to dismiss it back to the canvas. */}
          <button
            type="button"
            className="cv-mobile-sheet-close"
            onClick={closeMobileSheet}
            aria-label="Close audit overview"
          >
            <CloseIcon />
          </button>
          {/* Audit overview — restructured for a calmer first
              read. Three things visible at rest: score number +
              grade label, finding count, severity bar. Everything
              else (severity counts, source split, scope) collapses
              behind a "More details" disclosure so the rail isn't
              a wall of data on first arrival. The dedicated
              "Audit overview" section heading is gone — the score
              IS the overview, no need to label it. */}
          <section
            className="cv-overview"
            aria-label={`Audit overview: ${totalFindings2} ${totalFindings2 === 1 ? "finding" : "findings"}, ${highCount} high, ${medCount} medium, ${lowCount} low, ${viewportLabel}, ${totalPages} ${totalPages === 1 ? "page" : "pages"}`}
          >
            {/* Clean-audit affirmation. Replaces the score block
                entirely when there are zero findings — the win
                shouldn't share space with a meta-score it isn't
                describing. */}
            {totalFindings2 === 0 ? (
              /* Snapshot-only runs never produce AI findings by design
                 — saying "No issues found" implied the site is clean
                 when really we just didn't analyze it. For those
                 runs we explain the situation and nudge the user
                 toward Add finding so the empty list isn't a
                 dead-end. Audit / imageAudit runs that genuinely
                 find zero issues keep the celebratory copy. */
              result.runMode === "snapshot" && !captureHealth ? (
                <div
                  className="cv-overview-clean cv-overview-clean-snapshot"
                  role="status"
                  data-tone="neutral"
                >
                  <span className="cv-overview-clean-icon" aria-hidden="true">
                    <CameraIcon />
                  </span>
                  <div className="cv-overview-clean-text">
                    <strong>Snapshots only — no AI findings</strong>
                    <span>
                      You ran a screenshot-only capture, so no UX or accessibility
                      review was performed. Use <em>Add finding</em> to annotate
                      issues yourself, or re-run as a full audit.
                    </span>
                  </div>
                </div>
              ) : captureHealth ? (
                /* Captures failed (blank / blocked / not-found). 0 findings
                   here is the absence of a usable capture, NOT a clean site,
                   so we replace the green affirmation with an honest warn
                   card that nudges a re-run. */
                <div className="cv-capture-card" role="status" data-tone="warn">
                  <div className="cv-capture-card-head">
                    <span className="cv-capture-card-icon" aria-hidden="true">
                      <AlertTriangleIcon />
                    </span>
                    <strong>{captureHealth.title}</strong>
                  </div>
                  <p className="cv-capture-card-detail">{captureHealth.detail}</p>
                  {/* Credit reassurance — backend refunds an all-blank run
                      (settleBlankCapture). Owner-only; a public viewer
                      wasn't charged anything. Divider separates it as a
                      distinct "good news" note. */}
                  {isOwner && captureHealth.allFailed ? (
                    <p className="cv-capture-card-refund">
                      <strong>No credit was lost</strong> — refunded
                      automatically.
                    </p>
                  ) : null}
                  {/* The one useful next step. Bot-wall / blank: "Upload
                      image" mode, captured from the user's own browser —
                      not a re-run, which would just blank again. Server-down
                      (5xx): the opposite — the server was napping and is
                      almost certainly awake now, so a re-run IS the fix. */}
                  {!isOwner ? (
                    /* Shared/guest view — the viewer can't upload to someone
                       else's run, so the useful next step is to start their
                       own. Funnels a shared audit toward sign-up. */
                    <Link
                      href={START_AUDIT_HREF}
                      className="cv-capture-card-cta"
                    >
                      Try Snap Site — free
                    </Link>
                  ) : captureHealth.serverDown ? (
                    <Link
                      href={`/audit/new?url=${encodeURIComponent(result.sourceUrl)}`}
                      className="cv-capture-card-cta"
                    >
                      Run the audit again
                    </Link>
                  ) : (
                    <Link
                      href="/audit/new?mode=imageAudit"
                      className="cv-capture-card-cta"
                    >
                      Upload a screenshot to audit
                    </Link>
                  )}
                </div>
              ) : (
                <div className="cv-overview-clean" role="status">
                  <span className="cv-overview-clean-icon" aria-hidden="true">
                    <CheckIcon />
                  </span>
                  <div className="cv-overview-clean-text">
                    <strong>Clean first pass</strong>
                    <span>
                      No automated issues found. Finish with a{" "}
                      <button
                        type="button"
                        className="cv-clean-review"
                        aria-label="What a manual review still covers"
                        data-tip={MANUAL_REVIEW_TIP}
                      >
                        manual review
                      </button>
                      .
                    </span>
                  </div>
                </div>
              )
            ) : (
              <>
                {/* Clean header — eyebrow label, big number, grade
                    + count. The eyebrow ("Snap Site Score") tells
                    the user what the big number means before they
                    have to guess. Without it the "5" floated as a
                    raw value next to "Critical" with no anchor. */}
                {overviewScore != null && overviewGrade && overviewBreakdown ? (
                  <div
                    className="cv-overview-hero"
                    data-tone={overviewGrade.tone}
                  >
                    {/* The score-explanation tooltip lives on ONE explicit
                        trigger — the ⓘ beside the label — instead of the
                        whole card. The card used to be a giant invisible
                        hover target with its own data-tip, which collided
                        with the first-pass ⓘ inside it: moving the pointer
                        around this small area flipped between two different
                        tooltips. One concept, one anchor. */}
                    <div className="cv-overview-hero-eyebrow-row">
                      <p className="cv-overview-hero-eyebrow">Snap Site Score</p>
                      <button
                        type="button"
                        className="cv-score-info"
                        aria-label={
                          `How the Snap Site Score is calculated. ` +
                          `Score ${overviewScore} of 100, ${overviewGrade.label}. ` +
                          "Every page starts at 100 and its findings subtract points: high 12, medium 5, low 2. " +
                          "The site score is the average of the page scores." +
                          (overviewBreakdown.capped
                            ? " A high-severity finding caps the grade below Excellent."
                            : "") +
                          " By category: " +
                          scoreAreas
                            .map((a) => `${a.label} ${a.b.score} of 100`)
                            .join(", ") +
                          "."
                        }
                        data-tip={
                          // Reading order: the number, then what the number
                          // MEANS (grade bands, with this score's band
                          // highlighted), then HOW it's computed (per-page
                          // deductions → average), then the same findings
                          // grouped by category for orientation. Built from
                          // this audit's real numbers so it's grounded, not
                          // generic. Uses TooltipManager's structured
                          // syntax: `---` = rule, `## ` = section heading,
                          // `# ` = big value line, `A | B` = aligned
                          // two-column row, `> ` = highlighted row.
                          "## Snap Site Score\n" +
                          `# ${overviewScore} / 100 — ${overviewGrade.label}\n` +
                          "\n---\n\n" +
                          "## Grade bands\n" +
                          [
                            { label: "Excellent", range: "90–100" },
                            { label: "Good", range: "75–89" },
                            { label: "Needs work", range: "60–74" },
                            { label: "Poor", range: "40–59" },
                            { label: "Critical", range: "0–39" },
                          ]
                            .map(
                              (b) =>
                                `${b.label === overviewGrade.label ? "> " : ""}${b.label} | ${b.range}`,
                            )
                            .join("\n") +
                          "\n\n---\n\n" +
                          "## How it's scored\n" +
                          "Every page starts at 100. Each finding on it subtracts points:\n" +
                          "High severity | −12 pts\n" +
                          "Medium severity | −5 pts\n" +
                          "Low severity | −2 pts\n" +
                          `The site score is the average of the ${overviewBreakdown.pageScores.length === 1 ? "page score" : `${overviewBreakdown.pageScores.length} page scores`}.` +
                          (overviewBreakdown.capped
                            ? "\n> A high-severity finding caps the grade below Excellent."
                            : "") +
                          "\n\n---\n\n" +
                          "## By category\n" +
                          "The same findings, grouped — where the points went.\n" +
                          scoreAreas
                            .map((a) => `${a.label} | ${a.b.score}`)
                            .join("\n")
                        }
                      >
                        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
                          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
                          <circle cx="8" cy="4.7" r="0.95" fill="currentColor" />
                          <path d="M8 7.1v4.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                    <div className="cv-overview-hero-row">
                      <ScoreRing score={overviewScore ?? null} tone={overviewGrade.tone} size={64} />
                      <div className="cv-overview-hero-meta">
                        {/* Meta column: grade, count, then "First pass" as
                            the deliberate LAST line of the block — the same
                            position it has on the summary card, so the two
                            surfaces read as one design. (Inline-after-the-
                            count wrapped unpredictably in the narrow rail
                            and left the · separator dangling.) */}
                        <span className="cv-overview-hero-grade">{overviewGrade.label}</span>
                        <span className="cv-overview-hero-count">
                          {totalFindings2} finding{totalFindings2 !== 1 ? "s" : ""}
                        </span>
                        {/* Coverage — lives IN the meta column, right where
                            the score is read, not buried below the severity
                            bar: a "Good · 88" built from one page of two must
                            say so in the same glance. Amber so it reads as a
                            caveat, tooltip carries which page + why. */}
                        {captureHealth &&
                        !captureHealth.allFailed &&
                        captureHealth.excludedCount > 0 ? (
                          <button
                            type="button"
                            className="cv-overview-hero-coverage"
                            onClick={() => {
                              // Actionable, not just informational — jump
                              // the canvas to the first excluded page, whose
                              // shot carries the status badge (what happened
                              // + the reason). Same navigation as clicking
                              // the page in the rail.
                              const failed = layout.pages.find(
                                (p) =>
                                  p.page.captureQuality?.scorable === false &&
                                  p.page.captureQuality.status !== "notfound",
                              );
                              if (failed) flyToPage(failed.index);
                            }}
                            aria-label={
                              `Scored from ${captureHealth.totalCount - captureHealth.excludedCount} of ${captureHealth.totalCount} pages. ` +
                              `Not captured: ${captureHealth.excludedNames.join(", ")}. ` +
                              "Pages we couldn't capture aren't scored — the capture never saw them. Activate to view the page."
                            }
                            data-tip={
                              "## Score coverage\n" +
                              `# ${captureHealth.totalCount - captureHealth.excludedCount} of ${captureHealth.totalCount} pages scored\n` +
                              "\n---\n\n" +
                              "## Not captured\n" +
                              captureHealth.excludedNames
                                .map((n) => `> ${n}`)
                                .join("\n") +
                              "\n\n" +
                              "These pages never rendered for our capture (blocked or blank), so they carry no findings and don't move the score. Screenshot them in your browser and upload to audit them.\n" +
                              "\n---\n\n" +
                              "Click to jump to the page"
                            }
                          >
                            <AlertTriangleIcon />
                            Scored from{" "}
                            {captureHealth.totalCount - captureHealth.excludedCount}{" "}
                            of {captureHealth.totalCount} pages
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="cv-firstpass-link"
                          onClick={() => setFirstPassModalOpen(true)}
                          aria-haspopup="dialog"
                          aria-label="First pass — what this score covers, and what still needs manual review"
                        >
                          First pass
                          <svg viewBox="0 0 16 16" width="9" height="9" aria-hidden="true" focusable="false">
                            <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    {/* Subscores used to render here as an inline line
                        ("Usability 59 · Accessibility 42"), but that
                        put them right next to the finding count and
                        the source split (8 NN/g · 10 Accessibility),
                        which made the numbers easy to conflate (is
                        "42" a score or a count?). They live in the
                        "More details" disclosure now, paired with the
                        source split so the relationship between
                        subscore and finding count is visible side by
                        side when the user wants it. The ⓘ beside the
                        eyebrow carries the full formula for a quick
                        check. */}
                  </div>
                ) : null}

                {/* Severity bar + counts. The proportional bar gives
                    the visual "how bad" read; the count line below
                    it carries the exact numbers in matching colors,
                    so the bar and the data live as one unit instead
                    of being separated by a disclosure. */}
                <div className="cv-overview-severity">
                  <div
                    className="cv-overview-bar"
                    role="img"
                    aria-label={`Severity breakdown: ${highCount} high, ${medCount} medium, ${lowCount} low`}
                  >
                    {highCount > 0 ? (
                      <span
                        className="cv-overview-bar-segment"
                        data-severity="high"
                        style={{ flex: highCount }}
                      />
                    ) : null}
                    {medCount > 0 ? (
                      <span
                        className="cv-overview-bar-segment"
                        data-severity="medium"
                        style={{ flex: medCount }}
                      />
                    ) : null}
                    {lowCount > 0 ? (
                      <span
                        className="cv-overview-bar-segment"
                        data-severity="low"
                        style={{ flex: lowCount }}
                      />
                    ) : null}
                  </div>
                  {/* Severity breakdown = the canonical count summary AND a
                      filter entry: clicking a severity filters the findings
                      list (and syncs with the right-rail pills, which stay
                      count-free so these numbers aren't duplicated). */}
                  <p className="cv-overview-severity-counts">
                    {highCount > 0 ? (
                      <button
                        type="button"
                        className={`cv-overview-severity-count${severityFilter === "high" ? " is-active" : ""}`}
                        data-severity="high"
                        onClick={() => setSeverityFilter(severityFilter === "high" ? "all" : "high")}
                        aria-pressed={severityFilter === "high"}
                        aria-label={`${highCount} high-severity finding${highCount === 1 ? "" : "s"}${severityFilter === "high" ? " — filtering; click to clear" : " — click to filter"}`}
                      >
                        <strong>{highCount}</strong> high
                      </button>
                    ) : null}
                    {highCount > 0 && medCount > 0 ? (
                      <span className="cv-overview-severity-sep" aria-hidden="true">·</span>
                    ) : null}
                    {medCount > 0 ? (
                      <button
                        type="button"
                        className={`cv-overview-severity-count${severityFilter === "medium" ? " is-active" : ""}`}
                        data-severity="medium"
                        onClick={() => setSeverityFilter(severityFilter === "medium" ? "all" : "medium")}
                        aria-pressed={severityFilter === "medium"}
                        aria-label={`${medCount} medium-severity finding${medCount === 1 ? "" : "s"}${severityFilter === "medium" ? " — filtering; click to clear" : " — click to filter"}`}
                      >
                        <strong>{medCount}</strong> medium
                      </button>
                    ) : null}
                    {(highCount > 0 || medCount > 0) && lowCount > 0 ? (
                      <span className="cv-overview-severity-sep" aria-hidden="true">·</span>
                    ) : null}
                    {lowCount > 0 ? (
                      <button
                        type="button"
                        className={`cv-overview-severity-count${severityFilter === "low" ? " is-active" : ""}`}
                        data-severity="low"
                        onClick={() => setSeverityFilter(severityFilter === "low" ? "all" : "low")}
                        aria-pressed={severityFilter === "low"}
                        aria-label={`${lowCount} low-severity finding${lowCount === 1 ? "" : "s"}${severityFilter === "low" ? " — filtering; click to clear" : " — click to filter"}`}
                      >
                        <strong>{lowCount}</strong> low
                      </button>
                    ) : null}
                  </p>
                </div>

                {/* Progressive disclosure for the numerical
                    breakdowns + scope. Native <details> so we get
                    the toggle keyboard support + ARIA semantics
                    without writing the state. Collapsed by default
                    so the rail reads as "score + bar + pages"
                    on first arrival; power users who want the
                    severity / source / scope numbers click to
                    expand. */}
                <details className="cv-overview-details">
                  <summary className="cv-overview-details-summary">
                    <span>More details</span>
                    <ChevronDownIcon className="cv-overview-details-chevron" />
                  </summary>
                  {/* Severity moved up next to its bar — the
                      disclosure now only carries the axes that
                      aren't already visible at rest. */}
                  <dl className="cv-overview-details-body">
                    {/* Scope leads — it frames everything below (these
                        scores and findings are for this many pages /
                        viewports). It used to sit at the bottom, where
                        it read as an afterthought. */}
                    <dt>Scope</dt>
                    <dd>
                      <div className="cv-subscore-from">
                        {viewportLabel} · {totalPages} {totalPages === 1 ? "page" : "pages"}
                      </div>
                    </dd>
                    {/* Each subscore row is a two-line cell: the
                        "X / 100" number on top (prominent, tabular)
                        and "from N findings" caption underneath
                        (muted, smaller). The old single-line layout
                        broke awkwardly at narrow rail widths — the
                        "8 findings" portion wrapped onto its own
                        line with no styling cue that it was supposed
                        to read as a secondary detail. Stacking it
                        intentionally fixes both the wrap and the
                        hierarchy: users see the score first, then
                        the supporting count. */}
                    {overviewBreakdown ? (
                      <>
                        {/* Pin-letter chips next to each category
                            label double as the in-product legend for
                            U/A/C: a user who sees "U1" on a canvas
                            pin can find the matching letter here and
                            map it to "Usability" without having to
                            read source comments. */}
                        <dt>
                          <span className="cv-overview-details-key" data-kind="heuristic">U</span>
                          Usability
                        </dt>
                        <dd>
                          <div className="cv-subscore">
                            <span className="cv-subscore-num">
                              {overviewBreakdown.ux.score}
                            </span>
                            <span className="cv-subscore-of">/ 100</span>
                          </div>
                          <div className="cv-subscore-from">
                            from {overviewBreakdown.ux.findings}{" "}
                            {overviewBreakdown.ux.findings === 1 ? "finding" : "findings"}
                          </div>
                        </dd>
                        <dt>
                          <span className="cv-overview-details-key" data-kind="accessibility">A</span>
                          Accessibility
                        </dt>
                        <dd>
                          <div className="cv-subscore">
                            <span className="cv-subscore-num">
                              {overviewBreakdown.a11y.score}
                            </span>
                            <span className="cv-subscore-of">/ 100</span>
                          </div>
                          <div className="cv-subscore-from">
                            from {overviewBreakdown.a11y.findings}{" "}
                            {overviewBreakdown.a11y.findings === 1 ? "finding" : "findings"}
                          </div>
                        </dd>
                        <dt>
                          <span className="cv-overview-details-key" data-kind="bestpractice">B</span>
                          Best practices
                        </dt>
                        <dd>
                          <div className="cv-subscore">
                            <span className="cv-subscore-num">
                              {overviewBreakdown.bestPractices.score}
                            </span>
                            <span className="cv-subscore-of">/ 100</span>
                          </div>
                          <div className="cv-subscore-from">
                            from {overviewBreakdown.bestPractices.findings}{" "}
                            {overviewBreakdown.bestPractices.findings === 1 ? "finding" : "findings"}
                          </div>
                        </dd>
                        {/* First impression (presence) is the fourth
                            weighted dimension. It was previously shown
                            only in the canvas summary card, so the rail
                            read as three subscores while the summary
                            read as four. Surfacing it here keeps both
                            views consistent. Rendered conditionally
                            because presence data is optional (older
                            audits / single-page runs may lack it). */}
                        {overviewBreakdown.presence ? (
                          <>
                            <dt>
                              <span className="cv-overview-details-key" data-kind="presence">F</span>
                              First impression
                            </dt>
                            <dd>
                              <div className="cv-subscore">
                                <span className="cv-subscore-num">
                                  {overviewBreakdown.presence.score}
                                </span>
                                <span className="cv-subscore-of">/ 100</span>
                              </div>
                              <div className="cv-subscore-from">
                                from {overviewBreakdown.presence.findings}{" "}
                                {overviewBreakdown.presence.findings === 1 ? "finding" : "findings"}
                              </div>
                            </dd>
                          </>
                        ) : null}
                      </>
                    ) : null}
                    {/* Custom findings, when any, have no subscore —
                        they're user-added and don't affect the
                        headline number. Surface the count for
                        completeness so the totals add up. The "C"
                        chip ties this row to the C# pin labels
                        users see on their own annotations. */}
                    {customCount > 0 ? (
                      <>
                        <dt>
                          <span className="cv-overview-details-key" data-kind="custom">C</span>
                          Your findings
                        </dt>
                        <dd>
                          <div className="cv-subscore-from">
                            {customCount} {customCount === 1 ? "finding" : "findings"} &mdash;
                            not counted in the score
                          </div>
                        </dd>
                      </>
                    ) : null}
                  </dl>
                </details>
              </>
            )}
          </section>

          <section aria-labelledby="captured-pages-heading">
            <div className="cv-sidebar-header cv-sidebar-header-secondary">
              {/* "Captured pages" → "Pages". The (N) count is gone:
                  the rendered items already are the count, and the
                  shorter title gives the rail more breathing room
                  before the list begins. */}
              <h2 className="cv-sidebar-title" id="captured-pages-heading">
                Pages
              </h2>
            </div>
            <ul className="cv-page-list">
            <li>
              <button
                type="button"
                className={`cv-page-item${activePageIndex === -1 ? " active" : ""}`}
                onClick={() => {
                  if (!stageSize.width) return;
                  const scale = Math.max(0.5, Math.min(0.85, transform.scale));
                  setTransform({
                    x: stageSize.width / 2 - (layout.summaryX + 270) * scale,
                    y: stageSize.height / 4,
                    scale,
                  });
                  setActivePageIndex(-1);
                }}
              >
                <div className="cv-page-thumb cv-page-thumb-summary">
                  <SummaryIcon />
                </div>
                <div className="cv-page-info">
                  {/* "Score & overview" subtitle dropped — the
                      audit-overview block now lives at the top of
                      the rail, so the Summary item doesn't need to
                      re-describe itself. */}
                  <span className="cv-page-name">Summary</span>
                </div>
              </button>
            </li>
            {layout.pages.map((p) => {
              // Raw finding count for this page (all three audit categories,
              // including unpinned ones) so the badge matches the right rail
              // and dashboard rather than only counting visible canvas dots.
              const findingCount =
                (p.page.heuristicFindings?.length ?? 0) +
                (p.page.accessibilityFindings?.length ?? 0) +
                (p.page.bestPracticeFindings?.length ?? 0);
              const thumbShot = p.page.shots[0];
              return (
                <li key={p.index}>
                  <button
                    type="button"
                    className={`cv-page-item${activePageIndex === p.index ? " active" : ""}`}
                    onClick={() => flyToPage(p.index)}
                  >
                    <div className="cv-page-thumb">
                      {thumbShot?.screenshotPath ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={thumbShot.screenshotPath} alt="" draggable={false} />
                      ) : (
                        <LayoutIcon />
                      )}
                    </div>
                    <div className="cv-page-info">
                      <span className="cv-page-name">
                        {p.page.name || hostnameOf(p.page.url)}
                      </span>
                      <span className="cv-page-url-small">
                        {formatPageUrlShort(p.page.url)}
                      </span>
                    </div>
                    {p.page.captureQuality?.scorable === false ? (
                      /* Capture failed for this page (blank / blocked /
                         not-found). Show a warn badge — never the green
                         "clean" check, which would falsely imply the page
                         was analyzed and came back fine. */
                      <span
                        className="cv-page-count cv-page-count-failed"
                        data-tip={p.page.captureQuality.reason || "We couldn't capture this page"}
                        aria-label="Could not capture this page"
                      >
                        <AlertTriangleIcon />
                      </span>
                    ) : findingCount > 0 ? (
                      <span className="cv-page-count" aria-label={`${findingCount} finding${findingCount !== 1 ? "s" : ""} on this page`} data-tip={`${findingCount} finding${findingCount !== 1 ? "s" : ""} on this page`}>
                        {findingCount}
                      </span>
                    ) : result.runMode === "snapshot" ? (
                      /* Snapshot mode never produces findings — the
                         green check would imply "this page is clean,"
                         which is misleading because the page was
                         never analyzed. Show a neutral dash so the
                         column still has a badge slot but doesn't
                         falsely celebrate. */
                      <span
                        className="cv-page-count cv-page-count-na"
                        data-tip="Snapshot only — no audit was run on this page"
                        aria-label="Snapshot only — no audit was run on this page"
                      >
                        —
                      </span>
                    ) : (
                      /* Clean-state badge is now a check icon, not
                         text. The number-badge / text-badge mix
                         broke the visual rhythm of the column —
                         every other row had a small pill with a
                         number, this one had a word. The check
                         icon gives a positive visual cue in the
                         same shape slot. */
                      <span
                        className="cv-page-count cv-page-count-clean"
                        data-tip="No findings on this page"
                        aria-label="No findings on this page"
                      >
                        <CheckIcon />
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
            </ul>
          </section>

          {/* Bottom block — "+ Add page" affordance and audit-meta
              footer. Owner-only. Opens the add-page modal which captures
              another same-domain page and merges it into result.pages
              via POST /api/jobs/:id/append-page. Hidden entirely for
              image-mode audits since the backend rejects URL-based
              additions to them. */}
          <div className="cv-sidebar-left-bottom" aria-label="Audit actions and details">
            {isOwner && result.runMode !== "imageAudit" ? (
              canAddPage ? (
                <button
                  type="button"
                  className="cv-add-page-btn"
                  aria-label="Add another page from this website" data-tip="Add another page from this website"
                  onClick={() => {
                    setAddPageError(null);
                    setAddPageOpen(true);
                  }}
                >
                  <PlusIcon /> Add page
                </button>
              ) : (
                // Locked add-page for free users — mirrors the Share/Export
                // gates: a visible toast explaining the gate plus the
                // rate-limited openUpgradeInNewTab so repeat clicks don't
                // spawn multiple tabs. Keeps the cv-add-page-btn shell so
                // the sidebar layout doesn't shift between plans.
                <button
                  type="button"
                  className="cv-add-page-btn"
                  aria-label="Add another page to this audit — available on the Pro plan"
                  data-tip="Add another page to this audit — available on the Pro plan"
                  onClick={() => {
                    showToast(
                      "Adding pages to an audit is on the Pro plan. We opened your plan options in a new tab.",
                      "warn",
                    );
                    openUpgradeInNewTab();
                  }}
                >
                  <PlusIcon /> Add page <span className="cv-pro-pill">Pro</span>
                </button>
              )
            ) : null}

            <dl className="cv-audit-meta" aria-label="Audit details">
              <div className="cv-audit-meta-row">
                <dt>Created</dt>
                <dd>
                  {result.createdAt
                    ? new Date(result.createdAt).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })
                    : "—"}
                </dd>
              </div>
              {isOwner && creatorEmail ? (
                <div className="cv-audit-meta-row">
                  <dt>Owner</dt>
                  <dd aria-label={creatorEmail} data-tip={creatorEmail}>{creatorEmail}</dd>
                </div>
              ) : null}
            </dl>

            {/* Perpetual feedback entry point — tucked under the audit
                meta so it's always available without competing with the
                primary actions. A tertiary action button on a subtle
                grey slot, label underlined so it reads as clickable.
                Opens the same dialog as the one-time automatic prompt.
                Owner-only: the feedback endpoint needs an authenticated
                user, and public viewers aren't ours to survey. */}
            {isOwner ? (
              <div className="cv-feedback-slot">
                <button
                  type="button"
                  className="cv-action-btn cv-action-btn-tertiary cv-feedback-link"
                  onClick={openFeedback}
                >
                  <FeedbackIcon />
                  <span className="cv-feedback-link-label">Give feedback</span>
                </button>
              </div>
            ) : null}
          </div>
        </aside>

        {/* ── Center: Canvas ── */}
        <div
          className="cv-stage"
          ref={stageRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          data-grabbing={grabbing}
        >
          <div
            ref={stageInnerRef}
            className="cv-stage-inner"
            style={{
              // 2D transform (not translate3d) so this giant element isn't
              // permanently promoted to its own compositor layer. At full
              // size the canvas is enormous (multi-page screenshots), and an
              // always-on oversized layer makes Chrome re-rasterize the whole
              // thing on any inner repaint — e.g. a callout's hover shadow —
              // which shows up as constant flicker. The layer is promoted only
              // while panning (see .cv-stage[data-grabbing] .cv-stage-inner).
              transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
              width: totalWidth,
              height: totalHeight,
            }}
          >
            <CanvasSummaryBlock
              result={result}
              layoutSummaryX={layout.summaryX}
              fixFirst={fixFirst}
              pageNameByIndex={pageNameByIndex}
              showSubscores={leftRailCollapsed || exporting}
            />
            {layout.pages.map((p) => (
              <CanvasPageBlock
                key={p.index}
                layout={p}
                isSnapshot={result.runMode === "snapshot"}
                activeFindingId={activeFindingId}
                onFindingClick={(pin, shotIdx) => {
                  flyToFinding(pin, p.index, shotIdx);
                  // Locked teaser pin/callout: centre it, then open sign-in.
                  if (pin.locked) handleUnlock();
                }}
                pinTool={pinTool}
                sourceFilter={sourceFilter}
                cardOffsets={cardOffsets}
                onCardMove={moveCard}
                stageScale={transform.scale}
                solvedIds={solved}
                pinOverrides={pinOverrides}
                onPinMove={movePin}
                canMovePins={isOwner}
              />
            ))}
          </div>
          {/* Help line — dismissible. Was hostile to dismiss: the
              old tooltip told the user the only way to bring it back
              was to clear site data, which is a comically bad
              instruction for a paying customer. Now × hides it, and
              a small "?" button takes the same canvas slot so the
              user can summon the shortcuts back any time. Hidden
              entirely in viewer mode — public visitors are reading,
              not zooming/annotating. */}
          {!isViewer ? (
            !helpHidden ? (
              <div className="cv-help" data-canvas-interactive>
                <span><b>Pinch</b> or <b>Ctrl/⌘ + scroll</b> to zoom · <b>scroll</b> or <b>drag</b> to pan · <b>0</b> fit · <b>1</b> 100%</span>
                <button
                  type="button"
                  className="cv-help-dismiss"
                  onClick={dismissHelp}
                  aria-label="Hide shortcuts hint"
                  data-tip="Hide this hint — click the ? button to bring it back"
                  data-canvas-interactive
                >
                  ×
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="cv-help-reopen"
                onClick={showHelp}
                aria-label="Show keyboard and gesture shortcuts"
                data-tip="Show keyboard and gesture shortcuts"
                data-canvas-interactive
              >
                ?
              </button>
            )
          ) : null}

          {/* Pin-pick hint banner. Floats near the top-center of
              the canvas while the user is in pin-picking mode — a
              sub-step of the Add finding modal, not a standalone
              tool. The "Cancel" button drops back into the modal
              without losing the form values, since formSeed is
              still set in the parent. data-canvas-interactive
              keeps the cancel click from being swallowed by the
              pan handler. */}
          {pinTool && isOwner ? (
            <div className="cv-pin-tool-hint" role="status" data-canvas-interactive>
              <span className="cv-pin-tool-hint-text">
                Click a spot on any screenshot — your finding will be pinned there.
              </span>
              <button
                type="button"
                className="cv-pin-tool-hint-cancel"
                onClick={() => setPinTool(false)}
                aria-label="Cancel picking a spot — return to the form"
                data-canvas-interactive
              >
                Back
                <span className="cv-pin-tool-hint-shortcut">Esc</span>
              </button>
            </div>
          ) : null}
        </div>

        {/* ── Right: Findings Panel ── */}
        <aside
          className={`cv-sidebar-right${rightRailCollapsed ? " is-collapsed" : ""}`}
          aria-labelledby="findings-heading"
        >
          <button
            type="button"
            className="cv-rail-toggle cv-rail-toggle-right"
            onClick={toggleRightRail}
            aria-label={rightRailCollapsed ? "Show findings panel" : "Hide findings panel"}
            aria-expanded={!rightRailCollapsed}
          >
            <PanelToggleIcon side="right" collapsed={rightRailCollapsed} />
          </button>
          {/* Mobile-only close button (visible at ≤900 px via CSS),
              mirror of the one on the left rail — the chevron above
              is desktop chrome, mobile users need a clear X. */}
          <button
            type="button"
            className="cv-mobile-sheet-close"
            onClick={closeMobileSheet}
            aria-label="Close findings panel"
          >
            <CloseIcon />
          </button>
          <div className="cv-findings-header">
            {/* Top row — title on the left, Saved filter on the
                right. Wrapped in its own flex row so the source line
                below can sit cleanly as a full-width sub-line
                without competing with the Saved button for
                horizontal space. */}
            <div className="cv-findings-header-top">
              <div className="cv-findings-title-row">
                <h2 className="cv-findings-title" id="findings-heading">
                  Findings
                  <span className="cv-findings-total">({totalFindings2})</span>
                </h2>
              </div>
              <div className="cv-findings-header-actions">
                {/* Add finding moved down to the sidebar footer
                    alongside Copy all findings — both are bulk
                    actions that act on the whole list, so they
                    belong in the same cluster. Keeps the header
                    cleaner (title + Saved filter only). */}
                <button
                  type="button"
                  className={`cv-bookmark-filter${bookmarkedOnly ? " active" : ""}`}
                  onClick={() => setBookmarkedOnly((v) => !v)}
                  aria-pressed={bookmarkedOnly}
                  aria-label={bookmarkedOnly ? "Show all findings" : "Show only saved findings"}
                  data-tip={
                    bookmarkedOnly
                      ? "Filtering to saved findings only — click to show all"
                      : totalBookmarked === 0
                        ? "Bookmark any finding (icon on the right of each row) to save it here for later"
                        : "Filter the list to show only findings you saved"
                  }
                >
                  <BookmarkIcon />
                  <span className="cv-bookmark-filter-label">Saved</span>
                  <span className="cv-bookmark-filter-count">({totalBookmarked})</span>
                </button>
              </div>
            </div>
            {/* Source breakdown — sub-line beneath the title row.
                Dropped the "BY SOURCE" eyebrow that used to lead
                this line: the rail header right above it already
                says "Findings (41)", so the relationship between
                the count and the sub-breakdown is established by
                proximity. The eyebrow was a third uppercase tag
                on the same surface (after SNAP SITE SCORE in the
                left rail) and was competing for attention. */}
            {totalFindings2 > 0 ? (
              (() => {
                // Build category chips in display order, dropping any with a
                // zero count, then interleave "·" separators. Keeps the line
                // tidy across two, three, or four present categories.
                const chips = [
                  { count: heuristicCount, label: "Usability" },
                  { count: accessibilityCount, label: "Accessibility" },
                  { count: bestPracticeCount, label: "Best practices" },
                  { count: customCount, label: "Custom" },
                ].filter((c) => c.count > 0);
                return (
                  <p
                    className="cv-findings-source-line"
                    aria-label={`Findings by category: ${chips
                      .map((c) => `${c.count} ${c.label}`)
                      .join(", ")}`}
                  >
                    {chips.map((c, i) => (
                      <span key={c.label} style={{ display: "contents" }}>
                        {i > 0 ? (
                          <span className="cv-overview-breakdown-sep" aria-hidden="true">·</span>
                        ) : null}
                        <span className="cv-overview-breakdown-item">
                          {c.count} {c.label}
                        </span>
                      </span>
                    ))}
                  </p>
                );
              })()
            ) : null}
          </div>

          {/* Human-in-the-loop framing — echoes the AI guidance section
              on the marketing home page. Tells the user the findings
              below are AI-suggested drafts to triage, not gospel.
              Dismissible: once a user knows the workflow, this is
              ambient noise. Hidden state persisted to localStorage. */}
          {totalFindings2 > 0 && !aiNoteHidden ? (
            <p className="cv-findings-ai-note" role="note">
              <SparkleIcon />
              <span>
                AI-suggested findings. Mark each as reviewed once you&rsquo;ve
                verified it.
              </span>
              <button
                type="button"
                className="cv-findings-ai-note-dismiss"
                onClick={dismissAiNote}
                aria-label="Hide this hint"
                data-tip="Hide this hint"
              >
                ×
              </button>
            </p>
          ) : null}

          {/* Triage progress — turns the rail into a workspace. Only
              renders when there's something to triage; the bar fills
              left-to-right as findings are marked solved. When the
              user has triaged everything, swap to a completion state
              so the click-through-N-findings effort is acknowledged
              rather than ending with an unchanging full bar. */}
          {totalFindings2 > 0 ? (
            solved.size === totalFindings2 ? (
              <div
                className="cv-triage-progress cv-triage-progress-done"
                role="status"
                aria-label="All findings triaged"
              >
                <span className="cv-triage-progress-done-icon" aria-hidden="true">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <span className="cv-triage-progress-done-text">
                  <strong>All {totalFindings2} findings triaged</strong>
                  <span>Nice work — solved findings sit below.</span>
                </span>
              </div>
            ) : (
              <div
                className="cv-triage-progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={totalFindings2}
                aria-valuenow={solved.size}
                aria-label="Triage progress"
              >
                <div className="cv-triage-progress-row">
                  <span className="cv-triage-progress-text">
                    {solved.size} of {totalFindings2} solved
                  </span>
                  <span className="cv-triage-progress-pct">
                    {Math.round((solved.size / totalFindings2) * 100)}%
                  </span>
                </div>
                <div className="cv-triage-progress-track" aria-hidden="true">
                  <div
                    className="cv-triage-progress-fill"
                    style={{
                      width: `${Math.round((solved.size / totalFindings2) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            )
          ) : null}

          {/* Single filter strip — severity (primary, what drives
              triage) and source (only shown when there's a custom
              annotation to filter to) live on one row with a
              vertical divider between them. Previously these were
              two separate rows, which added visual weight without
              improving discoverability — and the source breakdown
              caption above already told users what split exists. */}
          <div className="cv-filter-chips">
            {/* "All" reads as active only when no filter of any kind is
                applied. If the user has a source filter on (e.g. only
                Custom), All shouldn't look selected — that was the
                confusing case where All + Custom looked like two
                radio selections. Clicking All clears both severity
                AND source filters back to a true "no filter" state. */}
            {/* Counts live once, in the left-rail severity summary; these
                pills stay lean toggles (no duplicated numbers). */}
            <button
              type="button"
              className={`cv-chip${severityFilter === "all" && sourceFilter === "all" ? " active" : ""}`}
              onClick={() => {
                setSeverityFilter("all");
                setSourceFilter("all");
              }}
            >
              All
            </button>
            <button
              type="button"
              className={`cv-chip cv-chip-high${severityFilter === "high" ? " active" : ""}`}
              onClick={() => setSeverityFilter(severityFilter === "high" ? "all" : "high")}
            >
              <span className="cv-chip-dot" data-severity="high" aria-hidden="true" />
              High
            </button>
            <button
              type="button"
              className={`cv-chip cv-chip-medium${severityFilter === "medium" ? " active" : ""}`}
              onClick={() => setSeverityFilter(severityFilter === "medium" ? "all" : "medium")}
            >
              <span className="cv-chip-dot" data-severity="medium" aria-hidden="true" />
              Medium
            </button>
            <button
              type="button"
              className={`cv-chip cv-chip-low${severityFilter === "low" ? " active" : ""}`}
              onClick={() => setSeverityFilter(severityFilter === "low" ? "all" : "low")}
            >
              <span className="cv-chip-dot" data-severity="low" aria-hidden="true" />
              Low
            </button>
          </div>

          {/* Type filter — secondary axis, so it's a compact dropdown
              rather than a second pill row (keeps severity as the primary
              quick-filter and lowers visual load). Counts stay visible in
              the options. Only renders when there's more than one type to
              filter between, so single-category audits aren't cluttered. */}
          {(() => {
            const typeOpts = (
              [
                { key: "heuristic", label: "Usability", count: heuristicCount },
                { key: "accessibility", label: "Accessibility", count: accessibilityCount },
                { key: "bestpractice", label: "Best practices", count: bestPracticeCount },
                { key: "custom", label: "Custom", count: customCount },
              ] as Array<{ key: SourceFilter; label: string; count: number }>
            ).filter((c) => c.count > 0);
            if (typeOpts.length < 2) return null;
            return (
              <div className="cv-filter-type">
                <label className="cv-filter-type-label" htmlFor="cv-type-filter">
                  Type
                </label>
                <select
                  id="cv-type-filter"
                  className="cv-filter-type-select"
                  value={sourceFilter === "all" || typeOpts.some((c) => c.key === sourceFilter) ? sourceFilter : "all"}
                  onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
                >
                  <option value="all">All types ({totalFindings2})</option>
                  {typeOpts.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label} ({c.count})
                    </option>
                  ))}
                </select>
              </div>
            );
          })()}

          {/* Findings list */}
          <ul className="cv-findings-list">
            {showFreeFindings ? (
              <li className="cv-free-findings">
                <h3>Start here: your free findings</h3>
                <p>Read these {freeFindings.length} findings without signing up.</p>
                {freeFindings.map((entry, index) => (
                  <details key={entry.id} className="cv-free-finding" open={index === 0}>
                    <summary>
                      <span className="cv-free-finding-meta">{entry.annotation.severity} · {pageNameByIndex[entry.pageIndex]}</span>
                      <strong>{entry.annotation.title}</strong>
                    </summary>
                    <p>{entry.annotation.observation}</p>
                    {entry.annotation.suggestedFix ? (
                      <div className="cv-finding-section"><h4>Suggested fix</h4><p>{entry.annotation.suggestedFix}</p></div>
                    ) : null}
                    <button type="button" className="cv-findings-empty-reset" onClick={() => {
                      track("Guest Finding Viewed", { source: "free-findings" });
                      if (entry.placed) flyToFinding(entry.annotation, entry.pageIndex, entry.shotIndex);
                      else flyToPage(entry.pageIndex);
                      setMobileSheet(null);
                    }}>See it on your page</button>
                  </details>
                ))}
                {dedupedFindings.length > freeFindings.length ? (
                  <p className="cv-free-findings-more">Create a free account to read the remaining {dedupedFindings.length - freeFindings.length} findings.</p>
                ) : null}
              </li>
            ) : null}
            {filteredFindings.length === 0 ? (
              <li className="cv-findings-empty">
                {/* A run whose pages never captured has no findings BY
                    DESIGN — "No findings here yet… add your own" invited
                    users to annotate a blank wall and made the empty list
                    read like the tool came up short. Say what actually
                    happened and point at the path that works. */}
                <span className="cv-findings-empty-title">
                  {severityFilter === "all" && sourceFilter === "all" && !bookmarkedOnly
                    ? captureHealth?.allFailed
                      ? "Nothing to review yet"
                      : "No findings here yet"
                    : "No matching findings"}
                </span>
                <span className="cv-findings-empty-desc">
                  {bookmarkedOnly
                    ? "You haven't saved any findings yet. Tap the bookmark icon on any finding to keep it handy here."
                    : severityFilter !== "all"
                    ? `No ${severityFilter}-severity findings. Try changing your filter.`
                    : sourceFilter === "custom"
                    ? "Use the Annotate tool to add your own findings."
                    : captureHealth?.allFailed
                    ? "This page couldn't be captured, so there's nothing to review yet — the summary shows how to audit it."
                    : "No findings on this page. Use the Annotate tool to add your own."}
                </span>
                {/* Clear-filters CTA — only when at least one
                    filter is actually narrowing the view. Tapping
                    it resets all three filter axes at once so the
                    user doesn't have to chase each chip + the
                    Saved button separately. */}
                {(severityFilter !== "all" || sourceFilter !== "all" || bookmarkedOnly) ? (
                  <button
                    type="button"
                    className="cv-findings-empty-reset"
                    onClick={() => {
                      setSeverityFilter("all");
                      setSourceFilter("all");
                      setBookmarkedOnly(false);
                    }}
                  >
                    Clear filters
                  </button>
                ) : null}
              </li>
            ) : (
              railFindings.map((p, idx) => {
                const id = p.id;
                const isActive = activeFindingId === id;
                // Locked teaser rows (guest mode) never expand — the body text
                // was stripped server-side. Clicking opens sign-in instead.
                const locked = !!p.annotation.locked;
                const isExpanded = !locked && expandedFindingId === id;
                const isBookmarked = bookmarked.has(id);
                const isSolved = solved.has(id);
                const cardClass =
                  `cv-finding-card${isActive ? " active" : ""}` +
                  `${locked ? " is-locked" : ""}` +
                  `${isSolved ? " is-solved" : ""}` +
                  `${isBookmarked ? " is-bookmarked" : ""}`;

                // Separator between open and resolved findings.
                const showSeparator = idx === firstSolvedIdx && firstSolvedIdx > 0;
                const solvedInFiltered = filteredFindings.length - firstSolvedIdx;

                // Page section header — appears at the first finding
                // of each page, in every view mode (Summary or
                // single-page). The header is the dependable page-
                // context cue now — the inline pill on each row
                // was removed because it duplicated this header
                // visually. Sticky positioning keeps the current
                // section's header pinned to the top of the rail
                // while the user scrolls through its findings.
                const prevPageIndex = idx > 0 ? railFindings[idx - 1].pageIndex : null;
                const prevSourceKind = idx > 0 ? railFindings[idx - 1].annotation.kind : null;
                const showPageHeader =
                  Boolean(pageNameByIndex[p.pageIndex]) &&
                  prevPageIndex !== p.pageIndex;
                // Source sub-header — renders inside each page
                // section at every source-group boundary. Sorting
                // ensures same-source findings sit together, so
                // we just compare with the previous finding's
                // source. Resets when the page changes (the page
                // header above carries the page break).
                const showSourceHeader =
                  showPageHeader || prevSourceKind !== p.annotation.kind;
                const sourceLabel =
                  p.annotation.kind === "heuristic" ? "Usability"
                    : p.annotation.kind === "accessibility" ? "Accessibility"
                    : p.annotation.kind === "bestpractice" ? "Best practices"
                    : "Custom";
                const pageBreakdown = pageBreakdownByIndex.get(p.pageIndex);

                return (
                  <li key={id}>
                    {showPageHeader && pageBreakdown ? (
                      <div className="cv-rail-page-header">
                        <span className="cv-rail-page-header-name">
                          {pageNameByIndex[p.pageIndex]}
                        </span>
                        {/* Just the count — the severity counts
                            that used to follow (" · 1 high, 2
                            medium") repeated info that the row
                            badges below already carry, so the
                            header was eyebrow-heavy. Count alone
                            is the orienting signal. */}
                        <span className="cv-rail-page-header-meta">
                          {pageBreakdown.total} finding{pageBreakdown.total !== 1 ? "s" : ""}
                        </span>
                      </div>
                    ) : null}
                    {showSourceHeader ? (
                      <div className="cv-rail-source-header" data-source={p.annotation.kind}>
                        {/* Match the U/A/C chip the user sees on
                            canvas pins so the rail's section
                            headers and the dot labels read as the
                            same vocabulary. */}
                        <span
                          className="cv-overview-details-key"
                          data-kind={p.annotation.kind}
                          aria-hidden="true"
                        >
                          {kindPrefix(p.annotation.kind)}
                        </span>
                        {sourceLabel}
                      </div>
                    ) : null}
                    {showSeparator ? (
                      <div className="cv-resolved-separator">
                        {/* Just the label — the flanking hairlines
                            used to compete visually with the
                            sticky page section headers above. The
                            label alone reads as the divider. */}
                        <span className="cv-resolved-separator-label">
                          Solved ({solvedInFiltered})
                        </span>
                      </div>
                    ) : null}
                    <div
                      className={cardClass}
                      data-severity={p.annotation.severity}
                    >
                    {/* Row used to be a single <button>, but we now
                        nest real <button>s inside it (inline bookmark
                        and solved toggles next to the chevron). Nested
                        buttons aren't valid HTML, so the outer is a
                        <div role="button"> with keyboard handlers. */}
                    <div
                      role="button"
                      tabIndex={0}
                      className="cv-finding-row"
                      data-canvas-interactive
                      aria-expanded={isExpanded}
                      onClick={() => {
                        if (locked) {
                          // Fly to the pin first (proves the finding is real and
                          // placed) then open sign-in to unlock the detail.
                          if (p.placed) {
                            flyToFinding(p.annotation, p.pageIndex, p.shotIndex);
                          }
                          handleUnlock();
                        } else if (isExpanded) {
                          setExpandedFindingId(null);
                        } else if (p.placed) {
                          flyToFinding(p.annotation, p.pageIndex, p.shotIndex);
                        } else {
                          flyToPage(p.pageIndex);
                          setActiveFindingId(id);
                          setExpandedFindingId(id);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          (event.currentTarget as HTMLDivElement).click();
                        }
                      }}
                    >
                      {/* Leading badge — for pinned findings it's the dot's
                          number in its severity colour, so the list row and
                          the canvas dot read as the same item. Page-level
                          findings (no single spot on the screenshot) show a
                          muted page glyph instead, with the row aligned. */}
                      {p.placed && p.annotation.number > 0 ? (
                        <span
                          className="cv-finding-num"
                          data-severity={isSolved ? "solved" : p.annotation.severity}
                          data-kind={p.annotation.kind}
                          aria-hidden="true"
                        >
                          {p.annotation.label}
                        </span>
                      ) : (
                        /* Page-level finding — no single bbox to
                           pin on the canvas. Dashed-outline badge
                           tinted by severity (matches the rest of
                           the row's colour cue), with a document
                           glyph inside that visually communicates
                           "applies to the whole page". The plain
                           "—" we had before didn't read as anything
                           meaningful. Combined with the inline
                           PAGE-LEVEL tag, the row gives the user
                           three independent cues that this finding
                           has no specific pin location. */
                        <span
                          className="cv-finding-num cv-finding-num-unplaced"
                          data-severity={isSolved ? "solved" : p.annotation.severity}
                          aria-label="No single spot to pin — this issue is general (page-wide or affects multiple elements)" data-tip="No single spot to pin — this issue is general (page-wide or affects multiple elements)"
                          aria-hidden="true"
                        >
                          <PageLevelIcon />
                        </span>
                      )}
                      <div className="cv-finding-info">
                        <div className="cv-finding-meta">
                          {/* Source label dropped from the inline
                              tag — the source sub-header above each
                              group now carries that context, so
                              repeating "NN/G" on every row was
                              eyebrow noise. */}
                          <span
                            className="cv-finding-tag"
                            data-severity={p.annotation.severity}
                          >
                            <span className="cv-finding-tag-sev">
                              {SEVERITY_LABEL[p.annotation.severity]}
                            </span>
                          </span>
                          {/* "General" tag — covers findings that
                              don't have a pin location, whether
                              that's because the issue is page-wide
                              (e.g. structural) or affects multiple
                              elements (e.g. all flag images). Soft
                              catch-all word that avoids overclaiming
                              ("whole page" implied all-or-nothing). */}
                          {!p.placed ? (
                            <span
                              className="cv-finding-page-level-tag"
                              aria-label="No single spot to pin — this issue is general (page-wide or affects multiple elements)" data-tip="No single spot to pin — this issue is general (page-wide or affects multiple elements)"
                            >
                              General
                            </span>
                          ) : null}
                          {/* Recurring-issue chip — the same signature was
                              found on other pages too. Reframes template
                              issues as leverage ("one fix improves N
                              pages") instead of N separate problems. */}
                          {(() => {
                            if (p.annotation.kind === "custom") return null;
                            const spread = findingPageSpread.get(
                              findingEntrySig(p),
                            );
                            if (!spread || spread.size < 2) return null;
                            return (
                              <span
                                className="cv-finding-spread-tag"
                                aria-label={`This issue appears on ${spread.size} pages — one fix improves all of them`}
                                data-tip={`Found on ${spread.size} pages — one fix improves all of them`}
                              >
                                on {spread.size} pages
                              </span>
                            );
                          })()}
                          {isBookmarked ? (
                            <span className="cv-finding-bookmark-mark" aria-label="Saved" data-tip="Saved">
                              <BookmarkIcon />
                            </span>
                          ) : null}
                          {isSolved ? (
                            <span className="cv-finding-status is-solved">
                              <CheckIcon /> Solved
                            </span>
                          ) : null}
                        </div>
                        {locked ? (
                          /* Locked teaser — only the first two words of the
                             headline survive server-side; the rest is a redact
                             bar. The severity badge above and the numbered pin
                             still prove a real finding sits at this spot. */
                          <div className="cv-finding-locked">
                            <p className="cv-finding-name cv-title-locked">
                              {p.annotation.title ? (
                                <span className="cv-title-words">
                                  {p.annotation.title}
                                </span>
                              ) : null}
                              <span className="cv-title-redact" />
                            </p>
                            <span className="cv-finding-locked-cta">
                              <LockIcon /> Sign in to unlock
                            </span>
                          </div>
                        ) : (
                          <p className="cv-finding-name">
                            {/* Short headline — stays put whether collapsed
                                or expanded. The full description renders as a
                                separate body paragraph below when expanded, so
                                the user keeps the at-a-glance title AND gets
                                the detail. */}
                            {p.annotation.title}
                          </p>
                        )}
                      </div>
                      {/* Inline triage toggles — sit between the
                          title and the expand chevron. Owner-only.
                          Click stops propagation so the row's expand
                          handler doesn't fire alongside the toggle.
                          Lets users mark-as-solved or save without
                          having to expand each card to reach the
                          action bar inside. */}
                      {isOwner ? (
                        <div className="cv-finding-row-toggles" data-canvas-interactive>
                          <button
                            type="button"
                            className={`cv-finding-row-toggle${isBookmarked ? " is-active" : ""}`}
                            data-canvas-interactive
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleBookmark(id);
                            }}
                            aria-pressed={isBookmarked}
                            aria-label={isBookmarked ? "Unsave" : "Save"}
                            data-tip={isBookmarked ? "Saved — click to unsave" : "Save"}
                          >
                            <BookmarkIcon />
                          </button>
                          <button
                            type="button"
                            className={`cv-finding-row-toggle${isSolved ? " is-active is-solved" : ""}`}
                            data-canvas-interactive
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleSolved(id);
                            }}
                            aria-pressed={isSolved}
                            aria-label={isSolved ? "Mark as not solved" : "Mark as solved"}
                            data-tip={isSolved ? "Solved — click to reopen" : "Mark as solved"}
                          >
                            <CheckIcon />
                          </button>
                        </div>
                      ) : null}
                      {locked ? (
                        <LockIcon className="cv-finding-chevron" />
                      ) : (
                        <ChevronDownIcon className={`cv-finding-chevron${isExpanded ? " open" : ""}`} />
                      )}
                    </div>

                    {isExpanded && (
                      <div className="cv-finding-detail">
                        {/* Full description — the complete observation +
                            suggested fix the AI wrote. The headline above
                            stays visible, so the user gets both. */}
                        <p className="cv-finding-detail-desc">
                          {p.annotation.observation}
                        </p>

                        {/* Reference — one clean line: the underlying NN/g
                            heuristic or WCAG criterion, or "Added by you"
                            for custom annotations. No heavy box. */}
                        {heuristicLineFor(p.annotation) ? (
                          <div className="cv-finding-ref">
                            <span className="cv-finding-ref-label">Reference</span>
                            <span className="cv-finding-ref-value">
                              {heuristicLineFor(p.annotation)}
                            </span>
                          </div>
                        ) : p.annotation.kind === "custom" ? (
                          <div className="cv-finding-ref">
                            <span className="cv-finding-ref-label">Source</span>
                            <span className="cv-finding-ref-value">Added by you</span>
                          </div>
                        ) : null}

                        {/* Why it matters — only when the AI produced impact
                            text genuinely distinct from the observation. */}
                        {p.annotation.whyItMatters &&
                         p.annotation.whyItMatters.trim() !== p.annotation.observation.trim() ? (
                          <div className="cv-finding-section">
                            <h4>Why it matters</h4>
                            <p>{p.annotation.whyItMatters}</p>
                          </div>
                        ) : null}

                        {/* Suggested fix — closes the loop when the AI
                            emits a distinct recommended action. */}
                        {p.annotation.suggestedFix ? (
                          <div className="cv-finding-section">
                            <h4>Suggested fix</h4>
                            <p>{p.annotation.suggestedFix}</p>
                          </div>
                        ) : null}

                        <FindingActions
                          isOwner={isOwner}
                          isBookmarked={isBookmarked}
                          isSolved={isSolved}
                          isCustom={p.annotation.kind === "custom"}
                          copyText={`${p.annotation.title}\nSeverity: ${SEVERITY_LABEL[p.annotation.severity]}\n${p.annotation.observation}`}
                          onToggleBookmark={() => toggleBookmark(id)}
                          onToggleSolved={() => toggleSolved(id)}
                          onEdit={
                            p.annotation.kind === "custom" && p.annotation.customId
                              ? () => {
                                  setFormSeed({
                                    title: p.annotation.title,
                                    severity: p.annotation.severity,
                                    description: p.annotation.observation,
                                    editingId: p.annotation.customId!,
                                  });
                                  setFormError(null);
                                }
                              : undefined
                          }
                          onDelete={
                            p.annotation.kind === "custom" && p.annotation.customId
                              ? () => {
                                  void handleDelete(p.annotation.customId!);
                                }
                              : undefined
                          }
                        />
	                      </div>
	                    )}
	                    </div>
	                  </li>
                );
              })
            )}
          </ul>

          {/* Quick actions — bulk operations on the whole list.
              Add finding sits as the primary CTA, Copy all is the
              secondary affordance. Owner-only check applies only to
              Add finding (Copy works for viewers too). On an all-failed
              run BOTH are replaced by the upload CTA: "Add finding"
              invited users to annotate a wall, and "Copy findings"
              offered an export of nothing — success-shaped buttons on a
              run that delivered no capture. */}
          <div className="cv-sidebar-footer">
            {captureHealth?.allFailed ? (
              isOwner ? (
                <Link
                  href="/audit/new?mode=imageAudit"
                  className="cv-add-finding-btn"
                >
                  <CameraIcon />
                  <span className="cv-add-finding-btn-label">
                    Upload a screenshot to audit
                  </span>
                </Link>
              ) : (
                /* Shared/guest view: they can't upload to someone else's
                   run, so push them to start their own audit instead. */
                <Link href={START_AUDIT_HREF} className="cv-add-finding-btn">
                  <span className="cv-add-finding-btn-label">
                    Try Snap Site — free
                  </span>
                </Link>
              )
            ) : null}
            {!captureHealth?.allFailed && isOwner ? (
              <button
                type="button"
                className="cv-add-finding-btn"
                onClick={() => {
                  // Opens the form modal directly. User can pick
                  // whether to pin the finding to a specific spot
                  // (via "Pick spot on canvas" inside the modal) or
                  // leave it as a general / whole-page finding.
                  setFormSeed({
                    title: "",
                    severity: "medium",
                    description: "",
                  });
                  setFormError(null);
                  setPinTool(false);
                }}
                aria-label="Add a custom finding" data-tip="Add a custom finding"
              >
                <PlusIcon />
                <span className="cv-add-finding-btn-label">Add finding</span>
              </button>
            ) : null}
            {/* Copy findings — opens the export dialog (plain text /
                markdown) instead of the old silent plain-text dump.
                Pro/Agency only: free users get a paywall toast + the
                pricing page, mirroring the locked Share button. The
                Pro pill on the locked variant matches the Share gate so
                the affordance reads consistently. */}
            {captureHealth?.allFailed ? null : isLocked ? (
              /* Locked guests can't export — the report isn't theirs yet. The
                 topbar "Unlock full report" CTA is their path; a second nudge
                 here keeps the bottom of the rail on-message. */
              <button
                type="button"
                className="cv-add-finding-btn"
                onClick={() => handleUnlock("rail")}
                aria-label="Sign in to unlock the full report"
              >
                <LockIcon /> Unlock full report — free
              </button>
            ) : canExportFindings ? (
              <button
                type="button"
                className="cv-sidebar-action"
                data-tip="Copy all findings as plain text or markdown"
                onClick={() => setExportOpen(true)}
                disabled={dedupedFindings.length === 0}
                aria-label="Copy all findings as plain text or markdown"
              >
                <CopyIcon /> Copy findings
              </button>
            ) : (
              <button
                type="button"
                className="cv-sidebar-action cv-action-locked"
                data-tip="Copy findings as text or markdown — available on the Pro plan"
                onClick={() => {
                  showToast(
                    "Copying findings as text or markdown is on the Pro plan. We opened your plan options in a new tab.",
                    "warn",
                  );
                  openUpgradeInNewTab();
                }}
                aria-label="Copy findings — available on the Pro plan"
              >
                <CopyIcon /> Copy findings <span className="cv-pro-pill">Pro</span>
              </button>
            )}
            {/* Provenance line. Public viewers see a slightly more
                inviting variant pointing back at marketing so a
                shared audit becomes a funnel touchpoint instead of a
                dead end. Owners see the neutral version. */}
            {isViewer ? (
              <p className="cv-sidebar-footer-prov">
                Audited with{" "}
                <Link href="/" className="cv-sidebar-footer-prov-link">
                  Snap Site
                </Link>
                {" — try your own"}
              </p>
            ) : (
              <p className="cv-sidebar-footer-prov">
                Findings generated by Snap Site
              </p>
            )}
          </div>
        </aside>
      </div>

      {/* Modal renders only when a seed exists AND the user isn't
          currently picking a spot on the canvas. When they ARE
          picking (pinTool=true), the modal is unmounted so the
          canvas is reachable — but formSeed stays populated so
          the typed-in values survive the round trip and the modal
          can remount with everything intact (plus the new placement
          when the click lands). */}
      {formSeed && !pinTool ? (
        <AnnotationFormModal
          seed={formSeed}
          saving={formSaving}
          error={formError}
          /* Human-readable confirmation for the modal: "Home
             (Desktop)" once a spot has been picked. Built here
             because pageNameByIndex lives at this scope; the
             modal just renders the string. */
          placementLabel={
            formSeed.placement
              ? `${pageNameByIndex[formSeed.placement.pageIndex] ?? `Page ${formSeed.placement.pageIndex + 1}`} · ${
                  formSeed.placement.viewport === "mobile" ? "Mobile" : "Desktop"
                }`
              : null
          }
          onCancel={() => setFormSeed(null)}
          onSave={handleSaveAnnotation}
          onPickSpot={(current) => {
            // Preserve the in-progress form values in formSeed so
            // they're still there when the modal remounts after
            // the canvas click. Then enter pin-pick mode.
            setFormSeed((prev) => prev
              ? { ...prev, title: current.title, severity: current.severity, description: current.description }
              : null);
            setPinTool(true);
          }}
        />
      ) : null}

      {/* Themed delete-confirm dialog. Lives in the same .cv-modal
          family as Add finding so the destructive flow doesn't yank
          the user into a native browser confirm (which ignores dark
          mode + the design tokens). The danger variant on the
          primary button signals the irreversibility. */}
      {deleteTarget ? (
        <DeleteFindingModal
          title={deleteTarget.title}
          deleting={deleting}
          onCancel={() => {
            if (deleting) return;
            setDeleteTarget(null);
          }}
          onConfirm={confirmDelete}
        />
      ) : null}

      {/* Copy-findings export dialog — plain text / markdown picker with a
          live preview. Only mounts when opened; gating happens at the
          trigger button (Pro/Agency or public viewer). */}
      {exportOpen ? (
        <ExportFindingsModal
          findings={dedupedFindings}
          pageNameByIndex={pageNameByIndex}
          meta={exportMeta}
          onClose={() => setExportOpen(false)}
        />
      ) : null}

      {/* "First pass" explainer — opened from the pill beside the grade.
          Same modal kit as the other dialogs. */}
      {firstPassModalOpen ? (
        <FirstPassModal
          auditId={job.id}
          onClose={() => setFirstPassModalOpen(false)}
        />
      ) : null}

      {/* Capture-failure dialog — one-time, owner-only. Explains the
          blank result, confirms the credit was returned, and offers the
          one path that works (Upload image). */}
      {captureFailModalOpen && captureHealth ? (
        <div className="cv-modal-backdrop" onClick={closeCaptureFailModal}>
          <div
            className="cv-modal cv-capture-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cv-capture-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="cv-modal-head">
              {/* Warn icon sets the tone before a word is read — without
                  it the modal's shape (title + friendly body + green note
                  + teal CTA) read like a success confirmation. */}
              <h3 id="cv-capture-modal-title" className="cv-capture-modal-title">
                <span className="cv-capture-modal-title-icon" aria-hidden="true">
                  <AlertTriangleIcon />
                </span>
                {captureHealth.title}
              </h3>
              <button
                type="button"
                className="cv-modal-close"
                onClick={closeCaptureFailModal}
                aria-label="Close"
                autoFocus
              >
                ×
              </button>
            </header>
            <div className="cv-modal-body cv-capture-modal-body">
              <p>{captureHealth.detail}</p>
              <p className="cv-capture-modal-credit">
                <strong>No credit was lost</strong> — this run was refunded
                automatically.
              </p>
              <div className="cv-capture-modal-actions">
                <button
                  type="button"
                  className="cv-action-btn cv-action-btn-tertiary"
                  onClick={closeCaptureFailModal}
                >
                  Dismiss
                </button>
                {captureHealth.serverDown ? (
                  <Link
                    href={`/audit/new?url=${encodeURIComponent(result.sourceUrl)}`}
                    className="cv-action-btn cv-action-primary"
                    onClick={closeCaptureFailModal}
                  >
                    Run the audit again
                  </Link>
                ) : (
                  <Link
                    href="/audit/new?mode=imageAudit"
                    className="cv-action-btn cv-action-primary"
                    onClick={closeCaptureFailModal}
                  >
                    Upload a screenshot to audit
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {addPageOpen ? (
        <AddPageModal
          siteUrl={result.sourceUrl}
          existingUrls={[
            result.homepage.url,
            ...result.pages.map((p) => p.url),
          ]}
          viewports={result.viewports}
          creditBalanceHint={creditBalance}
          saving={addPageSaving}
          error={addPageError}
          onCancel={() => {
            if (addPageSaving) return;
            setAddPageOpen(false);
            setAddPageError(null);
          }}
          onSubmit={async (finalUrl, chosenViewports) => {
            const token = readToken();
            if (!token) {
              setAddPageError("Sign in required to add a page.");
              return;
            }
            setAddPageSaving(true);
            setAddPageError(null);
            try {
              const response = await appendPageToAudit(
                token,
                job.id,
                finalUrl,
                chosenViewports
              );
              onJobUpdate?.({
                ...job,
                result: response.result,
                progress: "done",
              });
              setAddPageOpen(false);
              showToast(
                `Added "${response.page.name || hostnameOf(response.page.url)}" to the audit.`,
                "success"
              );
            } catch (err) {
              setAddPageError(
                err instanceof ApiError
                  ? err.message
                  : err instanceof Error
                    ? err.message
                    : "Could not add the page. Try again."
              );
            } finally {
              setAddPageSaving(false);
            }
          }}
        />
      ) : null}

      {shareModalOpen ? (
        <ShareModal jobId={job.id} onClose={() => setShareModalOpen(false)} />
      ) : null}

      {exportPhase !== "idle" ? (
        <ExportProgressOverlay phase={exportPhase} onCancel={handleCancelExport} />
      ) : null}

      {/* Feedback dialog — opened by the one-time 15s prompt (see the
          timer effect near the other first-visit hints) or the left
          rail's "Give feedback" button. */}
      {feedbackOpen ? (
        <FeedbackDialog
          jobId={job.id}
          userEmail={creatorEmail}
          onClose={() => setFeedbackOpen(false)}
        />
      ) : null}

      {/* Toast notifications. `notranslate` for the same reason as the
          tooltip: a short-lived, dynamically mounted/unmounted text node
          that page-translation extensions can rewrap mid-life, which then
          trips React's removeChild on dismiss. */}
      {toast && (
        <div className={`cv-toast cv-toast-${toast.type} notranslate`} role="alert" translate="no">
          <span className="cv-toast-msg">{toast.message}</span>
          <button type="button" className="cv-toast-close" onClick={() => setToast(null)} aria-label="Dismiss">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Export progress overlay - keeps the user oriented while the live  */
/*  canvas is captured and compiled into a PDF.                        */
/* ================================================================== */

function ExportProgressOverlay({
  phase,
  onCancel,
}: {
  phase: "prepare" | "capture" | "compile" | "save" | "done";
  onCancel: () => void;
}) {
  const steps: Array<{ id: "prepare" | "capture" | "compile" | "save"; label: string }> = [
    { id: "prepare", label: "Preparing canvas" },
    { id: "capture", label: "Capturing annotations and screenshots" },
    { id: "compile", label: "Building PDF" },
    { id: "save", label: "Downloading file" },
  ];
  const order = (id: string) => steps.findIndex((s) => s.id === id);
  const activeIdx = phase === "done" ? steps.length : order(phase);

  return (
    <div className="cv-export-overlay" role="dialog" aria-modal="true" aria-label="Exporting">
      <div className="cv-export-card">
        <div className="cv-export-spinner" aria-hidden="true">
          {phase === "done" ? <ExportCheckIcon /> : null}
        </div>
        <h3 className="cv-export-title">
          {phase === "done" ? "Your PDF is ready" : "Exporting your audit"}
        </h3>
        <p className="cv-export-sub">
          {phase === "done"
            ? "Check your downloads folder."
            : "Large canvases can take 10 to 20 seconds."}
        </p>
        <ol className="cv-export-steps" aria-hidden="true">
          {steps.map((s, i) => {
            const status =
              i < activeIdx ? "done" : i === activeIdx ? "active" : "pending";
            return (
              <li key={s.id} className={`cv-export-step cv-export-step-${status}`}>
                <span className="cv-export-step-icon">
                  {status === "done" ? (
                    <ExportStepDoneIcon />
                  ) : status === "active" ? (
                    <ExportStepSpinnerIcon />
                  ) : (
                    <ExportStepPendingIcon />
                  )}
                </span>
                <span>{s.label}</span>
              </li>
            );
          })}
        </ol>
        {phase !== "done" ? (
          <button type="button" className="cv-export-cancel" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ExportCheckIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/* Step status icons for the export overlay — a filled check (done), a
   spinning ring (active, animated via CSS), and a hollow circle
   (pending). Crisper and more consistent than the old CSS-drawn dots. */
function ExportStepDoneIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="9" fill="var(--primary, #2b8a8f)" />
      <path d="M5 9.3l2.6 2.6L13 6.4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ExportStepSpinnerIcon() {
  return (
    <svg className="cv-export-spin" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="7.4" stroke="var(--soft-line, #ebeef3)" strokeWidth="2" />
      <path d="M9 1.6a7.4 7.4 0 0 1 7.4 7.4" stroke="var(--primary, #2b8a8f)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ExportStepPendingIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="7.4" stroke="var(--soft-line, #d7dce4)" strokeWidth="2" />
    </svg>
  );
}

/* ================================================================== */
/*  Annotation form modal — create/edit a custom annotation             */
/* ================================================================== */

function AnnotationFormModal({
  seed,
  saving,
  error,
  placementLabel,
  onCancel,
  onSave,
  onPickSpot,
}: {
  seed: FormSeed;
  saving: boolean;
  error: string | null;
  /** Human-readable label of the picked spot ("Home · Desktop").
      Null when no placement is set yet. Built by the parent so the
      modal doesn't need access to the page-name lookup. */
  placementLabel: string | null;
  onCancel: () => void;
  onSave: (data: { title: string; severity: HeuristicSeverity; description: string; clearPlacement: boolean }) => void;
  /** Called when user clicks "Pick a spot on the canvas". Parent
      should hide this modal, enter pin-pick mode, and update the
      seed.placement once the user clicks a screenshot. The current
      title/severity/description are passed up so the parent can
      preserve them across the hide/show cycle. */
  onPickSpot: (current: { title: string; severity: HeuristicSeverity; description: string }) => void;
}) {
  const [title, setTitle] = useState(seed.title);
  const [severity, setSeverity] = useState<HeuristicSeverity>(seed.severity);
  const [description, setDescription] = useState(seed.description);
  const [titleError, setTitleError] = useState<string | null>(null);
  // Brief "Copied" confirmation after the rescue button is clicked.
  // Resets automatically a couple of seconds later so the user can
  // re-copy without re-mounting the modal.
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  /** Copy the user's typed finding to the clipboard so it can't be
   *  lost even if they close the modal, refresh, or lose auth state.
   *  Triggered from the rescue button that renders next to any save
   *  error — paired with copy that says "your text is safe", this is
   *  the actual mechanism that makes that promise true. */
  const handleCopyText = async () => {
    const text = [
      title.trim() ? `Title: ${title.trim()}` : null,
      `Severity: ${SEVERITY_LABEL[severity]}`,
      description.trim() ? `\n${description.trim()}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      /* clipboard blocked — UI just won't flip to "Copied"; user
         can still select-and-copy from the form fields manually. */
    }
  };
  // "pinned" if a placement exists (either passed in or just picked
  // via the canvas pin-pick step), "general" otherwise. Editing an
  // existing custom finding keeps whatever placement it had.
  const [placementMode, setPlacementMode] = useState<"pinned" | "general">(
    seed.placement ? "pinned" : "general",
  );
  // If the parent updates seed.placement (e.g., after the user
  // picks a spot on the canvas), reflect it in the placementMode
  // toggle. Otherwise we'd be stuck on "general" even after a pin
  // landed.
  useEffect(() => {
    if (seed.placement) setPlacementMode("pinned");
  }, [seed.placement]);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const isEdit = !!seed.editingId;

  return (
    <div className="cv-modal-backdrop" onClick={onCancel}>
      <div
        className="cv-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? "Edit finding" : "Add finding"}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cv-modal-head">
          {/* Title matches the CTA the user clicked ("Add finding")
              so the vocabulary stays consistent across the flow.
              Close × dropped — Cancel in the footer is enough; two
              ways to dismiss was noise. */}
          <h3>{isEdit ? "Edit finding" : "Add finding"}</h3>
        </header>
        <form
          className="cv-modal-body"
          onSubmit={(e) => {
            e.preventDefault();
            const t = title.trim();
            if (saving) return;
            if (!t) {
              setTitleError("Add a title for this finding.");
              titleInputRef.current?.focus();
              return;
            }
            setTitleError(null);
            onSave({
              title: t,
              severity,
              description: description.trim(),
              // Tells the parent to strip any pre-existing placement
              // off the finding when the user chose "general" (covers
              // the edit case where someone toggles a pinned finding
              // back to general).
              clearPlacement: placementMode === "general",
            });
          }}
        >
          <label className="cv-field">
            <span>Title</span>
            <input
              ref={titleInputRef}
              type="text"
              value={title}
              autoFocus
              maxLength={240}
              onChange={(e) => {
                setTitle(e.target.value);
                if (titleError && e.target.value.trim()) setTitleError(null);
              }}
              placeholder="Short summary, e.g. CTA color is too low contrast"
              aria-invalid={titleError ? true : undefined}
            />
            {titleError ? (
              <span className="cv-field-error" role="alert">{titleError}</span>
            ) : null}
          </label>
          <div className="cv-field">
            <span>Severity</span>
            <div className="cv-sev-chips">
              {(["high", "medium", "low"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`cv-sev-chip cv-sev-${s}${severity === s ? " active" : ""}`}
                  onClick={() => setSeverity(s)}
                >
                  {SEVERITY_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
          <label className="cv-field">
            <span>Description (optional)</span>
            <textarea
              value={description}
              maxLength={4000}
              rows={4}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Why this matters and what to fix."
            />
          </label>
          {/* Where on the page? — the new bit that replaces the
              old "click a spot first then fill the form" flow.
              User picks whether this finding is pinned to a
              specific screenshot location or applies generally
              (whole page / multiple elements). */}
          <div className="cv-field">
            <span>Where on the page?</span>
            <div className="cv-placement-options" role="radiogroup" aria-label="Where on the page">
              <button
                type="button"
                role="radio"
                aria-checked={placementMode === "pinned"}
                className={`cv-placement-option${placementMode === "pinned" ? " is-active" : ""}`}
                onClick={() => setPlacementMode("pinned")}
              >
                <span className="cv-placement-option-title">Pin to a spot</span>
                <span className="cv-placement-option-sub">
                  {seed.placement && placementLabel
                    ? `Pinned on ${placementLabel}`
                    : "Click a place on the screenshot"}
                </span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={placementMode === "general"}
                className={`cv-placement-option${placementMode === "general" ? " is-active" : ""}`}
                onClick={() => setPlacementMode("general")}
              >
                <span className="cv-placement-option-title">General</span>
                <span className="cv-placement-option-sub">
                  Page-wide or affects multiple elements
                </span>
              </button>
            </div>
            {/* Heads-up when an EDIT switches a pinned finding to
                General — saving will drop the existing bbox, and
                that's not obvious from the toggle. The previous
                behaviour silently destroyed work (parallel to the
                annotation-rescue we fixed for session expiry):
                user toggles → clicks Save → pin gone, no warning.
                Now they see "This will remove the pin" before
                they commit. */}
            {seed.editingId && seed.placement && placementMode === "general" ? (
              <p className="cv-placement-warning" role="status">
                <strong>Heads up:</strong> saving will remove the existing pin
                from {placementLabel ?? "the screenshot"}. Switch back to
                <strong> Pin to a spot</strong> if you want to keep it.
              </p>
            ) : null}
            {placementMode === "pinned" ? (
              <div className="cv-placement-pick-row">
                {/* Confirmation chip — only renders once a spot has
                    been picked. Calls out the page + viewport in
                    plain text so the user is sure their click
                    landed where they thought it did. */}
                {seed.placement && placementLabel ? (
                  <span className="cv-placement-confirmed" aria-live="polite">
                    <CheckIcon /> Pinned on {placementLabel}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="cv-placement-pick-btn"
                  onClick={() =>
                    onPickSpot({ title, severity, description })
                  }
                >
                  {seed.placement ? "Change spot" : "Pick spot on canvas"}
                </button>
              </div>
            ) : null}
          </div>
          {error ? (
            <div className="cv-modal-error" role="alert">
              <p className="cv-modal-error-text">{error}</p>
              {/* Rescue button — visible on any save error so the user
                  can preserve their work no matter what went wrong.
                  The auth-expiry case is the prime motivator (the old
                  "Refresh the page" copy used to wipe typed content),
                  but a generic save failure benefits from the same
                  escape hatch. */}
              <button
                type="button"
                className="cv-modal-error-copy"
                onClick={handleCopyText}
                aria-label="Copy this finding's text to your clipboard"
              >
                {copied ? "Copied ✓" : "Copy text"}
              </button>
            </div>
          ) : null}
          <footer className="cv-modal-foot">
            <button type="button" className="cv-modal-btn" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="cv-modal-btn cv-modal-btn-primary" disabled={saving}>
              {/* Verb-only label — the modal title already says "Add
                  finding" / "Edit finding", so the button doesn't
                  need to repeat the noun. Previously the screen
                  had three "Add finding" labels stacked (footer
                  CTA, modal title, button), which read as noise. */}
              {saving ? "Saving…" : isEdit ? "Save" : "Add"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Delete-finding confirm — themed replacement for window.confirm()    */
/* ================================================================== */

function DeleteFindingModal({
  title,
  deleting,
  onCancel,
  onConfirm,
}: {
  title: string;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  // Esc to dismiss, matching the AnnotationFormModal's keyboard
  // contract. Disabled while the request is in-flight so users
  // can't half-cancel the operation.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !deleting) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleting, onCancel]);

  return (
    <div className="cv-modal-backdrop" onClick={deleting ? undefined : onCancel}>
      <div
        className="cv-modal cv-delete-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="cv-delete-title"
        aria-describedby="cv-delete-body"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cv-modal-head">
          <h3 id="cv-delete-title">Delete this finding?</h3>
        </header>
        {/* Footer is nested INSIDE the body — same structure as
            AnnotationFormModal. .cv-modal-foot only sets
            padding-top: 4px; its horizontal padding comes from
            the body (18px 22px). Previously the footer was a
            sibling of the body, which left the buttons stuck to
            the modal's right edge with a strange empty band. */}
        <div className="cv-modal-body cv-delete-body" id="cv-delete-body">
          {/* Lead with irreversibility — the trash dialog uses the
              same pattern. The finding title is quoted so users can
              eyeball which one they're about to lose. */}
          <p className="cv-delete-lead">This can&rsquo;t be undone.</p>
          <p className="cv-delete-note">
            <b>&ldquo;{title}&rdquo;</b> will be permanently removed from this audit.
          </p>
          <footer className="cv-modal-foot">
            <button
              type="button"
              className="cv-modal-btn"
              onClick={onCancel}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cv-modal-btn cv-modal-btn-danger"
              onClick={onConfirm}
              disabled={deleting}
              autoFocus
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  First-pass explainer modal — what the automated score covers       */
/* ================================================================== */

/**
 * Opened by clicking the "First pass" pill beside the grade. Renders
 * the manual-review guidance as real markup (paragraphs + list)
 * instead of the plain-text tooltip it used to be, so the checklist
 * reads properly. Esc / backdrop / × all close — same contract as the
 * other cv-modals.
 */
function FirstPassModal({
  auditId,
  onClose,
}: {
  /** Current audit id — threaded onto the "Request a full audit" link
      so the contact form lands pre-filled with this audit, and the
      lead arrives already knowing which site and score to look at. */
  auditId: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="cv-modal-backdrop" onClick={onClose}>
      <div
        className="cv-modal cv-firstpass-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cv-firstpass-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cv-modal-head">
          <h3 id="cv-firstpass-title">About this first pass</h3>
          <button
            type="button"
            className="cv-modal-close"
            onClick={onClose}
            aria-label="Close"
            autoFocus
          >
            ×
          </button>
        </header>
        <div className="cv-modal-body">
          {/* Lead — the framing statement for the whole dialog. */}
          <div className="cv-firstpass-modal-lead">
            <p>
              This score reflects everything we can check automatically
              across accessibility and usability. A human expert will still
              catch nuances no tool can — so treat it as a{" "}
              <strong>strong starting point, not the final word</strong>.
            </p>
          </div>
          {/* The hand-check list — plain bullets; icon chips here read
              as decoration and competed with the lead's check chip. */}
          <div className="cv-firstpass-modal-note">
            <p className="cv-firstpass-note-title">Worth checking by hand</p>
            <ul className="cv-firstpass-checklist">
              <li>Can you use the whole site with just a keyboard, no mouse?</li>
              <li>Are error messages easy to spot and understand?</li>
              <li>Does the page read clearly out loud for screen readers?</li>
            </ul>
          </div>
          {/* Soft upsell — the deeper, human-only review is a paid
              hands-on audit. First person on purpose: the pitch is "a
              human takes it further," so a human voice fits here. Sits
              below the checklist so it answers a need we just named. */}
          <div className="cv-firstpass-modal-cta">
            <p className="cv-firstpass-cta-title">Need a human&rsquo;s eyes on it?</p>
            <p className="cv-firstpass-cta-sub">
              The scan above is the free first pass. For the deeper issues,
              you can hire me for a hands-on UX audit &mdash; I take on a
              few each month.
            </p>
            <a
              className="cv-firstpass-cta-btn"
              href={`/contact?topic=full-audit&audit=${encodeURIComponent(
                auditId,
              )}#contact-form`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Hire me for a full audit →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Export-findings modal — copy all findings as plain text / markdown  */
/* ================================================================== */

/**
 * Replaces the old "Copy all" button's silent plain-text dump. Opens a
 * dialog letting the user pick Markdown (default — pastes nicely into
 * Notion, Linear, GitHub, docs) or Plain text, shows a live preview of
 * exactly what will be copied, and copies on demand. Gated to Pro/Agency
 * by the caller; this component assumes access has been granted.
 */
function ExportFindingsModal({
  findings,
  pageNameByIndex,
  meta,
  onClose,
}: {
  findings: FindingEntry[];
  pageNameByIndex: Record<number, string>;
  meta: FindingsExportMeta;
  onClose: () => void;
}) {
  const [format, setFormat] = useState<ExportFormat>("markdown");
  const [copied, setCopied] = useState(false);

  // Both formats are cheap to build and recomputing on every keystroke
  // isn't a concern (there's no input), but memoizing keeps the preview
  // from re-serializing the whole audit on unrelated re-renders.
  const text = useMemo(
    () => buildFindingsExportText(format, findings, pageNameByIndex, meta),
    [format, findings, pageNameByIndex, meta],
  );

  // Reset the "Copied ✓" affordance whenever the user switches format —
  // the previously-copied text no longer matches what's on screen.
  useEffect(() => setCopied(false), [format]);

  // Auto-clear the copied state so the button can be re-pressed without
  // re-opening the dialog. Matches the rescue-button pattern elsewhere.
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2200);
    return () => window.clearTimeout(id);
  }, [copied]);

  // Esc to dismiss — same keyboard contract as the other cv-modals.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      /* Clipboard blocked — the textarea is selectable so the user can
         still ⌘/Ctrl-A, ⌘/Ctrl-C manually. The button just won't flip. */
    }
  };

  const findingCount = findings.length;

  return (
    <div className="cv-modal-backdrop" onClick={onClose}>
      <div
        className="cv-modal cv-export-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cv-export-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cv-modal-head">
          <h3 id="cv-export-title">Copy findings</h3>
        </header>
        <div className="cv-modal-body cv-export-body">
          {/* Format toggle — segmented control in the same family as the
              severity chips / placement options. Markdown leads because
              it pastes cleanly into the tools teams actually triage in. */}
          <div className="cv-field">
            <span>Format</span>
            <div
              className="cv-export-format"
              role="radiogroup"
              aria-label="Export format"
            >
              <button
                type="button"
                role="radio"
                aria-checked={format === "markdown"}
                className={`cv-export-format-opt${format === "markdown" ? " is-active" : ""}`}
                onClick={() => setFormat("markdown")}
              >
                <span className="cv-export-format-title">Markdown</span>
                <span className="cv-export-format-sub">
                  For Notion, Linear, GitHub, docs
                </span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={format === "plain"}
                className={`cv-export-format-opt${format === "plain" ? " is-active" : ""}`}
                onClick={() => setFormat("plain")}
              >
                <span className="cv-export-format-title">Plain text</span>
                <span className="cv-export-format-sub">
                  For email, Slack, anywhere
                </span>
              </button>
            </div>
          </div>

          {/* Live preview — read-only but fully selectable, so even if the
              clipboard API is blocked the user can grab the text by hand.
              Mono font for plain, default for markdown source. */}
          <div className="cv-field">
            <span>Preview</span>
            <textarea
              className="cv-export-preview"
              value={text}
              readOnly
              spellCheck={false}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Export preview"
            />
          </div>

          <footer className="cv-modal-foot cv-export-foot">
            <span className="cv-export-count">
              {findingCount} finding{findingCount === 1 ? "" : "s"}
            </span>
            <span className="cv-export-foot-spacer" />
            <button type="button" className="cv-modal-btn" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="cv-modal-btn cv-modal-btn-primary"
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <CheckIcon /> Copied
                </>
              ) : (
                <>
                  <CopyIcon /> Copy {format === "markdown" ? "Markdown" : "text"}
                </>
              )}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Share modal — surface the public read-only link with a copy button  */
/* ================================================================== */

function ShareModal({
  jobId,
  onClose,
}: {
  jobId: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const url = useMemo(() => createShareLink(jobId).url, [jobId]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset the "Copied" flip after a moment.
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2200);
    return () => window.clearTimeout(id);
  }, [copied]);

  // Esc to dismiss — same keyboard contract as the other cv-modals.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Pre-select the URL on open so a keyboard user can copy it straight
  // away even if they never click the button.
  useEffect(() => {
    inputRef.current?.select();
  }, []);

  const handleCopy = async () => {
    const ok = await copyShareLink(jobId);
    if (ok) {
      setCopied(true);
    } else {
      // Clipboard blocked — re-select the field so the user can copy by hand.
      inputRef.current?.select();
    }
  };

  return (
    <div className="cv-modal-backdrop" onClick={onClose}>
      <div
        className="cv-modal cv-share-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cv-share-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cv-modal-head">
          <h3 id="cv-share-title">Share this audit</h3>
          <button
            type="button"
            className="cv-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="cv-modal-body cv-share-modal-body">
          <p className="cv-share-sub">
            Anyone with this link can view a read-only version of this audit —
            no account needed.
          </p>
          <input
            ref={inputRef}
            className="cv-share-url"
            value={url}
            readOnly
            spellCheck={false}
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Public share link"
          />
          <footer className="cv-modal-foot">
            <button
              type="button"
              className="cv-modal-btn cv-modal-btn-primary"
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <CheckIcon /> Copied
                </>
              ) : (
                <>
                  <CopyIcon /> Copy link
                </>
              )}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Add-page modal — extend an existing audit with another URL          */
/* ================================================================== */

function AddPageModal({
  siteUrl,
  existingUrls,
  viewports,
  allowedViewports,
  creditBalanceHint,
  saving,
  error,
  onCancel,
  onSubmit,
}: {
  siteUrl: string;
  existingUrls: string[];
  /** Audit's default viewports — pre-selected when the picker opens. */
  viewports: Viewport[];
  /**
   * Viewports the user's plan permits. Undefined = no restriction known
   * (every viewport selectable). When defined, viewports not in this list
   * render disabled with a "Pro" tag.
   */
  allowedViewports?: Viewport[];
  creditBalanceHint: number | null;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (finalUrl: string, chosenViewports: Viewport[]) => void;
}) {
  const origin = useMemo(() => {
    try {
      const u = new URL(siteUrl);
      return `${u.protocol}//${u.host}`;
    } catch {
      return siteUrl;
    }
  }, [siteUrl]);
  const hostLabel = useMemo(() => {
    try {
      return new URL(siteUrl).hostname.replace(/^www\./, "");
    } catch {
      return siteUrl;
    }
  }, [siteUrl]);

  const [input, setInput] = useState("");

  // Selected viewports for this single page. Defaults to whatever the
  // audit was set up with — most users won't deviate, and the picker is
  // tucked behind a "Change" affordance so this default is the only thing
  // the modal shows at rest. allowedViewports filters out plan-restricted
  // options on initialisation so a Free user doesn't start with Mobile
  // pre-selected and then see it gated on submit.
  const ALL_VIEWPORTS: Viewport[] = useMemo(() => ["desktop", "mobile"], []);
  const isAllowed = useCallback(
    (v: Viewport) =>
      allowedViewports === undefined ? true : allowedViewports.includes(v),
    [allowedViewports]
  );
  const [selectedViewports, setSelectedViewports] = useState<Viewport[]>(() => {
    const seed = (viewports.length ? viewports : (["desktop"] as Viewport[])).filter(
      (v) => (allowedViewports === undefined ? true : allowedViewports.includes(v))
    );
    return seed.length > 0 ? seed : [ALL_VIEWPORTS[0]];
  });
  const [pickerOpen, setPickerOpen] = useState(false);

  // Stable canonical ordering — keeps display deterministic regardless of
  // the toggle order. Cost is always derived from the selected set so the
  // CTA and meta line agree.
  const orderedSelected = useMemo(
    () => ALL_VIEWPORTS.filter((v) => selectedViewports.includes(v)),
    [ALL_VIEWPORTS, selectedViewports]
  );
  const creditCost = Math.max(1, orderedSelected.length);

  // Gate: only when we actually know the balance AND it's short. When the
  // balance is unknown (null), fall through to the backend's 402 safety net.
  const insufficientCredits =
    creditBalanceHint !== null && creditBalanceHint < creditCost;
  const shortBy = insufficientCredits
    ? creditCost - (creditBalanceHint as number)
    : 0;

  const toggleViewport = useCallback(
    (v: Viewport) => {
      if (!isAllowed(v)) return;
      setSelectedViewports((prev) => {
        const has = prev.includes(v);
        if (has) {
          // Don't allow zeroing out — at least one viewport must remain.
          if (prev.length <= 1) return prev;
          return prev.filter((x) => x !== v);
        }
        return [...prev, v];
      });
    },
    [isAllowed]
  );

  const viewportLabel = useCallback(
    (v: Viewport) => (v === "mobile" ? "Mobile" : "Desktop"),
    []
  );

  const selectedLabel = useMemo(() => {
    if (orderedSelected.length === 0) return "—";
    if (orderedSelected.length === 1) return viewportLabel(orderedSelected[0]);
    return orderedSelected.map(viewportLabel).join(" + ");
  }, [orderedSelected, viewportLabel]);

  // Resolve whatever the user typed into a full URL on the audit's origin.
  // Accepts paths ("/about"), bare hostnames+paths ("skyscanner.com/about"),
  // or fully-qualified URLs ("https://skyscanner.com/about"). Returns null
  // if the input doesn't make sense.
  const resolved = useMemo(() => {
    const trimmed = input.trim();
    if (!trimmed) return null;
    let absolute: string;
    if (/^https?:\/\//i.test(trimmed)) {
      absolute = trimmed;
    } else if (trimmed.startsWith("/")) {
      absolute = origin + trimmed;
    } else {
      // Could be "about" (bare path), or "skyscanner.com/about" (host).
      // Heuristic: if it has a dot in the first segment, assume a host.
      const firstSegment = trimmed.split("/")[0];
      if (firstSegment.includes(".")) {
        absolute = `https://${trimmed}`;
      } else {
        absolute = `${origin}/${trimmed}`;
      }
    }
    try {
      return new URL(absolute).toString();
    } catch {
      return null;
    }
  }, [input, origin]);

  const isSameDomain = useMemo(() => {
    if (!resolved) return false;
    try {
      const a = new URL(siteUrl).hostname.replace(/^www\./, "");
      const b = new URL(resolved).hostname.replace(/^www\./, "");
      return a.toLowerCase() === b.toLowerCase();
    } catch {
      return false;
    }
  }, [resolved, siteUrl]);

  const isDuplicate = useMemo(() => {
    if (!resolved) return false;
    const key = (raw: string) => {
      try {
        const u = new URL(raw);
        u.hash = "";
        let rest = u.pathname + u.search;
        if (rest.length > 1 && rest.endsWith("/")) rest = rest.slice(0, -1);
        return `${u.protocol}//${u.host.replace(/^www\./, "")}${rest}`;
      } catch {
        return raw.trim().toLowerCase().replace(/\/$/, "");
      }
    };
    const k = key(resolved);
    return existingUrls.some((u) => key(u) === k);
  }, [resolved, existingUrls]);

  // Tracks whether the user has tried to submit at least once. We
  // suppress empty-state errors until first submit, so the field
  // doesn't shout "required" the moment the modal opens.
  const [triedSubmit, setTriedSubmit] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmed = input.trim();
  const inlineError =
    trimmed && !resolved
      ? "That URL doesn't look right."
      : trimmed && !isSameDomain
        ? `Pages must be on ${hostLabel}.`
        : trimmed && isDuplicate
          ? "This page is already in the audit."
          : triedSubmit && !trimmed
            ? `Enter a URL or path on ${hostLabel} to continue.`
            : null;

  const isValid = !!resolved && isSameDomain && !isDuplicate;

  return (
    <div
      className="cv-modal-backdrop"
      onClick={onCancel}
    >
      <div
        className="cv-modal cv-add-page-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Add another page"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cv-modal-head">
          <h3>Add a page to this audit</h3>
          <span className="cv-modal-head-right">
            {/* Balance as a pill, matching the "credits available" badge on
                the dashboard/new-audit screens. */}
            {creditBalanceHint !== null ? (
              <span
                className="cv-add-page-balance"
                aria-label={`${creditBalanceHint} credit${creditBalanceHint === 1 ? "" : "s"} available`}
              >
                {creditBalanceHint} credit{creditBalanceHint === 1 ? "" : "s"} available
              </span>
            ) : null}
            <button
              type="button"
              className="cv-modal-close"
              onClick={onCancel}
              aria-label="Close"
              disabled={saving}
            >
              ×
            </button>
          </span>
        </header>
        <form
          className="cv-modal-body"
          onSubmit={(e) => {
            e.preventDefault();
            // Always proceed to validation — no preemptive button
            // disable. If invalid, surface the inline error on the
            // input and pull focus there so the user knows what to fix.
            setTriedSubmit(true);
            if (saving) return;
            if (!isValid || !resolved) {
              inputRef.current?.focus();
              return;
            }
            // orderedSelected enforces canonical viewport order and is
            // never empty (toggleViewport refuses to clear the last one).
            onSubmit(resolved, orderedSelected);
          }}
        >
          <label className="cv-field">
            <span>Page URL or path on {hostLabel}</span>
            <input
              ref={inputRef}
              type="text"
              value={input}
              autoFocus
              spellCheck={false}
              autoComplete="off"
              placeholder={`/pricing`}
              onChange={(e) => {
                setInput(e.target.value);
                // Clear the "tried submit" empty-state error once the
                // user starts typing — feels less aggressive.
                if (triedSubmit && e.target.value.trim()) setTriedSubmit(false);
              }}
              disabled={saving}
              aria-invalid={inlineError ? true : undefined}
            />
            <span
              className={`cv-add-page-hint${inlineError ? " is-error" : resolved && isSameDomain && !isDuplicate ? " is-ok" : ""}`}
              role={inlineError ? "alert" : undefined}
            >
              {inlineError ??
                (resolved && isSameDomain
                  ? `Will capture ${resolved}`
                  : `Same-site only — pages on other sites need a separate audit.`)}
            </span>
          </label>

          {/* Single quiet line. The device choice lives behind a "Change"
              affordance — default users see one calm sentence; users who
              want to deviate get checkboxes that recompute the cost live.
              Cost is also echoed on the CTA (see the submit button); one
              ambient mention here is enough trust signal for a 1-credit
              action. */}
          <div className="cv-add-page-meta">
            <p className="cv-add-page-meta-line">
              Captures{" "}
              {pickerOpen ? (
                <span
                  id="cv-add-page-picker"
                  className="cv-add-page-picker"
                  role="group"
                  aria-label="Devices to capture"
                >
                  {ALL_VIEWPORTS.map((v) => {
                    const allowed = isAllowed(v);
                    const checked = selectedViewports.includes(v);
                    return (
                      <label
                        key={v}
                        className={`cv-add-page-picker-opt${allowed ? "" : " is-locked"}${checked ? " is-checked" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked && allowed}
                          disabled={!allowed || saving}
                          onChange={() => toggleViewport(v)}
                        />
                        <span>{viewportLabel(v)}</span>
                        {!allowed ? (
                          <span className="cv-add-page-pro-tag" aria-label="Available on Pro plans">
                            Pro
                          </span>
                        ) : null}
                      </label>
                    );
                  })}
                </span>
              ) : (
                <b>{selectedLabel}</b>
              )}
              {" · "}
              <b>
                {creditCost} credit{creditCost === 1 ? "" : "s"}
              </b>
              <span className="cv-add-page-meta-sub">
                {" · "}about a minute
              </span>
              {/* Inline toggle. Plain button, no border — the affordance is
                  the underline, mirroring the link feel of other "Change"
                  controls in the app. Hidden during save to avoid mid-flight
                  edits to a submitted state. */}
              {!saving ? (
                <>
                  {" · "}
                  <button
                    type="button"
                    className="cv-add-page-change"
                    onClick={() => setPickerOpen((open) => !open)}
                    aria-expanded={pickerOpen}
                    aria-controls="cv-add-page-picker"
                  >
                    {pickerOpen ? "Done" : "Change"}
                  </button>
                </>
              ) : null}
            </p>
          </div>

          {error ? <p className="cv-modal-error">{error}</p> : null}

          {/* Insufficient-credits notice — proactive, before the user tries
              and hits the backend 402. */}
          {insufficientCredits && !saving ? (
            <p className="cv-add-page-credit-warn" role="status">
              Short by {shortBy} credit{shortBy === 1 ? "" : "s"} to add this
              page.{" "}
              <Link href="/account#topup" className="cv-add-page-credit-link">
                Top up
              </Link>{" "}
              or{" "}
              <button
                type="button"
                className="cv-add-page-credit-link"
                onClick={() => openUpgradeInNewTab()}
              >
                upgrade
              </button>
              .
            </p>
          ) : null}

          {saving ? (
            <p className="cv-add-page-progress" role="status">
              <span className="cv-add-page-spinner" aria-hidden="true" />
              Capturing and analysing — this can take up to a minute.
            </p>
          ) : null}

          <footer className="cv-modal-foot">
            <button
              type="button"
              className="cv-modal-btn"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </button>
            {/* When short on credits, the path forward is the Top up /
                upgrade links in the notice above — so just leave Cancel
                here, no dead "Add page" button. */}
            {insufficientCredits ? null : (
              <button
                type="submit"
                className="cv-modal-btn cv-modal-btn-primary"
                disabled={saving}
              >
                {saving
                  ? "Adding…"
                  : `Add page · ${creditCost} credit${creditCost === 1 ? "" : "s"}`}
              </button>
            )}
          </footer>
        </form>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Canvas page block — renders one captured page                       */
/* ================================================================== */

function CanvasPageBlock({
  layout,
  isSnapshot,
  activeFindingId,
  onFindingClick,
  pinTool,
  sourceFilter,
  cardOffsets,
  onCardMove,
  stageScale,
  solvedIds,
  pinOverrides,
  onPinMove,
  canMovePins,
}: {
  layout: PageLayout;
  /** Whether the parent audit ran in snapshot-only mode. When true,
   *  the analysis panel slot renders an explanatory placeholder
   *  instead of the regular findings cards — so a snapshot user
   *  doesn't see screenshots with nothing underneath. */
  isSnapshot: boolean;
  activeFindingId: string | null;
  onFindingClick: (pin: AuditAnnotation, shotIndex: number) => void;
  pinTool: boolean;
  sourceFilter: SourceFilter;
  cardOffsets: Record<string, { x: number; y: number }>;
  onCardMove: (id: string, x: number, y: number) => void;
  stageScale: number;
  solvedIds: Set<string>;
  pinOverrides: Record<string, { x: number; y: number }>;
  onPinMove: (id: string, x: number, y: number) => void;
  canMovePins: boolean;
}) {
  return (
    <div
      className="cv-page-block"
      style={{ left: layout.x, top: layout.y, width: layout.width, minHeight: layout.height }}
    >
      <header
        className="cv-page-header cv-page-header-card"
        style={{
          position: "absolute",
          left: layout.shotsX - layout.x,
          top: 0,
          width: layout.shotsWidth,
        }}
      >
        <p className="cv-page-eyebrow">Page {layout.index + 1}</p>
        <h2>{layout.page.name || hostnameOf(layout.page.url)}</h2>
        <p className="cv-page-header-url">{layout.page.url}</p>
        {layout.page.description ? <p className="cv-page-desc">{layout.page.description}</p> : null}
      </header>

      {layout.shots.map((shot, sIdx) => (
        <ShotBlock
          key={`${shot.shot.viewport}-${sIdx}`}
          shot={shot}
          shotIndex={sIdx}
          shotsX={layout.shotsX - layout.x}
          shotsY={layout.shotsY - layout.y}
          shotOffsetX={shot.offsetX}
          isSolo={layout.isSolo}
          activeFindingId={activeFindingId}
          pageIndex={layout.index}
          captureQuality={layout.page.captureQuality}
          onFindingClick={(pin) => onFindingClick(pin, sIdx)}
          pinTool={pinTool}
          sourceFilter={sourceFilter}
          cardOffsets={cardOffsets}
          onCardMove={onCardMove}
          stageScale={stageScale}
          solvedIds={solvedIds}
          pinOverrides={pinOverrides}
          onPinMove={onPinMove}
          canMovePins={canMovePins}
        />
      ))}

      {layout.hasAudit ? (
        <UxAnalysisPanel
          page={layout.page}
          top={layout.analysisTop}
          width={ANALYSIS_PANEL_WIDTH}
          left={ANALYSIS_PANEL_LEFT}
        />
      ) : isSnapshot ? (
        /* Snapshot-mode placeholder where the analysis cards would
           sit. Without this, snapshot runs left a literal void
           beneath the screenshots — the "Use Annotate to add
           findings" guidance lived only as a small line up in the
           overview, easy to miss. Same width and offset as
           UxAnalysisPanel so the page layout reads as intentional,
           not broken. */
        <section
          className="cv-analysis cv-analysis-snapshot"
          style={{
            position: "absolute",
            left: ANALYSIS_PANEL_LEFT,
            top: layout.analysisTop,
            width: ANALYSIS_PANEL_WIDTH,
          }}
        >
          <article className="cv-audit-card cv-audit-card-snapshot">
            <header>
              <p className="cv-audit-eyebrow">Snapshot mode</p>
              <p className="cv-audit-disclaimer">
                This page was captured as a screenshot only &mdash; no AI
                usability or accessibility findings were generated.
              </p>
            </header>
            <p className="cv-audit-empty cv-audit-empty-snapshot" role="status">
              You can still pin your own findings on the canvas using
              <strong> Add finding</strong>, or re-run this URL as a full
              audit to get AI-generated heuristic and accessibility checks.
            </p>
          </article>
        </section>
      ) : null}
    </div>
  );
}

/* ================================================================== */
/*  Shot block — image + dots + callout cards                           */
/* ================================================================== */

function ShotBlock({
  shot,
  shotIndex,
  shotsX,
  shotsY,
  shotOffsetX,
  isSolo,
  pageIndex,
  captureQuality,
  activeFindingId,
  onFindingClick,
  pinTool,
  sourceFilter,
  cardOffsets,
  onCardMove,
  stageScale,
  solvedIds,
  pinOverrides,
  onPinMove,
  canMovePins,
}: {
  shot: ShotLayout;
  shotIndex: number;
  shotsX: number;
  shotsY: number;
  shotOffsetX: number;
  isSolo: boolean;
  pageIndex: number;
  captureQuality?: CapturedPage["captureQuality"];
  activeFindingId: string | null;
  onFindingClick: (pin: AuditAnnotation) => void;
  pinTool: boolean;
  sourceFilter: SourceFilter;
  cardOffsets: Record<string, { x: number; y: number }>;
  onCardMove: (id: string, x: number, y: number) => void;
  stageScale: number;
  solvedIds: Set<string>;
  pinOverrides: Record<string, { x: number; y: number }>;
  onPinMove: (id: string, x: number, y: number) => void;
  canMovePins: boolean;
}) {
  // Tracks whether the single (unsliced) screenshot failed to load. Held
  // in React state — NOT mutated imperatively in the <img> onError — so
  // the fallback placeholder is part of the render tree React owns.
  // (The previous handler did `parent.appendChild(...)` into a
  // React-managed node, which made React's later reconciliation call
  // removeChild on a node that was no longer its child → the
  // "Failed to execute 'removeChild' on 'Node'" runtime error.)
  const [singleImgBroken, setSingleImgBroken] = useState(false);
  // Helpers for resolving the per-card pixel offset that's applied on top
  // of the auto-stacked column position. Drag handlers (in CalloutCard)
  // call back with the new offset; we read it back here so connectors
  // track the card live.
  const annoId = (a: AuditAnnotation) =>
    `${pageIndex}-${shotIndex}-${a.kind}-${a.number}`;
  const offsetFor = (a: AuditAnnotation) =>
    cardOffsets[annoId(a)] ?? { x: 0, y: 0 };
  // Effective dot center on the shot, in % (0–100). Falls back to the
  // AI-detected bbox center when the user hasn't dragged the pin.
  /** Pin center in the screenshot's 0–100 coordinate space.
   *  Returns null for general / page-level findings (sentinel bbox
   *  of zero width and height) — they don't have a specific spot
   *  to anchor to, so callers should skip drawing dots and
   *  connector lines for them instead of pinning everything to
   *  (0, 0) which made the lines look like they were going
   *  nowhere. */
  const pinCenterFor = (a: AuditAnnotation): { x: number; y: number } | null => {
    const override = pinOverrides[annoId(a)];
    if (override) return override;
    if (a.bbox.width === 0 && a.bbox.height === 0) return null;
    return {
      x: a.bbox.x + a.bbox.width / 2,
      y: a.bbox.y + a.bbox.height / 2,
    };
  };
  // Annotations that survive the source filter (hide-AI / hide-custom).
  const visibleAnnotations = shot.annotations.filter((a) =>
    sourceFilter === "all" ? true : a.kind === sourceFilter
  );
  // Layout decisions still use the unfiltered list so dot numbers don't
  // jump around when the user toggles a filter — but visible dots/cards
  // come from `visibleAnnotations`. Every visible annotation gets a dot;
  // only card-bearers (top picks per shot, plus all customs) get a callout
  // card + connector, so the canvas stays readable while every finding is
  // still locatable via its numbered dot.
  const left: AuditAnnotation[] = [];
  const right: AuditAnnotation[] = [];
  for (const a of visibleAnnotations) {
    if (!a.hasCard) continue;
    const cx = a.bbox.x + a.bbox.width / 2;
    (cx < 50 ? left : right).push(a);
  }

  const leftCards = layoutCalloutColumn(left, shot.height);
  const rightCards = layoutCalloutColumn(right, shot.height);

  const wrapperWidth = isSolo
    ? CALLOUT_CARD_WIDTH + CALLOUT_GAP + shot.width + CALLOUT_GAP + CALLOUT_CARD_WIDTH
    : shot.width;
  const innerShotX = isSolo ? CALLOUT_CARD_WIDTH + CALLOUT_GAP : 0;
  const wrapperHeight = Math.max(
    shot.height,
    leftCards.length ? leftCards[leftCards.length - 1].bottom : 0,
    rightCards.length ? rightCards[rightCards.length - 1].bottom : 0,
  );

  return (
    <div
      className="cv-shot-wrapper"
      style={{
        position: "absolute",
        left: isSolo ? shotsX + shotOffsetX - (CALLOUT_CARD_WIDTH + CALLOUT_GAP) : shotsX + shotOffsetX,
        top: shotsY,
        width: wrapperWidth,
        height: wrapperHeight + 28,
      }}
    >
      <div className="cv-shot" style={{ position: "absolute", left: innerShotX, top: 0, width: shot.width }}>
        <div className="cv-shot-label">
          {shot.shot.viewport === "desktop" ? "Desktop" : "Mobile"} · {shot.shot.width}×{shot.shot.height}
        </div>
        {(() => {
          // Web rendering preference order:
          //   1) `fullScreenshotPath` — single unsliced image (added for the
          //      web canvas; the Figma plugin keeps using `slices`).
          //   2) `screenshotPath` when no slices exist (single image already).
          //   3) Legacy multi-slice jobs: stack the slice <img>s with
          //      `height: auto` so the natural per-slice aspect adds up flush
          //      against the parent — eliminates the sub-pixel seams the
          //      previous percentage-height layout left behind.
          const slices = shot.shot.slices ?? [];
          const validSlices = slices.filter((s) => !!s.screenshotPath && s.height > 0);
          const fullPath = shot.shot.fullScreenshotPath;
          const singleImagePath =
            fullPath || (validSlices.length === 0 ? shot.shot.screenshotPath : undefined);
          const hasValidSource = !!singleImagePath || validSlices.length > 0;
          const wrapHeight = shot.height > 0 ? shot.height : 220;
          return (
            <div
              className="cv-shot-image-wrap"
              data-empty={!hasValidSource || singleImgBroken ? "true" : undefined}
              data-shot-anchor=""
              data-shot-page-index={pageIndex}
              data-shot-viewport={shot.shot.viewport}
              data-pin-tool={pinTool ? "true" : undefined}
              style={{ width: shot.width, height: wrapHeight }}
            >
              {!hasValidSource ? (
                <ShotUnavailable />
              ) : singleImagePath ? (
                singleImgBroken ? (
                  <div className="cv-shot-broken">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <p>Screenshot could not be loaded</p>
                  </div>
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    className="cv-shot-img"
                    src={singleImagePath}
                    alt={`${shot.shot.viewport} screenshot`}
                    draggable={false}
                    onError={() => {
                      // eslint-disable-next-line no-console
                      console.warn("[canvas] shot image failed to load", singleImagePath);
                      // Flip to state and let React swap in the placeholder.
                      // Never touch the DOM imperatively here — the parent is
                      // React-managed and a manual appendChild caused the
                      // removeChild reconciliation crash.
                      setSingleImgBroken(true);
                    }}
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "fill", display: "block" }}
                  />
                )
              ) : (
                <div className="cv-shot-slice-stack">
                  {validSlices.map((slice, i) => (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      key={`${slice.screenshotPath}-${i}`}
                      className="cv-shot-img"
                      src={slice.screenshotPath}
                      alt={`${shot.shot.viewport} screenshot slice ${i + 1} of ${validSlices.length}`}
                      draggable={false}
                      onError={(e) => {
                        // eslint-disable-next-line no-console
                        console.warn("[canvas] slice image failed to load", slice.screenshotPath);
                        (e.currentTarget as HTMLImageElement).dataset.broken = "true";
                      }}
                      style={{ display: "block", width: "100%", height: "auto" }}
                    />
                  ))}
                </div>
              )}

              {/* Failed-capture overlay — a blank/blocked/404 shot on the
                  canvas used to be indistinguishable from "the site is
                  just white": the only cue was a hover tooltip in the
                  Pages rail. Badge the screenshot itself with the status
                  + reason so a failed column explains itself at a glance.
                  Sized relative to the shot (the canvas transform scales
                  everything), anchored near the top so it stays visible
                  on very tall full-page captures, pointer-events: none so
                  pin placement and drag still reach the shot beneath. */}
              {captureQuality && captureQuality.scorable === false ? (
                (() => {
                  const CAPTURE_BADGE: Record<
                    string,
                    { label: string; fallback: string }
                  > = {
                    blocked: {
                      label: "Blocked by bot protection",
                      fallback: "The site turned our capture away before the page loaded.",
                    },
                    // 5xx "blocked" — the site's own server was down or
                    // restarting, not a wall. Saying "bot protection" here
                    // was a shipped mislabel (Render free-tier cold start).
                    server: {
                      label: "Server didn't respond",
                      fallback:
                        "The server returned a temporary error while we captured this page, even after retries.",
                    },
                    blank: {
                      label: "Page didn't render",
                      fallback: "No content painted while we captured this page.",
                    },
                    notfound: {
                      label: "Page not found",
                      fallback: "This URL returned an error or no longer exists.",
                    },
                  };
                  const badge = isServerDownQuality(captureQuality)
                    ? CAPTURE_BADGE.server
                    : (CAPTURE_BADGE[captureQuality.status] ??
                      CAPTURE_BADGE.blank);
                  const titlePx = Math.max(16, Math.round(shot.width * 0.026));
                  const reasonPx = Math.max(12, Math.round(shot.width * 0.019));
                  const iconPx = Math.round(titlePx * 1.55);
                  return (
                    <div className="cv-shot-capture-overlay" aria-hidden="true">
                      <div
                        className="cv-shot-capture-badge"
                        data-status={captureQuality.status}
                        style={{
                          top: Math.min(Math.round(shot.height / 2), 460),
                          maxWidth: Math.round(shot.width * 0.78),
                          padding: `${Math.round(titlePx * 0.9)}px ${Math.round(titlePx * 1.2)}px`,
                        }}
                      >
                        <span
                          className="cv-shot-capture-badge-icon"
                          style={{ width: iconPx, height: iconPx }}
                        >
                          <AlertTriangleIcon />
                        </span>
                        <span className="cv-shot-capture-badge-text">
                          <strong style={{ fontSize: titlePx }}>{badge.label}</strong>
                          <span style={{ fontSize: reasonPx }}>
                            {captureQuality.reason ?? badge.fallback}
                          </span>
                        </span>
                      </div>
                    </div>
                  );
                })()
              ) : null}

              {/* Numbered dots — draggable for owners. The dot's position
                  is the override (if dragged) or the AI-detected bbox
                  center otherwise. General findings (no specific spot)
                  return null from pinCenterFor and skip the dot
                  entirely — their callout card surfaces separately
                  under a "Page-level" header below. */}
              {visibleAnnotations.map((a) => {
                const id = annoId(a);
                const center = pinCenterFor(a);
                if (!center) return null;
                return (
                  <Dot
                    key={`${shotIndex}-${a.kind}-${a.number}`}
                    annotation={a}
                    cx={center.x}
                    cy={center.y}
                    isActive={activeFindingId === id}
                    isSolved={solvedIds.has(id)}
                    canMove={canMovePins}
                    stageScale={stageScale}
                    shotWidthPx={shot.width}
                    shotHeightPx={shot.height}
                    onClick={() => onFindingClick(a)}
                    onMove={(x, y) => onPinMove(id, x, y)}
                  />
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* SVG connectors — overflow visible so the line still draws when
          the user drags a card outside the wrapper bounds. */}
      {isSolo && (leftCards.length > 0 || rightCards.length > 0) ? (
        <svg
          className="cv-connectors"
          width={wrapperWidth}
          height={wrapperHeight + 28}
          viewBox={`0 0 ${wrapperWidth} ${wrapperHeight + 28}`}
          style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "visible" }}
        >
          {leftCards.map(({ a, top }, i) => {
            const center = pinCenterFor(a);
            // No pin = general finding → no line. The card is still
            // rendered separately so the user can read the note, but
            // we don't draw a connector to nowhere.
            if (!center) return null;
            const off = offsetFor(a);
            const startX = CALLOUT_CARD_WIDTH + off.x;
            const cardCenter = top + off.y + estimateCardHeight(a) / 2;
            const dotX = innerShotX + (center.x / 100) * shot.width;
            const imgY = (shot.width * shot.shot.height) / shot.shot.width;
            const dotY = 28 + (center.y / 100) * imgY;
            const isSolved = solvedIds.has(annoId(a));
            return (
              <line key={`L${i}`} x1={startX} y1={cardCenter} x2={dotX} y2={dotY}
                stroke={isSolved ? "#9ca3b0" : SEVERITY_COLOR[a.severity]}
                strokeWidth={2} strokeLinecap="round"
                opacity={isSolved ? 0.5 : 1} />
            );
          })}
          {rightCards.map(({ a, top }, i) => {
            const center = pinCenterFor(a);
            if (!center) return null;
            const off = offsetFor(a);
            const cardX = innerShotX + shot.width + CALLOUT_GAP + off.x;
            const cardCenter = top + off.y + estimateCardHeight(a) / 2;
            const dotX = innerShotX + (center.x / 100) * shot.width;
            const imgY = (shot.width * shot.shot.height) / shot.shot.width;
            const dotY = 28 + (center.y / 100) * imgY;
            const isSolved = solvedIds.has(annoId(a));
            return (
              <line key={`R${i}`} x1={cardX} y1={cardCenter} x2={dotX} y2={dotY}
                stroke={isSolved ? "#9ca3b0" : SEVERITY_COLOR[a.severity]}
                strokeWidth={2} strokeLinecap="round"
                opacity={isSolved ? 0.5 : 1} />
            );
          })}
        </svg>
      ) : null}

      {/* Left callout cards */}
      {isSolo && leftCards.map(({ a, top }, i) => {
        const id = annoId(a);
        return (
          <CalloutCard key={`Lc${i}`} annotation={a} pageIndex={pageIndex} shotIndex={shotIndex}
            isActive={activeFindingId === id}
            isSolved={solvedIds.has(id)}
            onClick={() => onFindingClick(a)}
            style={{ position: "absolute", left: 0, top, width: CALLOUT_CARD_WIDTH }}
            offset={offsetFor(a)}
            onMove={(x, y) => onCardMove(id, x, y)}
            stageScale={stageScale}
          />
        );
      })}
      {/* Right callout cards */}
      {isSolo && rightCards.map(({ a, top }, i) => {
        const id = annoId(a);
        return (
          <CalloutCard key={`Rc${i}`} annotation={a} pageIndex={pageIndex} shotIndex={shotIndex}
            isActive={activeFindingId === id}
            isSolved={solvedIds.has(id)}
            onClick={() => onFindingClick(a)}
            style={{ position: "absolute", left: innerShotX + shot.width + CALLOUT_GAP, top, width: CALLOUT_CARD_WIDTH }}
            offset={offsetFor(a)}
            onMove={(x, y) => onCardMove(id, x, y)}
            stageScale={stageScale}
          />
        );
      })}
    </div>
  );
}

/* ================================================================== */
/*  Numbered dot — draggable pin on the screenshot                      */
/* ================================================================== */

function Dot({
  annotation,
  cx,
  cy,
  isActive,
  isSolved,
  canMove,
  stageScale,
  shotWidthPx,
  shotHeightPx,
  onClick,
  onMove,
}: {
  annotation: AuditAnnotation;
  cx: number; // % of shot width
  cy: number; // % of shot height
  isActive: boolean;
  isSolved: boolean;
  canMove: boolean;
  stageScale: number;
  shotWidthPx: number;
  shotHeightPx: number;
  onClick: () => void;
  onMove: (x: number, y: number) => void;
}) {
  const dragRef = useRef<{
    startClientX: number;
    startClientY: number;
    baseCx: number;
    baseCy: number;
    moved: boolean;
  } | null>(null);
  const [dragging, setDragging] = useState(false);

  const handlePointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    if (!canMove) return; // viewer mode → click-only
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      baseCx: cx,
      baseCy: cy,
      moved: false,
    };
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dxScreen = e.clientX - drag.startClientX;
    const dyScreen = e.clientY - drag.startClientY;
    if (!drag.moved && Math.hypot(dxScreen, dyScreen) > 4) {
      drag.moved = true;
      setDragging(true);
    }
    if (drag.moved) {
      // Convert screen-pixel mouse delta into shot-percentage delta:
      // (mouse / stageScale) gives canvas px; / shotPx * 100 gives %.
      const scale = Math.max(0.0001, stageScale);
      const dxPct = (dxScreen / scale / shotWidthPx) * 100;
      const dyPct = (dyScreen / scale / shotHeightPx) * 100;
      const nextCx = Math.max(0, Math.min(100, drag.baseCx + dxPct));
      const nextCy = Math.max(0, Math.min(100, drag.baseCy + dyPct));
      onMove(nextCx, nextCy);
    }
  };

  const handlePointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    if (drag.moved) {
      // Suppress the click that fires after pointerup.
      setTimeout(() => setDragging(false), 0);
    } else {
      setDragging(false);
    }
  };

  return (
    <button
      type="button"
      data-canvas-interactive
      data-dragging={dragging || undefined}
      className={
        `cv-dot${isActive ? " active" : ""}` +
        `${annotation.kind === "custom" ? " cv-dot-custom" : ""}` +
        `${isSolved ? " cv-dot-solved" : ""}` +
        `${canMove ? " cv-dot-movable" : ""}`
      }
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClick={(e) => {
        if (dragging) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick();
      }}
      aria-label={`Finding ${annotation.label}: ${annotation.locked ? "locked — sign in to unlock" : annotation.title}${isSolved ? " (solved)" : ""}`}
      data-tip={
        `${annotation.label} · ${annotation.locked ? "Sign in to unlock" : annotation.title}` +
        `${annotation.kind === "custom" ? " (custom)" : ""}` +
        `${isSolved ? " · solved" : ""}` +
        `${canMove ? " · drag to reposition" : ""}`
      }
      style={{
        left: `${cx}%`,
        top: `${cy}%`,
        background: isSolved ? "#9ca3b0" : SEVERITY_COLOR[annotation.severity],
        height: DOT_SIZE,
        // Pill width is content-driven (CSS `min-width: DOT_SIZE`);
        // translate(-50%, -50%) centres it on the anchor regardless
        // of how wide the typed label makes it.
      }}
    >
      {annotation.label}
    </button>
  );
}

/* ================================================================== */
/*  Callout card — floating card beside screenshot                      */
/* ================================================================== */

function CalloutCard({
  annotation,
  isActive,
  isSolved = false,
  onClick,
  style,
  offset,
  onMove,
  stageScale,
}: {
  annotation: AuditAnnotation;
  pageIndex: number;
  shotIndex: number;
  isActive: boolean;
  isSolved?: boolean;
  onClick: () => void;
  style: React.CSSProperties;
  offset: { x: number; y: number };
  onMove: (x: number, y: number) => void;
  stageScale: number;
}) {
  // Solved findings get a neutral grey treatment so the canvas reads
  // the same way the sidebar row does (faded, struck-through title).
  const color = isSolved ? "#9ca3b0" : SEVERITY_COLOR[annotation.severity];
  // General / page-level findings have a zero-width sentinel bbox.
  // They surface as callout cards but no dot or connector line, so
  // we mark the card itself as page-level — dashed left border +
  // "Whole page" pill — to signal "this doesn't point at one
  // spot" rather than reading as a broken card with a missing line.
  const isGeneral = annotation.bbox.width === 0 && annotation.bbox.height === 0;
  const dragRef = useRef<{
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
    moved: boolean;
  } | null>(null);
  const [dragging, setDragging] = useState(false);

  const handlePointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    // Lock pointer to this card so drag continues even when the mouse
    // leaves it. Mouse movement happens in screen pixels, but the card
    // lives inside cv-stage-inner which is scaled — divide by stageScale
    // so 1px of mouse motion equals 1px of card movement on the canvas
    // at any zoom level.
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: offset.x,
      baseY: offset.y,
      moved: false,
    };
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = (e.clientX - drag.startX) / Math.max(0.0001, stageScale);
    const dy = (e.clientY - drag.startY) / Math.max(0.0001, stageScale);
    if (!drag.moved && Math.hypot(dx * stageScale, dy * stageScale) > 4) {
      drag.moved = true;
      setDragging(true);
    }
    if (drag.moved) {
      onMove(drag.baseX + dx, drag.baseY + dy);
    }
  };

  const handlePointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    if (drag.moved) {
      // Suppress the click that would otherwise follow this pointerup.
      // We rely on the onClick check below to actually swallow it.
      setTimeout(() => setDragging(false), 0);
    } else {
      setDragging(false);
    }
  };

  return (
    <button
      type="button"
      data-canvas-interactive
      data-active={isActive}
      data-dragging={dragging || undefined}
      data-solved={isSolved || undefined}
      data-general={isGeneral || undefined}
      className={`cv-callout${isSolved ? " is-solved" : ""}${isGeneral ? " is-general" : ""}`}
      style={{
        ...style,
        borderColor: color,
        transform: `translate(${offset.x}px, ${offset.y}px)`,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClick={(e) => {
        if (dragging) {
          // The drag finished < 1 frame ago — swallow the click that
          // would otherwise fly the canvas to this finding.
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick();
      }}
    >
      <div className="cv-callout-head">
        {/* Number badge becomes a "page" glyph for general findings so
            users immediately read the card as page-level, not
            "finding #N that's missing its line." Same severity
            color so the visual vocabulary still ties together. */}
        {isGeneral ? (
          <span
            className="cv-callout-num cv-callout-num-page"
            style={{ background: color }}
            aria-label="Page-level finding"
            data-tip="Applies to the whole page — not tied to a specific spot"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
              <polyline points="14 3 14 8 19 8" />
            </svg>
          </span>
        ) : (
          <span className="cv-callout-num" style={{ background: color }}>{annotation.label}</span>
        )}
        <span className="cv-callout-title">
          {annotation.locked ? (
            <span className="cv-title-locked">
              {annotation.title ? (
                <span className="cv-title-words">{annotation.title}</span>
              ) : null}
              <span className="cv-title-redact" />
            </span>
          ) : (
            annotation.title
          )}
        </span>
        {isGeneral ? (
          <span
            className="cv-callout-page-tag"
            aria-label="Applies to the whole page" data-tip="Applies to the whole page"
          >
            Whole page
          </span>
        ) : null}
        {annotation.kind === "custom" ? (
          <span
            className="cv-callout-yours"
            aria-label="You added this annotation" data-tip="You added this annotation"
          >
            Yours
          </span>
        ) : null}
        <BookmarkIcon className="cv-callout-bookmark" />
      </div>
      {/* Severity - Source label — matches the right-rail card so the
          canvas annotation reads the same way at a glance. The whole
          tag inherits the severity colour via inline style; children
          inherit it. When solved, swap to "Solved" with the
          neutral grey tone applied to the entire callout. */}
      <span className="cv-callout-tag" style={{ color }}>
        {isSolved ? (
          <span className="cv-callout-tag-sev">Solved</span>
        ) : (
          <>
            <span className="cv-callout-tag-sev">
              {SEVERITY_LABEL[annotation.severity]}
            </span>
            <span className="cv-callout-tag-sep" aria-hidden="true">-</span>
            <span
              className="cv-callout-tag-source"
              data-source={sourceSlugFor(sourceLabelFor(annotation))}
            >
              {sourceLabelFor(annotation)}
            </span>
          </>
        )}
      </span>
      {annotation.locked ? (
        <div className="cv-callout-body cv-callout-locked" aria-hidden="true">
          <span className="cv-callout-locked-bar" />
          <span className="cv-callout-locked-bar" />
          <span className="cv-callout-locked-bar short" />
        </div>
      ) : (
        <p className="cv-callout-body">{annotation.observation}</p>
      )}
      <span className="cv-callout-link">
        {annotation.locked ? (
          <>
            <LockIcon /> Sign in to unlock
          </>
        ) : (
          "View finding →"
        )}
      </span>
    </button>
  );
}

/* ================================================================== */
/*  UX Analysis panel — beneath shot                                    */
/* ================================================================== */

function UxAnalysisPanel({
  page,
  top,
  left,
  width,
}: {
  page: CapturedPage;
  top: number;
  left: number;
  width: number;
}) {
  const heuristics = (page.heuristicFindings ?? []).slice().sort((a, b) => {
    const d = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    return d !== 0 ? d : a.heuristicNumber - b.heuristicNumber;
  });
  const a11y = (page.accessibilityFindings ?? []).slice().sort((a, b) => {
    const d = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    return d !== 0 ? d : a.wcagId.localeCompare(b.wcagId, undefined, { numeric: true });
  });
  const bestPractices = (page.bestPracticeFindings ?? []).slice().sort((a, b) => {
    const d = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    return d !== 0
      ? d
      : a.practiceId.localeCompare(b.practiceId, undefined, { numeric: true });
  });

  // Build a {source-finding → pin label} map by running the same
  // annotation/labelling pass the canvas uses, then keying by the
  // source object identity. Findings without a bbox don't get a pin
  // label — they show a GENERAL badge in the card instead.
  const pinLabels = useMemo(() => buildPinLabels(page), [page]);

  // Note: no per-card severity filter. The right-rail already
  // filters globally by severity for the active task on the canvas;
  // duplicating that here would mean two competing controls with
  // overlapping purpose. The analysis cards are the "full report"
  // surface — they show everything that was found, no editorial
  // filtering by the reader.

  return (
    <section className="cv-analysis" style={{ position: "absolute", left, top, width }}>
      {/* The "Audit summary" + "Top quick wins" card used to live
          here. Removed: the summary restated what the left-rail
          score block and the right-rail finding list already say
          ("N findings, M critical"), and the quick-wins list was
          just the top-3 observations re-printed without a link
          back to their canvas pins. Both were noise. The two
          finding cards below carry the substance now. */}

      {/* Detailed findings — same card template for both types so the
          reader's eye doesn't have to re-learn the layout halfway
          across. Always render BOTH cards (even when empty) so the
          structure of the audit is visible — an empty card with a
          "Nothing flagged" placeholder reads as "we ran this check
          and found nothing", not "we didn't run this check".

          Common AI-assisted disclaimer lifted above both cards so
          the same sentence doesn't repeat in two places. Only the
          a11y-specific compliance tail stays inside the
          accessibility card — that's the part that actually differs. */}
      <header className="cv-analysis-head">
        {/* Name the page these findings belong to — an audit can span
            several pages, each with its own analysis panel, so a bare
            "All findings" left the reader guessing which one. Echoes the
            page's own header label (name, else host + path). */}
        <h3 className="cv-analysis-title">
          All findings
          <span className="cv-analysis-title-page">
            {"· "}
            {page.name?.trim() || formatPageUrlShort(page.url)}
          </span>
        </h3>
        <p className="cv-analysis-disclaimer" role="note">
          AI-assisted starting point &mdash; verify findings before acting.
        </p>
      </header>
      <div className="cv-analysis-row">
        <AuditFindingsCard
          eyebrow="Usability heuristics"
          disclaimer={null}
          findings={heuristics}
          renderTitle={(f) => f.title?.trim() || f.heuristicName}
          renderSpec={(f) =>
            `Usability heuristic ${f.heuristicNumber} · ${f.heuristicName}`
          }
          findingKey={(f, i) => `h-${i}`}
          getLabel={(f) => pinLabels.get(f) ?? null}
          getSeverity={(f) => f.severity}
          getLocked={(f) => !!f.locked}
        />
        <AuditFindingsCard
          eyebrow="Accessibility (WCAG 2.2)"
          disclaimer="Not a substitute for compliance review."
          findings={a11y}
          renderTitle={(f) => f.title?.trim() || f.wcagName}
          renderSpec={(f) => `WCAG ${f.wcagId} · ${f.wcagName}`}
          findingKey={(f, i) => `a-${i}`}
          getLabel={(f) => pinLabels.get(f) ?? null}
          getSeverity={(f) => f.severity}
          getLocked={(f) => !!f.locked}
        />
        <AuditFindingsCard
          eyebrow="UX best practices"
          disclaimer={null}
          findings={bestPractices}
          renderTitle={(f) => f.title?.trim() || f.practiceName}
          renderSpec={(f) => `${f.category} · ${f.practiceName}`}
          findingKey={(f, i) => `b-${i}`}
          getLabel={(f) => pinLabels.get(f) ?? null}
          getSeverity={(f) => f.severity}
          getLocked={(f) => !!f.locked}
        />
      </div>
    </section>
  );
}

/**
 * AuditFindingsCard — single template used for both heuristics and
 * accessibility on the bottom analysis panel. Keeping the rendering
 * uniform (pin label + AI title up top, spec ID + severity in the
 * metadata footer) means the reader's eye doesn't have to re-learn
 * the layout between the two cards.
 *
 * Pin labels are passed in via `getLabel`; if a finding has no pin
 * (page-level / no bbox), the leading badge shows a small PAGE
 * indicator instead. This is the key affordance the previous version
 * was missing — readers couldn't trace a card back to its dot on
 * the screenshot.
 */
function AuditFindingsCard<F extends { observation: string }>({
  eyebrow,
  disclaimer,
  findings,
  renderTitle,
  renderSpec,
  findingKey,
  getLabel,
  getSeverity,
  getLocked,
}: {
  eyebrow: string;
  /** Per-card disclaimer tail. Pass null when the common
   *  AI-assisted line is lifted above both cards and this card has
   *  nothing category-specific to add. */
  disclaimer: string | null;
  findings: F[];
  renderTitle: (f: F) => string;
  renderSpec: (f: F) => string;
  findingKey: (f: F, i: number) => string;
  getLabel: (f: F) => string | null;
  getSeverity: (f: F) => HeuristicSeverity;
  /** Guest teaser: true for findings whose text was stripped server-side.
      Their title + body render as a locked placeholder. */
  getLocked?: (f: F) => boolean;
}) {
  // Most-severe first so the reader triages top-down.
  const sorted = [...findings].sort(
    (a, b) => SEVERITY_RANK[getSeverity(a)] - SEVERITY_RANK[getSeverity(b)]
  );

  return (
    <article className="cv-audit-card">
      <header>
        <p className="cv-audit-eyebrow">{eyebrow}</p>
        {/* Count up front (and the category-specific disclaimer, when any)
            so the reader knows the size of each list at a glance. */}
        <p className="cv-audit-subhead">
          {findings.length} {findings.length === 1 ? "finding" : "findings"}
          {disclaimer ? ` · ${disclaimer}` : ""}
        </p>
      </header>
      {findings.length === 0 ? (
        <p className="cv-audit-empty" role="status">
          Nothing flagged. Verify with a manual pass.
        </p>
      ) : (
        <ul>
          {sorted.map((f, i) => {
            const sev = getSeverity(f);
            const label = getLabel(f);
            return (
              // data-sev drives the thin left edge (one quiet, scannable
              // severity signal); the chip below is the explicit one.
              <li key={findingKey(f, i)} data-sev={sev}>
                <div className="cv-audit-head">
                  {/* Location reference back to the canvas pin — a NEUTRAL
                      badge, so it stops doubling as a severity color.
                      "GENERAL" = spans elements / layout-level. */}
                  {label ? (
                    <span className="cv-audit-num" aria-label={`Finding ${label}`}>
                      {label}
                    </span>
                  ) : (
                    <span
                      className="cv-audit-num cv-audit-num-page"
                      aria-label="General finding — no single spot on the screenshot"
                      data-tip="General finding — no single spot on the screenshot"
                    >
                      GENERAL
                    </span>
                  )}
                  <p className="cv-audit-title">
                    {getLocked?.(f) ? (
                      <span className="cv-title-locked">
                        {renderTitle(f) ? (
                          <span className="cv-title-words">{renderTitle(f)}</span>
                        ) : null}
                        <span className="cv-title-redact" />
                      </span>
                    ) : (
                      renderTitle(f)
                    )}
                  </p>
                  {/* The single, explicit severity signal. */}
                  <span className="cv-audit-chip" data-sev={sev}>
                    {SEVERITY_LABEL[sev]}
                  </span>
                </div>
                {getLocked?.(f) ? (
                  <div className="cv-audit-body cv-audit-locked" aria-hidden="true">
                    <span className="cv-audit-locked-bar" />
                    <span className="cv-audit-locked-bar short" />
                  </div>
                ) : (
                  <p className="cv-audit-body">{f.observation}</p>
                )}
                <p className="cv-audit-ref">{renderSpec(f)}</p>
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}

/**
 * buildPinLabels — derives the same {finding → "H1"/"A2"/...} mapping
 * the canvas uses for its pin badges, so the bottom analysis cards
 * can show a matching reference. Runs `pickAnnotations` (which assigns
 * the typed labels) and indexes the result by the finding's source
 * identity.
 *
 * Findings without a bbox don't get a pin and are absent from the
 * returned map; the AuditFindingsCard renders a PAGE badge for them.
 */
function buildPinLabels(
  page: CapturedPage,
): Map<HeuristicFinding | AccessibilityFinding | BestPracticeFinding, string> {
  // Derive a "primary" viewport from the page's shots — preferring
  // desktop when present, matching collectShotAnnotations' rule.
  const shotViewports = page.shots.map((s) => s.viewport);
  const primary: Viewport = shotViewports.includes("desktop") ? "desktop" : "mobile";
  const annotations: AuditAnnotation[] = [];
  for (const f of page.heuristicFindings ?? []) {
    if (!f.bbox) continue;
    annotations.push(heuristicToAnnotation(f, primary));
  }
  for (const f of page.accessibilityFindings ?? []) {
    if (!f.bbox) continue;
    annotations.push(accessibilityToAnnotation(f, primary));
  }
  for (const f of page.bestPracticeFindings ?? []) {
    if (!f.bbox) continue;
    annotations.push(bestPracticeToAnnotation(f, primary));
  }
  pickAnnotations(annotations);
  const map = new Map<
    HeuristicFinding | AccessibilityFinding | BestPracticeFinding,
    string
  >();
  for (const a of annotations) {
    // `source` is one of the three finding types | CustomAnnotationApi — only
    // the finding types entered this function's loop, so the cast is safe.
    map.set(
      a.source as HeuristicFinding | AccessibilityFinding | BestPracticeFinding,
      a.label,
    );
  }
  return map;
}

/* ================================================================== */
/*  Summary block                                                       */
/* ================================================================== */

function CanvasSummaryBlock({
  result,
  layoutSummaryX,
  fixFirst,
  pageNameByIndex,
  showSubscores,
}: {
  result: AnalyzeResult;
  layoutSummaryX: number;
  /** Top open findings by score impact + the projected score if they're
   *  fixed (computed by the parent from the live model + solved state).
   *  Null when there's nothing open or no meaningful gain. */
  fixFirst: { items: FindingEntry[]; projected: number } | null;
  pageNameByIndex: Record<number, string>;
  // The four-dimension subscore grid is redundant when the left rail
  // is open (its "More details" disclosure carries the same numbers).
  // So the card only renders the grid when the rail isn't there to
  // show it: when the rail is collapsed, or during PDF export (which
  // captures the canvas without the rail). The score + grade headline
  // always stays as the card's self-contained "cover".
  showSubscores: boolean;
}) {
  const allPagesForCounts = [result.homepage, ...(result.pages ?? [])];
  // Pre-compute per-source totals so the Findings stat tile can
  // show the NN/g vs Accessibility split inline. Custom annotations
  // aren't part of the canvas summary (they're a user-added layer,
  // not part of the AI's audit), so they're not counted here — the
  // left/right rail counts include them, this one is "what the AI
  // produced".
  const heuristicTotal = allPagesForCounts.reduce(
    (sum, p) => sum + (p?.heuristicFindings?.length ?? 0),
    0,
  );
  const accessibilityTotal = allPagesForCounts.reduce(
    (sum, p) => sum + (p?.accessibilityFindings?.length ?? 0),
    0,
  );
  const bestPracticeTotal = allPagesForCounts.reduce(
    (sum, p) => sum + (p?.bestPracticeFindings?.length ?? 0),
    0,
  );
  // The "findings" tally counts the pinned, severity-graded findings
  // only (Usability / Accessibility / Best practices) — the same set
  // the left rail and the findings list count. The First-impression
  // issue isn't pinned or severity-graded (it's surfaced in the First
  // Impression panel and its own subscore), so it's deliberately left
  // out of the tally so every count across the rail and card agrees on
  // the same number rather than the card reading one higher.
  const listedFindings =
    heuristicTotal + accessibilityTotal + bestPracticeTotal;
  const score = computeScore(result);
  const palette = result.tokens?.palette ?? [];
  const fonts = filterDisplayFonts(result.tokens?.fonts ?? []);
  const host = hostnameOf(result.sourceUrl);
  // Lead-with-score restructure: the headline number + its grade
  // band open the card, pages/findings drop to a metadata line, and
  // all four weighted dimensions render together as subscores.
  const grade = score?.overall != null ? scoreToGrade(score.overall) : null;
  // The AI review is best-effort on the backend. Keep missing-review fallback
  // text visibly separate so a generic score summary never reads like the AI's
  // editorial review.
  const aiReviewText = result.review?.trim() || null;
  const scoreSummaryText =
    !aiReviewText && grade ? GRADE_SUMMARY[grade.tone] : null;
  const pageCount = 1 + (result.pages?.length ?? 0);
  // Pages excluded from the score (blocked/blank captures — notfound pages
  // still count, they carry the broken-link finding). The card is also the
  // PDF-export cover, so the meta line must not read "2 pages" as if both
  // fed the score when one never captured.
  const excludedFromScore = allPagesForCounts.filter(
    (p) =>
      p?.captureQuality?.scorable === false &&
      p.captureQuality.status !== "notfound",
  );
  const scoredPageCount = pageCount - excludedFromScore.length;
  const subscoreRows = score
    ? [
        { letter: "U", label: "Usability", data: score.ux },
        { letter: "A", label: "Accessibility", data: score.a11y },
        { letter: "B", label: "Best practices", data: score.bestPractices },
        ...(score.presence
          ? [{ letter: "F", label: "First impression", data: score.presence }]
          : []),
      ]
    : [];

  // A run where NO page captured has nothing score-shaped to show. This
  // card used to render its normal scaffolding anyway — "– / 100 ·
  // 1 page · 0 findings" — which at a glance reads as a CLEAN audit, the
  // exact opposite of what happened. Replace the whole body with an
  // explicit couldn't-capture state (neutral copy: the card is shared
  // and exported, so no owner-only refund claims here — the rail and
  // modal carry those).
  const allCapturesFailed =
    allPagesForCounts.length > 0 &&
    allPagesForCounts.every((p) => p?.captureQuality?.scorable === false);
  if (allCapturesFailed) {
    const counts = new Map<string, number>();
    for (const p of allPagesForCounts) {
      const s = p?.captureQuality?.status;
      if (s) counts.set(s, (counts.get(s) ?? 0) + 1);
    }
    let dominant = "blank";
    let max = 0;
    for (const [s, n] of counts) if (n > max) { max = n; dominant = s; }
    const FAIL_COPY: Record<string, { title: string; detail: string }> = {
      blocked: {
        title: "This site has bot protection",
        detail:
          "It blocks automated visitors, so our capture couldn't open the page — that's why there are no findings yet. It loads fine in a normal browser, so you can still audit it from a screenshot of the page.",
      },
      // 5xx from the site's own server — transient, unlike a bot wall.
      // Plain fact first, no presumed cause, note that we already retried,
      // clear next step. (Same copy as the rail card's server entry.)
      server: {
        title: "The site's server didn't respond",
        detail:
          "The server returned a temporary 'unavailable' response instead of the page, even after we retried for about a minute — most likely it was waking up or mid-restart. Give it a minute and run the audit again.",
      },
      blank: {
        title: "This page didn't load for our capture",
        detail:
          "Nothing rendered for our capture, so there are no findings yet. It may just need a real browser — you can still audit it from a screenshot of the page.",
      },
      notfound: {
        title: "We couldn't reach this page",
        detail:
          "The page returned an error or doesn't exist, so there was nothing to audit yet. Double-check the link, or audit a screenshot of the page you meant.",
      },
    };
    // A blocked run where every blocked page traces to a 5xx is a napping
    // server, not a wall — pick the copy that tells the user the truth.
    const dominantBlockedPages = allPagesForCounts.filter(
      (p) => p?.captureQuality?.status === "blocked",
    );
    const dominantServerDown =
      dominant === "blocked" &&
      dominantBlockedPages.length > 0 &&
      dominantBlockedPages.every((p) => isServerDownQuality(p?.captureQuality));
    const failCopy =
      (dominantServerDown ? FAIL_COPY.server : FAIL_COPY[dominant]) ??
      FAIL_COPY.blank;
    // Failed captures never got a page title, so siteName is often the
    // raw source URL — which made the h2 a giant duplicate of the URL
    // line under it. Fall back to the hostname in that case. Scoped to
    // this branch: normal runs have real captured titles.
    const failedDisplayName =
      result.siteName &&
      result.siteName !== result.sourceUrl &&
      !/^https?:\/\//i.test(result.siteName)
        ? result.siteName
        : hostnameOf(result.sourceUrl);
    return (
      <div className="cv-summary" style={{ position: "absolute", left: layoutSummaryX, top: PAGE_PAD, width: SUMMARY_WIDTH }}>
        <header className="cv-page-header">
          <p className="cv-page-eyebrow">Summary</p>
          <h2>{failedDisplayName}</h2>
          <p className="cv-page-header-url">
            {`${result.sourceUrl} · ${new Date(result.createdAt).toLocaleDateString()}`}
          </p>
        </header>
        {/* Centered empty-state — replaces the old hairline box floating
            inside the card (double border, dead space, stranded amber
            meta line). Icon badge → title → detail → coverage chip, all
            on the card's own surface. Shared with /s/ share pages and
            the PDF cover, so copy stays neutral (no refund claims). */}
        <article className="cv-summary-card cv-summary-card-capture">
          <div className="cv-summary-capture" role="status">
            <span className="cv-summary-capture-icon" aria-hidden="true">
              <AlertTriangleIcon />
            </span>
            <strong className="cv-summary-capture-title">{failCopy.title}</strong>
            <p className="cv-summary-capture-detail">{failCopy.detail}</p>
            <span className="cv-summary-capture-coverage">
              <AlertTriangleIcon />
              0 of {pageCount} {pageCount === 1 ? "page" : "pages"} captured
            </span>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="cv-summary" style={{ position: "absolute", left: layoutSummaryX, top: PAGE_PAD, width: SUMMARY_WIDTH }}>
      <header className="cv-page-header">
        <p className="cv-page-eyebrow">Summary</p>
        <h2>{result.siteName || hostnameOf(result.sourceUrl)}</h2>
        <p className="cv-page-header-url">
          {result.runMode === "imageAudit"
            ? `Uploaded image · ${new Date(result.createdAt).toLocaleDateString()}`
            : `${result.sourceUrl} · ${new Date(result.createdAt).toLocaleDateString()}`}
        </p>
      </header>
      <article className="cv-summary-card">
        {/* Hero — score + grade lead the card. */}
        <div className="cv-summary-hero">
          <ScoreRing score={score?.overall ?? null} tone={grade?.tone} size={96} />
          <div className="cv-summary-hero-text">
            <div className="cv-summary-hero-title">
              <h3>Snap Site Score</h3>
            </div>
            {grade ? (
              <span className="cv-summary-grade" data-tone={grade.tone}>
                {grade.label}
              </span>
            ) : null}
            <span className="cv-summary-hero-of">
              {score?.overall ?? "–"} <span aria-hidden="true">/ 100</span>
            </span>
          </div>
        </div>
        {aiReviewText ? (
          <div className="cv-summary-review-block" data-source="ai">
            <p className="cv-summary-block-label">AI overall review</p>
            <p className="cv-summary-review">{aiReviewText}</p>
          </div>
        ) : scoreSummaryText ? (
          <div className="cv-summary-review-block" data-source="fallback">
            <p className="cv-summary-block-label">Score summary</p>
            <p className="cv-summary-review">{scoreSummaryText}</p>
          </div>
        ) : null}
        {/* Pages + findings — demoted from stat tiles to a quiet
            metadata line. The per-category finding split now lives in
            the subscores below (each row's "from N findings"), so the
            old "By category" line is dropped to avoid repeating it. */}
        <p className="cv-summary-meta">
          {excludedFromScore.length > 0 && scoredPageCount > 0 ? (
            <span className="cv-summary-meta-item cv-summary-meta-coverage">
              <AlertTriangleIcon />
              Scored from {scoredPageCount} of {pageCount} pages —{" "}
              {excludedFromScore.length === 1
                ? `${excludedFromScore[0]?.name ?? "one page"} couldn't be captured`
                : `${excludedFromScore.length} couldn't be captured`}
            </span>
          ) : (
            <span className="cv-summary-meta-item">
              {pageCount} {pageCount === 1 ? "page" : "pages"}
            </span>
          )}
          <span className="cv-summary-meta-sep" aria-hidden="true">·</span>
          <span className="cv-summary-meta-item">
            {listedFindings} {listedFindings === 1 ? "finding" : "findings"}
          </span>
        </p>
        {/* Fix these first — the score's actionable counterpart. The
            projected number comes from the real model (parent computes it
            from per-page penalties + the grade cap), so it's a promise the
            product can keep: fix, re-run, watch the number move. */}
        {fixFirst && score?.overall != null ? (
          <div className="cv-summary-block cv-summary-fixfirst">
            <p className="cv-summary-block-label">Fix these first</p>
            <ol className="cv-summary-fixfirst-list">
              {fixFirst.items.map((e) => (
                <li key={e.id}>
                  <span
                    className="cv-summary-fixfirst-dot"
                    data-severity={e.annotation.severity}
                    aria-hidden="true"
                  />
                  <span className="cv-summary-fixfirst-title">
                    {e.annotation.title || `Finding ${e.annotation.number}`}
                  </span>
                  <span className="cv-summary-fixfirst-page">
                    {pageNameByIndex[e.pageIndex] ?? `Page ${e.pageIndex + 1}`}
                  </span>
                </li>
              ))}
            </ol>
            <p className="cv-summary-fixfirst-gain">
              Fixing these takes the score {score.overall} →{" "}
              <strong>{fixFirst.projected}</strong>
            </p>
          </div>
        ) : null}
        {/* All four weighted dimensions together. Monochrome letter
            chips (U/A/B/F) are the legend for the canvas pin labels;
            the category colour lives on the findings-list section
            headers, not here. Only shown when the rail isn't already
            carrying these numbers (collapsed rail / export). */}
        {showSubscores && subscoreRows.length > 0 ? (
          <div className="cv-summary-block">
            <p className="cv-summary-block-label">Subscores</p>
            <div className="cv-summary-subscores">
              {subscoreRows.map((s) => (
                <div className="cv-summary-subscore" key={s.letter}>
                  <span className="cv-summary-subscore-chip">{s.letter}</span>
                  <div className="cv-summary-subscore-body">
                    <span className="cv-summary-subscore-label">{s.label}</span>
                    <span className="cv-summary-subscore-num">
                      {s.data.score}
                      <span className="cv-summary-subscore-of"> / 100</span>
                    </span>
                    <span className="cv-summary-subscore-from">
                      from {s.data.findings}{" "}
                      {s.data.findings === 1 ? "finding" : "findings"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {(palette.length > 0 || fonts.length > 0) && (
          <div className="cv-summary-block">
            <p className="cv-summary-block-label">Brand</p>
            <div className="cv-summary-tokens">
              {palette.length > 0 && (
                <div>
                  <h4>Palette</h4>
                  <div className="cv-summary-palette">
                    {palette.slice(0, 12).map((c) => (
                      <div className="cv-summary-swatch" key={c}>
                        <span style={{ background: c }} />
                        <span>{c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {fonts.length > 0 && (
                <div>
                  <h4>Fonts</h4>
                  <ul className="cv-summary-fonts">
                    {fonts.slice(0, 8).map((f) => (
                      <li key={f} style={{ fontFamily: `${f}, system-ui` }}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </article>
      {result.presence ? (
        <FirstImpressionSection
          presence={result.presence}
          siteName={result.siteName || host}
          host={host}
          presenceScore={score?.presence?.score}
        />
      ) : null}
    </div>
  );
}

/* ================================================================== */
/*  First impression — pre-click presence (search + social share)      */
/* ================================================================== */

function FirstImpressionSection({
  presence,
  siteName,
  host,
  presenceScore,
}: {
  presence: SitePresence;
  siteName: string;
  host: string;
  presenceScore?: number;
}) {
  const SEARCH_IDS = new Set(["title", "meta-description", "indexable", "structured-data"]);
  const SHARED_IDS = new Set(["share-image", "open-graph", "favicon"]);
  const searchChecks = presence.checks.filter((c) => SEARCH_IDS.has(c.id));
  const sharedChecks = presence.checks.filter((c) => SHARED_IDS.has(c.id));

  const favicon = presence.faviconPath || presence.faviconUrl;
  const shareImg = presence.ogImagePath || presence.ogImageUrl;
  const serpTitle = presence.title || siteName || host;
  const serpDesc = presence.description;
  const shareTitle = presence.ogTitle || presence.title || siteName || host;
  const shareDesc = presence.ogDescription || presence.description;

  return (
    <section className="cv-presence">
      <header className="cv-presence-head">
        <p className="cv-page-eyebrow">First impression</p>
        {typeof presenceScore === "number" ? (
          <span className="cv-presence-score" title="First-impression subscore">
            {presenceScore}<span className="cv-presence-score-max">/100</span>
          </span>
        ) : null}
      </header>

      <article className="cv-presence-card">
        <h3>In search</h3>
        <p className="cv-presence-sub">How {host} appears on Google</p>
        <div className="cv-serp">
          <div className="cv-serp-head">
            {favicon ? (
              <img className="cv-serp-favicon" src={favicon} alt="" draggable={false} />
            ) : (
              <span className="cv-serp-favicon cv-serp-favicon-empty" aria-hidden="true" />
            )}
            <span className="cv-serp-url">{host}</span>
          </div>
          <div className="cv-serp-title">{serpTitle}</div>
          <div className="cv-serp-desc" data-empty={serpDesc ? undefined : "true"}>
            {serpDesc ||
              "No description set — Google writes its own snippet from page text."}
          </div>
        </div>
        <PresenceChecks checks={searchChecks} />
      </article>

      <article className="cv-presence-card">
        <h3>When shared</h3>
        <p className="cv-presence-sub">Your link on Slack, iMessage, X &amp; LinkedIn</p>
        <div className="cv-share">
          {shareImg ? (
            <img className="cv-share-img" src={shareImg} alt="" draggable={false} />
          ) : (
            <div className="cv-share-img cv-share-img-empty">No preview image</div>
          )}
          <div className="cv-share-body">
            <span className="cv-share-domain">{host}</span>
            <span className="cv-share-title">{shareTitle}</span>
            {shareDesc ? <span className="cv-share-desc">{shareDesc}</span> : null}
          </div>
        </div>
        <PresenceChecks checks={sharedChecks} />
      </article>
    </section>
  );
}

function PresenceChecks({ checks }: { checks: SitePresenceCheck[] }) {
  if (!checks.length) return null;
  const issues = checks.filter((c) => c.status !== "pass");
  const passes = checks.filter((c) => c.status === "pass");
  return (
    <div className="cv-presence-checks">
      {issues.map((c) => (
        <div key={c.id} className="cv-presence-issue" data-status={c.status}>
          <span className="cv-presence-issue-icon" aria-hidden="true">
            {c.status === "fail" ? "✗" : "!"}
          </span>
          <span className="cv-presence-issue-text">
            <span className="cv-presence-issue-label">{c.label}</span>
            {c.observation ? (
              <span className="cv-presence-issue-obs">{c.observation}</span>
            ) : null}
          </span>
        </div>
      ))}
      {passes.length ? (
        <div className="cv-presence-passes">
          {passes.map((c) => (
            <span key={c.id} className="cv-presence-pass">
              {c.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ================================================================== */
/*  Layout helpers (unchanged from original)                            */
/* ================================================================== */

function buildLayout(result: AnalyzeResult): {
  pages: PageLayout[];
  totalWidth: number;
  totalHeight: number;
  summaryX: number;
} {
  const allPages: CapturedPage[] = [result.homepage, ...(result.pages ?? [])].filter(Boolean) as CapturedPage[];
  const viewports = result.viewports ?? ["desktop"];

  const summaryX = PAGE_PAD;
  let cursorX = summaryX + SUMMARY_WIDTH + SECTION_GAP;
  let maxBottom = 0;

  const pages: PageLayout[] = [];
  for (let i = 0; i < allPages.length; i++) {
    const page = allPages[i];
    const shotsLayout = buildShotsLayout(page, viewports);
    // Shots sit side-by-side, each as a full solo block (shot + card wings).
    // The page block spans all of them horizontally and is as tall as the
    // tallest shot block.
    const wingWidth = CALLOUT_CARD_WIDTH + CALLOUT_GAP;
    const last = shotsLayout[shotsLayout.length - 1];
    const contentWidth = last ? last.offsetX + SHOT_BLOCK_WIDTH(last.width) : 0;
    // Width spanning just the shot images (used for header placement).
    const shotsTotalWidth = last ? last.offsetX + last.width : 0;
    const shotsTotalHeight = Math.max(
      ...shotsLayout.map((s) => s.blockHeight),
      280,
    );
    const headerHeight = 140 + (page.description ? 44 : 0);
    // Reserve vertical space for the findings panel so the canvas scroll
    // bounds always cover it. Generous per-finding height because the
    // redesigned cards (bordered, padded, with the "All findings" title
    // above) are taller than the old compact rows. Over-reserving only adds
    // harmless scroll space; under-reserving would clip the panel bottom.
    const maxFindingColumn = Math.max(
      page.heuristicFindings?.length ?? 0,
      page.accessibilityFindings?.length ?? 0,
      page.bestPracticeFindings?.length ?? 0,
    );
    const auditHeight = maxFindingColumn > 0 ? 480 + maxFindingColumn * 140 : 0;
    // The fixed-width analysis panel sits below the shots; the block must be
    // wide enough to contain it (matters for narrow mobile captures).
    const panelExtent = auditHeight ? ANALYSIS_PANEL_LEFT + ANALYSIS_PANEL_WIDTH : 0;
    const blockWidth = Math.max(contentWidth, SUMMARY_WIDTH + wingWidth * 2, panelExtent);

    // Anchor the findings panel below the DESKTOP shot (it lives in the
    // desktop column, well left of the mobile block), so it isn't pushed
    // far down by a much-taller mobile capture. Fall back to the first /
    // tallest shot when there's no desktop viewport (mobile-only audits).
    const desktopShot = shotsLayout.find((s) => s.shot.viewport === "desktop");
    const anchorHeight =
      desktopShot?.blockHeight ?? shotsLayout[0]?.blockHeight ?? shotsTotalHeight;
    const analysisTop = headerHeight + anchorHeight + ANALYSIS_GAP;

    // Block bottom is whichever extends lower: the tallest shot column, or
    // the panel now tucked under the desktop shot.
    const shotsBottom = headerHeight + shotsTotalHeight;
    const panelBottom = auditHeight ? analysisTop + auditHeight : 0;
    const blockHeight = Math.max(shotsBottom, panelBottom);

    pages.push({
      page,
      index: i,
      x: cursorX,
      y: PAGE_PAD,
      shotsX: cursorX + wingWidth,
      shotsY: PAGE_PAD + headerHeight,
      shotsWidth: shotsTotalWidth,
      shotsHeight: shotsTotalHeight,
      analysisTop,
      width: blockWidth,
      height: blockHeight,
      shots: shotsLayout,
      // Every page renders solo-style now (card wings + vertical stacking),
      // so this flag is always true. Kept for the ShotBlock contract.
      isSolo: true,
      hasAudit: !!auditHeight,
    });
    cursorX += blockWidth + SECTION_GAP;
    if (PAGE_PAD + blockHeight > maxBottom) maxBottom = PAGE_PAD + blockHeight;
  }

  // The left column (Summary + optional First-impression cards) isn't measured
  // here, so estimate its height generously and floor totalHeight with it —
  // clipping the column is far worse than a little extra scroll space under a
  // short single-page audit.
  let leftColumnHeight = 90 /* header */ + 300 /* summary card base */;
  if (result.review) {
    leftColumnHeight += Math.min(320, Math.ceil(result.review.length / 52) * 22);
  }
  if (
    (result.tokens?.palette?.length ?? 0) > 0 ||
    (result.tokens?.fonts?.length ?? 0) > 0
  ) {
    leftColumnHeight += 120;
  }
  if (result.presence) {
    const issues = result.presence.checks.filter((c) => c.status !== "pass").length;
    // eyebrow + the two cards (headings + SERP/share mockups + chrome) + checks
    leftColumnHeight += 40 + 660 + issues * 72;
  }

  return {
    pages,
    totalWidth: cursorX + PAGE_PAD,
    totalHeight: Math.max(maxBottom, leftColumnHeight, 800) + PAGE_PAD,
    summaryX,
  };
}

// Width of one shot's full solo block: the shot plus a callout-card column
// on each side.
const SHOT_BLOCK_WIDTH = (shotWidth: number) =>
  CALLOUT_CARD_WIDTH + CALLOUT_GAP + shotWidth + CALLOUT_GAP + CALLOUT_CARD_WIDTH;

function buildShotsLayout(page: CapturedPage, viewports: Viewport[]): ShotLayout[] {
  const allAnnotations = collectShotAnnotations(page, viewports);
  const out: ShotLayout[] = [];
  // Lay viewports out side-by-side (desktop | mobile). Every shot uses the
  // full solo width + callout-card wings, so multi-viewport audits show the
  // same annotated cards as single-viewport ones — not bare dots.
  let cursorX = 0;
  for (const v of viewports) {
    const shot = page.shots.find((s) => s.viewport === v);
    if (!shot) continue;
    const width = v === "desktop" ? DESKTOP_SOLO_WIDTH : MOBILE_SOLO_WIDTH;
    const ratio = shot.height / shot.width;
    const height = width * ratio;
    const annsForVp = pickAnnotations(allAnnotations.filter((a) => a.viewport === v));
    // The callout columns can extend below the shot; the block is as tall as
    // the taller of the two so the page block height fits every card. Only
    // card-bearing annotations occupy a column.
    const cardAnns = annsForVp.filter((a) => a.hasCard);
    const left = cardAnns.filter((a) => a.bbox.x + a.bbox.width / 2 < 50);
    const right = cardAnns.filter((a) => a.bbox.x + a.bbox.width / 2 >= 50);
    const leftCol = layoutCalloutColumn(left, height);
    const rightCol = layoutCalloutColumn(right, height);
    const colBottom = Math.max(
      leftCol.length ? leftCol[leftCol.length - 1].bottom : 0,
      rightCol.length ? rightCol[rightCol.length - 1].bottom : 0,
    );
    const blockHeight = Math.max(height, colBottom) + 28;
    out.push({
      shot,
      width,
      height,
      isGridMode: false,
      annotations: annsForVp,
      offsetX: cursorX,
      blockHeight,
    });
    cursorX += SHOT_BLOCK_WIDTH(width) + VIEWPORT_SIDE_GAP;
  }
  return out;
}

// Map a raw heuristic finding to the canvas annotation shape. `bbox` may be a
// zero placeholder for findings the AI couldn't ground — those never render a
// pin, but still appear in the right-rail list.
function heuristicToAnnotation(
  f: HeuristicFinding,
  primary: Viewport
): AuditAnnotation {
  return {
    viewport: f.viewport ?? primary,
    // Prefer the AI's short headline; older audits without one fall back to
    // deriving a (tightened) title from the observation. The heuristic name
    // itself still shows in the expanded meta line via heuristicLineFor.
    // Locked teaser findings carry only a 2-word snippet — use it as-is.
    title: f.locked
      ? (f.title?.trim() ?? "")
      : f.title?.trim() || deriveIssueTitle(f.observation, f.heuristicName),
    observation: f.observation,
    severity: f.severity,
    bbox: f.bbox ?? { x: 0, y: 0, width: 0, height: 0 },
    source: f,
    kind: "heuristic",
    number: 0,
    label: "",
    hasCard: false,
    category: `#${f.heuristicNumber} ${f.heuristicName.split(":")[0] || "UX"}`,
    whyItMatters: f.observation,
    suggestedFix: "",
    locked: f.locked,
  };
}

function accessibilityToAnnotation(
  f: AccessibilityFinding,
  primary: Viewport
): AuditAnnotation {
  return {
    viewport: f.viewport ?? primary,
    // Prefer the AI's short headline; fall back to deriving one. The WCAG
    // rule label still surfaces in the Rule meta line.
    title: f.locked
      ? (f.title?.trim() ?? "")
      : f.title?.trim() ||
        deriveIssueTitle(f.observation, `WCAG ${f.wcagId} · ${f.wcagName}`),
    observation: f.observation,
    severity: f.severity,
    bbox: f.bbox ?? { x: 0, y: 0, width: 0, height: 0 },
    source: f,
    kind: "accessibility",
    number: 0,
    label: "",
    hasCard: false,
    category: `${f.principle} / ${f.wcagName}`,
    whyItMatters: f.observation,
    suggestedFix: "",
    locked: f.locked,
  };
}

function bestPracticeToAnnotation(
  f: BestPracticeFinding,
  primary: Viewport
): AuditAnnotation {
  return {
    viewport: f.viewport ?? primary,
    title: f.locked
      ? (f.title?.trim() ?? "")
      : f.title?.trim() || deriveIssueTitle(f.observation, f.practiceName),
    observation: f.observation,
    severity: f.severity,
    bbox: f.bbox ?? { x: 0, y: 0, width: 0, height: 0 },
    source: f,
    kind: "bestpractice",
    number: 0,
    label: "",
    hasCard: false,
    category: `${f.category} / ${f.practiceName}`,
    whyItMatters: f.observation,
    suggestedFix: "",
    locked: f.locked,
  };
}

function collectShotAnnotations(page: CapturedPage, viewports: Viewport[]): AuditAnnotation[] {
  const primary = viewports.includes("desktop") ? "desktop" : "mobile";
  const out: AuditAnnotation[] = [];
  // Only findings with a bbox can be pinned to a screenshot.
  for (const f of page.heuristicFindings ?? []) {
    if (!f.bbox) continue;
    out.push(heuristicToAnnotation(f, primary));
  }
  for (const f of page.accessibilityFindings ?? []) {
    if (!f.bbox) continue;
    out.push(accessibilityToAnnotation(f, primary));
  }
  for (const f of page.bestPracticeFindings ?? []) {
    if (!f.bbox) continue;
    out.push(bestPracticeToAnnotation(f, primary));
  }
  return out;
}

// Returns EVERY bbox'd annotation (so each gets a numbered dot), flagging
// only the top picks with `hasCard` so they also get a callout card. The
// card budget is the highest-severity findings, capped per shot and per
// side to keep the canvas readable; the rest still show as findable
// numbered dots. Numbering runs top→bottom across all dots so the dot
// order matches reading order down the page.
function pickAnnotations(all: AuditAnnotation[]): AuditAnnotation[] {
  // 1. Choose card-bearers: highest severity first, capped overall + per side.
  const bySeverity = [...all].sort((a, b) => {
    const d = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    return d !== 0 ? d : a.bbox.y + a.bbox.height / 2 - (b.bbox.y + b.bbox.height / 2);
  });
  const cardSet = new Set<AuditAnnotation>();
  let l = 0, r = 0;
  for (const a of bySeverity) {
    if (cardSet.size >= MAX_SCREENSHOT_ANNOTATIONS) break;
    const cx = a.bbox.x + a.bbox.width / 2;
    if (cx < 50) { if (l >= MAX_CALLOUTS_PER_SIDE) continue; l++; }
    else { if (r >= MAX_CALLOUTS_PER_SIDE) continue; r++; }
    cardSet.add(a);
  }

  // 2. Number every annotation top→bottom and flag the card-bearers.
  //    Two sequences: a global `number` (used for ordering math elsewhere),
  //    and a typed `label` like "H1" / "A2" / "C3" that the user actually
  //    reads. The typed label keeps heuristic vs. accessibility findings
  //    distinguishable on the canvas without recycling the WCAG
  //    conformance-level letters (A / AA / AAA) — picking literal letters
  //    for accessibility was tempting but would collide with those
  //    spec-defined levels in the same UI surface.
  const ordered = [...all].sort(
    (a, b) => a.bbox.y + a.bbox.height / 2 - (b.bbox.y + b.bbox.height / 2),
  );
  const perKindCount: Record<AuditAnnotation["kind"], number> = {
    heuristic: 0,
    accessibility: 0,
    bestpractice: 0,
    custom: 0,
  };
  ordered.forEach((a, i) => {
    a.number = i + 1;
    perKindCount[a.kind] += 1;
    a.label = `${kindPrefix(a.kind)}${perKindCount[a.kind]}`;
    a.hasCard = cardSet.has(a);
  });
  return ordered;
}

/** Single-character prefix for the typed pin label. U = usability
 *  (NN/g heuristic), A = accessibility (WCAG), C = custom. We pick
 *  "U" rather than "H" so the letter matches the user-facing
 *  category name ("Usability") shown in the analysis panel and the
 *  rail — a reader sees U3 and immediately maps it to "Usability"
 *  without needing to remember that the underlying source is the
 *  NN/g heuristic set. Kept centralised so the canvas dot, rail
 *  badge, and analysis cards never drift apart. */
function kindPrefix(kind: AuditAnnotation["kind"]): "U" | "A" | "B" | "C" {
  if (kind === "heuristic") return "U";
  if (kind === "accessibility") return "A";
  if (kind === "bestpractice") return "B";
  return "C";
}

function estimateCardHeight(a: AuditAnnotation): number {
  const titleLines = Math.max(1, Math.ceil(a.title.length / 28));
  const bodyLines = Math.max(1, Math.ceil(a.observation.length / 32));
  return 14 * 2 + titleLines * 18 + bodyLines * 18 + 14 + 14;
}

function layoutCalloutColumn(
  cards: AuditAnnotation[],
  shotHeight: number,
): Array<{ a: AuditAnnotation; top: number; bottom: number }> {
  const placed: Array<{ a: AuditAnnotation; top: number; bottom: number }> = [];
  let nextY = 28;
  const sorted = [...cards].sort((a, b) => a.bbox.y + a.bbox.height / 2 - (b.bbox.y + b.bbox.height / 2));
  for (const a of sorted) {
    const cardHeight = estimateCardHeight(a);
    const idealY = 28 + ((a.bbox.y + a.bbox.height / 2) / 100) * shotHeight - cardHeight / 2;
    const top = Math.max(nextY, idealY);
    placed.push({ a, top, bottom: top + cardHeight });
    nextY = top + cardHeight + CALLOUT_STACK_GAP;
  }
  return placed;
}

interface FindingEntry {
  pageIndex: number;
  /** Shot index for placed (pinned) findings; -1 for unplaced ones. */
  shotIndex: number;
  annotation: AuditAnnotation;
  /** True when the finding has a canvas pin; false for list-only findings
      the AI couldn't ground to a location. */
  placed: boolean;
  /** Stable id. Placed ids match their pin (`page-shot-kind-number`) so
      row↔pin highlighting lines up; unplaced ids are list-only. */
  id: string;
}

// Collect EVERY finding the audit produced — both the ones pinned to a
// screenshot and the ones the AI couldn't place a bounding box on. Pinned
// findings are deduped against the raw list by source-object reference, so
// each finding appears exactly once. This keeps the right-rail count (and
// the score card) equal to the audit's true finding count, matching the
// dashboard, while the canvas still only draws dots for placed findings.
function collectFindings(
  layout: { pages: PageLayout[] },
  viewports: Viewport[]
): FindingEntry[] {
  const primary: Viewport = viewports.includes("desktop") ? "desktop" : "mobile";
  const out: FindingEntry[] = [];
  for (const page of layout.pages) {
    const placedSources = new Set<unknown>();
    page.shots.forEach((shot, sIdx) => {
      for (const a of shot.annotations) {
        placedSources.add(a.source);
        out.push({
          pageIndex: page.index,
          shotIndex: sIdx,
          annotation: a,
          placed: true,
          id: `${page.index}-${sIdx}-${a.kind}-${a.number}`,
        });
      }
    });
    // Unplaced findings — listed without a pin, in raw discovery order.
    (page.page.heuristicFindings ?? []).forEach((f, i) => {
      if (placedSources.has(f)) return;
      out.push({
        pageIndex: page.index,
        shotIndex: -1,
        annotation: heuristicToAnnotation(f, primary),
        placed: false,
        id: `${page.index}-u-h${i}`,
      });
    });
    (page.page.accessibilityFindings ?? []).forEach((f, i) => {
      if (placedSources.has(f)) return;
      out.push({
        pageIndex: page.index,
        shotIndex: -1,
        annotation: accessibilityToAnnotation(f, primary),
        placed: false,
        id: `${page.index}-u-a${i}`,
      });
    });
    (page.page.bestPracticeFindings ?? []).forEach((f, i) => {
      if (placedSources.has(f)) return;
      out.push({
        pageIndex: page.index,
        shotIndex: -1,
        annotation: bestPracticeToAnnotation(f, primary),
        placed: false,
        id: `${page.index}-u-b${i}`,
      });
    });
  }
  return out;
}

/* ================================================================== */
/*  Cross-viewport dedup                                                */
/* ================================================================== */
//
// The audit runs per viewport, so the SAME issue (e.g. a low-contrast footer)
// is found once on desktop and again on mobile. For the on-canvas dots that's
// correct — each shot needs its own pin. But for the headline count, the Snap
// Site Score, the rail list, and the export it must count ONCE, or running two
// viewports silently doubles the findings and craters the score (the score is
// literally 100 − Σ penalties). These helpers collapse findings that share a
// viewport-independent identity. Dedup is always scoped to a single page —
// the same issue on two different pages is two findings.

function normalizeTargetText(s: string | undefined): string {
  return (s ?? "").toLowerCase().replace(/\s+/g, " ").trim();
}

/** Viewport-independent identity of a finding: its rule + the element it
 *  points at. Two findings with the same signature are the same issue. */
function heuristicSig(f: HeuristicFinding): string {
  return `h${f.heuristicNumber}|${normalizeTargetText(f.targetDescription ?? f.title)}`;
}
function accessibilitySig(f: AccessibilityFinding): string {
  return `${f.wcagId}|${normalizeTargetText(f.targetDescription ?? f.title)}`;
}
function bestPracticeSig(f: BestPracticeFinding): string {
  return `${f.practiceId}|${normalizeTargetText(f.targetDescription ?? f.title)}`;
}

/** Signature for a collected FindingEntry. Custom (user-placed) findings are
 *  never collapsed — each one is intentional, so they key on their own id. */
function findingEntrySig(e: FindingEntry): string {
  const a = e.annotation;
  if (a.kind === "custom") return `custom|${a.customId ?? e.id}`;
  if (a.kind === "heuristic") return `h:${heuristicSig(a.source as HeuristicFinding)}`;
  if (a.kind === "bestpractice")
    return `b:${bestPracticeSig(a.source as BestPracticeFinding)}`;
  return `a:${accessibilitySig(a.source as AccessibilityFinding)}`;
}

/** Collapse FindingEntries that are the same issue across viewports, scoped
 *  per page. Keeps the strongest representative (higher severity, then a
 *  placed/pinned one over an unplaced one) and preserves first-seen order. */
function dedupeFindingEntries(entries: FindingEntry[]): FindingEntry[] {
  const best = new Map<string, FindingEntry>();
  const order: string[] = [];
  for (const e of entries) {
    const key = `${e.pageIndex}|${findingEntrySig(e)}`;
    const existing = best.get(key);
    if (!existing) {
      best.set(key, e);
      order.push(key);
      continue;
    }
    const sameSev = e.annotation.severity === existing.annotation.severity;
    const better =
      SEVERITY_RANK[e.annotation.severity] < SEVERITY_RANK[existing.annotation.severity] ||
      // Guest teaser: when an issue was found on two viewports, keep the
      // unlocked (free-sample) representative so the rail shows the readable
      // one rather than its locked cross-viewport twin.
      (sameSev && !e.annotation.locked && existing.annotation.locked) ||
      (sameSev &&
        !!e.annotation.locked === !!existing.annotation.locked &&
        e.placed &&
        !existing.placed);
    if (better) best.set(key, e);
  }
  return order.map((k) => best.get(k)!);
}

/** Unique severities per page-finding-array, collapsing viewport duplicates by
 *  signature and keeping the highest severity per issue. Drives computeScore. */
function uniqueSeverities<T extends { severity: HeuristicSeverity }>(
  list: T[],
  sig: (t: T) => string,
): HeuristicSeverity[] {
  const best = new Map<string, HeuristicSeverity>();
  for (const f of list) {
    const k = sig(f);
    const cur = best.get(k);
    if (!cur || SEVERITY_RANK[f.severity] < SEVERITY_RANK[cur]) best.set(k, f.severity);
  }
  return [...best.values()];
}

/* ================================================================== */
/*  Findings export — plain text + markdown                            */
/* ================================================================== */

export type ExportFormat = "markdown" | "plain";

/** Header metadata for an export — mirrors the left-rail overview so a
 *  copied report carries the same context the canvas shows. */
interface FindingsExportMeta {
  siteName: string;
  url: string;
  score: number | null;
  grade: string | null;
  total: number;
  high: number;
  medium: number;
  low: number;
  /** Present when pages were excluded from the score (blocked/blank
   *  captures) — a copied report must carry the same caveat the canvas
   *  shows, or the shared text overclaims its coverage. */
  coverage: string | null;
  generatedBy: string;
}

/** Human-readable provenance per finding kind, used in both formats. */
const EXPORT_KIND_LABEL: Record<AuditAnnotation["kind"], string> = {
  heuristic: "Usability (NN/g heuristics)",
  accessibility: "Accessibility (WCAG)",
  bestpractice: "UX best practice",
  custom: "Custom note",
};

/** Group findings by their page (in page order), sorting each page's
 *  findings by severity (high → low) then by typed label so the export
 *  reads in the same priority order the rail presents. Returns an ordered
 *  list of { pageName, items } so the formatters can stay format-only. */
function groupFindingsForExport(
  findings: FindingEntry[],
  pageNameByIndex: Record<number, string>,
): Array<{ pageName: string; items: FindingEntry[] }> {
  const byPage = new Map<number, FindingEntry[]>();
  for (const f of findings) {
    const bucket = byPage.get(f.pageIndex);
    if (bucket) bucket.push(f);
    else byPage.set(f.pageIndex, [f]);
  }
  return [...byPage.keys()]
    .sort((a, b) => a - b)
    .map((pageIndex) => ({
      pageName: pageNameByIndex[pageIndex] ?? `Page ${pageIndex + 1}`,
      items: [...(byPage.get(pageIndex) ?? [])].sort((a, b) => {
        const sev =
          SEVERITY_RANK[a.annotation.severity] -
          SEVERITY_RANK[b.annotation.severity];
        if (sev !== 0) return sev;
        return a.annotation.label.localeCompare(b.annotation.label, undefined, {
          numeric: true,
        });
      }),
    }));
}

/** Build a well-organized export of every finding in either markdown or
 *  plain text. Both formats share the same structure (audit header →
 *  per-page sections → per-finding blocks with severity, category,
 *  source, observation, why-it-matters, and suggested fix) so the only
 *  difference users perceive is the markup. Optional fields are omitted
 *  when empty, and a "why it matters" line that just echoes the
 *  observation (common on custom notes) is suppressed to avoid noise. */
function buildFindingsExportText(
  format: ExportFormat,
  findings: FindingEntry[],
  pageNameByIndex: Record<number, string>,
  meta: FindingsExportMeta,
): string {
  const groups = groupFindingsForExport(findings, pageNameByIndex);
  const md = format === "markdown";
  const lines: string[] = [];

  // ── Header ──
  const title = `UX & Accessibility Audit — ${meta.siteName}`;
  if (md) {
    lines.push(`# ${title}`, "");
    // Branding up top, evidently — a copied report is a funnel touchpoint.
    lines.push(`**Generated by [${BRAND.name}](${BRAND.url})** · ${BRAND.domain}`);
    lines.push(`_${BRAND.tagline}_`, "");
    lines.push(`**Site:** ${meta.url}`);
    if (meta.score != null) {
      lines.push(
        `**Score:** ${meta.score}/100${meta.grade ? ` (${meta.grade})` : ""}`,
      );
    }
    if (meta.coverage) {
      lines.push(`**Coverage:** ${meta.coverage}`);
    }
    lines.push(
      `**Findings:** ${meta.total} — ${meta.high} high · ${meta.medium} medium · ${meta.low} low`,
    );
    lines.push("", `> ${AUDIT_CAVEAT}`);
    lines.push("", "---", "");
  } else {
    lines.push(title.toUpperCase());
    lines.push("=".repeat(Math.min(title.length, 64)), "");
    lines.push(`Generated by ${BRAND.name} · ${BRAND.url}`);
    lines.push(BRAND.tagline, "");
    lines.push(`Site: ${meta.url}`);
    if (meta.score != null) {
      lines.push(
        `Score: ${meta.score}/100${meta.grade ? ` (${meta.grade})` : ""}`,
      );
    }
    if (meta.coverage) {
      lines.push(`Coverage: ${meta.coverage}`);
    }
    lines.push(
      `Findings: ${meta.total} (${meta.high} high, ${meta.medium} medium, ${meta.low} low)`,
    );
    lines.push("", AUDIT_CAVEAT, "");
  }

  // ── Per-page sections ──
  groups.forEach((group, gi) => {
    if (md) {
      lines.push(`## ${group.pageName}`, "");
    } else {
      if (gi > 0) lines.push("");
      lines.push(group.pageName);
      lines.push("-".repeat(Math.min(group.pageName.length, 64)), "");
    }

    group.items.forEach((entry) => {
      const a = entry.annotation;
      const why =
        a.whyItMatters && a.whyItMatters.trim() !== a.observation.trim()
          ? a.whyItMatters.trim()
          : "";
      const fix = a.suggestedFix ? a.suggestedFix.trim() : "";
      const sevLabel = SEVERITY_LABEL[a.severity];
      const kindLabel = EXPORT_KIND_LABEL[a.kind];

      if (md) {
        lines.push(`### ${a.label}. ${a.title}`);
        lines.push(
          `**Severity:** ${sevLabel}  ·  **Category:** ${a.category}  ·  **Source:** ${kindLabel}`,
          "",
        );
        if (a.observation.trim()) lines.push(a.observation.trim(), "");
        if (why) lines.push(`**Why it matters:** ${why}`, "");
        if (fix) lines.push(`**Suggested fix:** ${fix}`, "");
        lines.push("");
      } else {
        lines.push(`[${a.label}] ${a.title}`);
        lines.push(
          `   Severity: ${sevLabel} | Category: ${a.category} | Source: ${kindLabel}`,
          "",
        );
        if (a.observation.trim()) {
          lines.push(...indentLines(a.observation.trim(), "   "), "");
        }
        if (why) {
          lines.push("   Why it matters:");
          lines.push(...indentLines(why, "   "), "");
        }
        if (fix) {
          lines.push("   Suggested fix:");
          lines.push(...indentLines(fix, "   "), "");
        }
      }
    });
  });

  // ── Footer provenance + branding ──
  if (md) {
    lines.push("---", "");
    lines.push(`**${meta.generatedBy}** — [${BRAND.name}](${BRAND.url})`);
    lines.push(`Run your own audit at [${BRAND.domain}](${BRAND.url})`);
  } else {
    lines.push("");
    lines.push(`${meta.generatedBy} — ${BRAND.name}`);
    lines.push(`Run your own audit at ${BRAND.url}`);
  }

  // Collapse any run of 3+ blank lines down to a single blank line so the
  // optional-field omissions above never leave large gaps.
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
}

/** Prefix every line of a (possibly multi-line) string with `pad`. Used by
 *  the plain-text formatter so wrapped observations stay visually nested
 *  under their finding. */
function indentLines(text: string, pad: string): string[] {
  return text.split("\n").map((line) => (line ? pad + line : line));
}

// Append custom annotations to the AI-derived layout. Renumbering continues
// from where AI's per-shot picks left off so dots count up sequentially. The
// AI cap (MAX_SCREENSHOT_ANNOTATIONS / per-side) doesn't apply to customs —
// the user explicitly placed them, so they're always shown.
function decorateLayoutWithCustoms(
  layout: ReturnType<typeof buildLayout>,
  customs: CustomAnnotationApi[]
): ReturnType<typeof buildLayout> {
  if (customs.length === 0) return layout;
  return {
    ...layout,
    pages: layout.pages.map((p) => ({
      ...p,
      shots: p.shots.map((s) => {
        const matching = customs.filter(
          (c) => c.pageIndex === p.index && c.viewport === s.shot.viewport
        );
        if (matching.length === 0) return s;
        const next = [...s.annotations];
        let n = next.length;
        // Per-kind sequence for the typed label (C1, C2, …). Start
        // from the count of customs already on this shot so newly
        // added ones continue the sequence without colliding.
        let customSeq = next.filter((a) => a.kind === "custom").length;
        for (const c of matching) {
          n += 1;
          customSeq += 1;
          next.push({
            viewport: c.viewport,
            title: c.title,
            observation: c.description,
            severity: c.severity,
            bbox: c.bbox,
            source: c,
            kind: "custom",
            number: n,
            label: `${kindPrefix("custom")}${customSeq}`,
            // User-placed annotations always get a card — they're never
            // subject to the AI card budget.
            hasCard: true,
            category: "Custom",
            whyItMatters: c.description,
            suggestedFix: "",
            customId: c.id,
          });
        }
        return { ...s, annotations: next };
      }),
    })),
  };
}

/**
 * Snap Site Score breakdown — overall plus the per-category math
 * the score was built from. Each category (usability + accessibility)
 * starts at 100 and loses points by severity (high: -12, medium: -5,
 * low: -2), clamped to 0; the overall is the average.
 *
 * Returning the per-severity counts alongside each subscore lets the
 * UI tie the headline number directly to the findings: "Usability 59
 * — from 8 findings (1 high · 5 medium · 2 low)" instead of forcing
 * the reader to infer where the points went.
 */
interface SubscoreBreakdown {
  score: number;
  findings: number;
  high: number;
  medium: number;
  low: number;
}

interface ScoreBreakdown {
  overall: number;
  /** True when a high-severity finding capped the grade below "Excellent"
   *  (overall clamped to 89) — surfaced in the score tooltip so the number
   *  is explainable. */
  capped: boolean;
  /** Per-page pooled scores — the numbers `overall` averages. `pageIndex`
   *  is the page's position in [homepage, ...pages] (the same convention
   *  FindingEntry.pageIndex uses), so consumers can map findings to the
   *  page score they deduct from. Drives the fix-first projection. */
  pageScores: Array<{ pageIndex: number; score: number }>;
  // Category tallies across all scored pages. INFORMATIONAL — they group
  // the same findings by category for the breakdown display; they no longer
  // feed `overall` (which is the per-page pooled average).
  ux: SubscoreBreakdown;
  a11y: SubscoreBreakdown;
  bestPractices: SubscoreBreakdown;
  // "First impression" (pre-click presence). Informational only — shown in
  // its own panel, not part of the score (the plugin has no presence data,
  // and the three scorers must agree).
  presence?: SubscoreBreakdown;
}

const SCORE_PENALTY = { high: 12, medium: 5, low: 2 } as const;

function tallySeverities(sev: HeuristicSeverity[]): SubscoreBreakdown {
  let high = 0, med = 0, low = 0;
  for (const s of sev) {
    if (s === "high") high++; else if (s === "medium") med++; else low++;
  }
  const score = Math.max(
    0,
    Math.min(
      100,
      100 - high * SCORE_PENALTY.high - med * SCORE_PENALTY.medium - low * SCORE_PENALTY.low,
    ),
  );
  return { score, findings: sev.length, high, medium: med, low };
}

// Exported for the guest /try unlock modal (guest-audit-client), which
// shows "Snap Site Score: N · Grade" beside the findings count — the same
// numbers this component puts in the rail, from the same function, so the
// two surfaces can't drift.
export function computeScore(result: AnalyzeResult): ScoreBreakdown | undefined {
  // Excluded: blocked / blank captures — they describe our capture limits,
  // not the visitor experience (humans walk past bot walls). Included:
  // `notfound` pages — a 404 reached via the site's own links is real
  // evidence, and the backend attaches a deterministic broken-link finding
  // to it, so including the page is what lets that finding lower the score.
  // BUT only when at least one page genuinely rendered: with zero rendered
  // pages there is nothing to anchor a score to, and a lone soft-404 would
  // otherwise ship a surreal "95 · Good" next to the refund modal. Keep in
  // lockstep with backend jobs.ts:computeScore and plugin
  // code.ts:computeAuditScore.
  const allPages = [result.homepage, ...(result.pages ?? [])].filter(Boolean);
  const renderedPages = allPages.filter(
    (p) => p.captureQuality?.scorable !== false,
  );
  if (!renderedPages.length) return undefined;
  const pages = allPages.filter(
    (p) =>
      p.captureQuality?.scorable !== false ||
      p.captureQuality?.status === "notfound",
  );
  // Per-page pooled scoring. Every page starts at 100 and its own findings
  // deduct from it (high −12, medium −5, low −2, all categories pooled;
  // viewport duplicates deduped per page first). The site score is the
  // AVERAGE of page scores. Why this shape (replacing the old cross-page
  // accumulation into weighted category averages):
  //   1. Page-count fairness — a 10-page audit of a site with one minor
  //      issue per page used to score far below a 1-page audit of the same
  //      site. Auditing more pages (a paid action!) must not, by itself,
  //      lower the score.
  //   2. Sitewide template issues — one footer problem repeated on every
  //      page used to deduct N× from one shared tally; averaged per-page it
  //      deducts once per page but is bounded by the mean.
  //   3. Monotonicity — the old "component only counts when it has
  //      findings" weighting could RAISE the overall when a low-severity
  //      finding appeared (61 → 69). Pooled deduction can only go down.
  // Category tallies below are kept for the breakdown display; presence is
  // informational only. GRADE CAP: any high-severity finding caps the
  // overall at 89 — averaging across pages must never dilute a serious
  // problem into an "Excellent". Keep in lockstep with backend
  // jobs.ts:computeScore and plugin code.ts:computeAuditScore.
  const scored = allPages
    .map((p, pageIndex) => ({ p, pageIndex }))
    .filter(
      ({ p }) =>
        p.captureQuality?.scorable !== false ||
        p.captureQuality?.status === "notfound",
    );
  const pageTallies = scored.map(({ p, pageIndex }) => ({
    pageIndex,
    tally: tallySeverities([
      ...uniqueSeverities(p.heuristicFindings ?? [], heuristicSig),
      ...uniqueSeverities(p.accessibilityFindings ?? [], accessibilitySig),
      ...uniqueSeverities(p.bestPracticeFindings ?? [], bestPracticeSig),
    ]),
  }));
  const pageScores = pageTallies.map((t) => ({
    pageIndex: t.pageIndex,
    score: t.tally.score,
  }));
  const mean = Math.round(
    pageScores.reduce((s, v) => s + v.score, 0) / (pageScores.length || 1),
  );
  const anyHigh = pageTallies.some((t) => t.tally.high > 0);
  const overall = anyHigh ? Math.min(mean, 89) : mean;
  const capped = anyHigh && mean > 89;

  // Category tallies (informational breakdown — same findings, grouped by
  // category instead of by page).
  const ux = tallySeverities(
    pages.flatMap((p) => uniqueSeverities(p.heuristicFindings ?? [], heuristicSig)),
  );
  const a11y = tallySeverities(
    pages.flatMap((p) => uniqueSeverities(p.accessibilityFindings ?? [], accessibilitySig)),
  );
  const bestPractices = tallySeverities(
    pages.flatMap((p) => uniqueSeverities(p.bestPracticeFindings ?? [], bestPracticeSig)),
  );
  // First-impression tally from the warn/fail presence checks — feeds the
  // First impression panel, not the score.
  const presence = result.presence
    ? tallySeverities(
        result.presence.checks
          .filter((c) => c.status !== "pass" && c.severity)
          .map((c) => c.severity as HeuristicSeverity),
      )
    : undefined;
  return { overall, capped, pageScores, ux, a11y, bestPractices, presence };
}

/**
 * Map a numeric Snap Site Score to a short human grade label.
 * The bands match common audit-tool conventions (Lighthouse-style)
 * so users coming from other tools have an immediate frame of reference.
 */
export function scoreToGrade(score: number): {
  label: string;
  tone: "excellent" | "good" | "needs-work" | "poor" | "critical";
} {
  if (score >= 90) return { label: "Excellent", tone: "excellent" };
  if (score >= 75) return { label: "Good", tone: "good" };
  if (score >= 60) return { label: "Needs work", tone: "needs-work" };
  if (score >= 40) return { label: "Poor", tone: "poor" };
  return { label: "Critical", tone: "critical" };
}

type ScoreTone = "excellent" | "good" | "needs-work" | "poor" | "critical";

/** Fallback summary lines, keyed by grade band, for when the backend's
 *  AI review is missing (snapshot-only runs or a failed generation). These
 *  are deliberately generic — they restate the grade in prose and point to
 *  the findings, never inventing specific observations. */
const GRADE_SUMMARY: Record<ScoreTone, string> = {
  excellent:
    "Strong across the board — only minor refinements remain. See the findings below for the specifics.",
  good: "Solid fundamentals, with a handful of issues worth tightening. See the findings below for the specifics.",
  "needs-work":
    "A few meaningful issues are holding this back. Start with the high-severity findings below.",
  poor: "Several significant issues need attention. Start with the high-severity findings below.",
  critical:
    "Major issues were found across the experience. Start with the high-severity findings below.",
};

/**
 * Circular score ring shared by the left-rail hero and the canvas
 * summary card so both render the Snap Site Score identically. The arc
 * sweeps proportional to the score and is tinted by grade tone; the
 * number sits centred. `size` is the rendered px box — the SVG geometry
 * is fixed (r=52 in a 120 viewBox) and scales into it.
 */
function ScoreRing({
  score,
  tone = "good",
  size = 96,
}: {
  score: number | null;
  tone?: ScoreTone;
  size?: number;
}) {
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score));
  const circ = 2 * Math.PI * 52;
  const offset = circ * (1 - pct / 100);
  return (
    <div className="cv-ring" style={{ width: size, height: size }}>
      <svg viewBox="0 0 120 120" className="cv-ring-svg" aria-hidden="true">
        <circle className="cv-ring-track" cx="60" cy="60" r="52" />
        <circle
          className="cv-ring-arc"
          data-tone={tone}
          cx="60"
          cy="60"
          r="52"
          style={{ strokeDasharray: circ, strokeDashoffset: offset }}
        />
      </svg>
      <span className="cv-ring-num" style={{ fontSize: Math.round(size * 0.31) }}>
        {score == null ? "–" : score}
      </span>
    </div>
  );
}

const GENERIC_FONT_NAMES = new Set([
  "serif", "sans-serif", "monospace", "cursive", "fantasy", "system-ui",
  "ui-sans-serif", "ui-serif", "ui-monospace", "ui-rounded", "ui", "sans",
  "-apple-system", "apple-system", "blinkmacsystemfont", "segoe ui",
  "helvetica", "helvetica neue", "arial", "inherit", "initial", "unset",
  "revert", "emoji", "math",
]);

function filterDisplayFonts(fonts: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of fonts) {
    const cleaned = f.trim().replace(/^['"]|['"]$/g, "");
    const key = cleaned.toLowerCase();
    if (!cleaned || GENERIC_FONT_NAMES.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(cleaned);
  }
  return out;
}

function hostnameOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

/**
 * Compact URL for the left-rail page list. Drops the scheme and `www.`
 * prefix so users don't read "https://www.google.com/" four times in a
 * row when all captured pages live on the same domain. Falls back to
 * the raw URL on parse failure.
 */
function formatPageUrlShort(url: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    const pathAndQuery =
      (u.pathname === "/" ? "" : u.pathname) +
      (u.search || "") +
      (u.hash || "");
    return host + pathAndQuery;
  } catch {
    return url.replace(/^https?:\/\/(www\.)?/i, "");
  }
}

/* ================================================================== */
/*  Icons                                                               */
/* ================================================================== */

function ChevronLeftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function MonitorIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function PagesIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

function ExportIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function FeedbackIcon() {
  // Speech bubble — the perpetual "Give feedback" entry point at the
  // bottom of the left rail (also reachable on mobile via the
  // Overview sheet, which re-surfaces the same rail).
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />
    </svg>
  );
}

function MoreIcon() {
  // Three-dot kebab — used as the trigger for the mobile topbar
  // overflow popover (zoom controls + share + export folded into
  // a menu at narrow widths so the topbar buttons don't get
  // clipped off-screen).
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  );
}

function CloseIcon() {
  // X glyph used for the mobile sheet close button. Stroked rather
  // than filled to match the other topbar icons.
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function FitIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
    </svg>
  );
}

function FullscreenIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

function ExitFullscreenIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 3 9 9 3 9" /><polyline points="15 21 15 15 21 15" /><line x1="3" y1="9" x2="10" y2="2" /><line x1="21" y1="15" x2="14" y2="22" />
    </svg>
  );
}

function LayoutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

function SummaryIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20V10" /><path d="M18 20V4" /><path d="M6 20v-4" />
    </svg>
  );
}

// Marker for findings that aren't tied to a single spot on the screenshot
// (page-wide issues, or ones the AI couldn't ground), so they have no
// numbered dot on the canvas. A small page glyph instead of a blank circle.
/* Sidebar collapse / expand icon — pared back to just a vertical
   bar + chevron. The earlier "rectangle with bar inside" version
   was too detailed at 16-18px and read as visual noise. The bar
   represents the rail's inner edge; the chevron points the way
   the panel will move when clicked. */
function PanelToggleIcon({ side, collapsed }: { side: "left" | "right"; collapsed: boolean }) {
  // Bar always sits on the rail's INNER edge of the icon (the
  // side touching the canvas). Chevron flips by side+collapsed:
  // - Left rail expanded: bar at x=20, chevron points left
  //   (panel collapses left)
  // - Left rail collapsed: bar at x=20, chevron points right
  //   (panel expands right)
  // - Right rail mirrors
  const barOnRight = side === "left";
  const pointingRight = side === "left" ? collapsed : !collapsed;
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* Vertical bar — represents the rail's edge */}
      <line x1={barOnRight ? 20 : 4} y1="4" x2={barOnRight ? 20 : 4} y2="20" />
      {/* Chevron pointing inward (toward the bar) when expanded,
          outward (away from the bar) when collapsed. */}
      {pointingRight
        ? <path d="M9 7 L14 12 L9 17" />
        : <path d="M15 7 L10 12 L15 17" />}
    </svg>
  );
}

function PageLevelIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <line x1="8" y1="8" x2="16" y2="8" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="8" y1="16" x2="13" y2="16" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/** Warning triangle — used for the capture-failure overview card and the
 *  per-page "couldn't capture" badge. Same size/stroke family as CheckIcon
 *  so it drops into the .cv-overview-clean-icon circle and the page-count
 *  pill without looking foreign. */
function AlertTriangleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

/** Camera icon — used in the snapshot empty-state to signal "this run
 *  was screenshots only, not a UX audit." Same stroke weight + size
 *  as CheckIcon so it slots into the .cv-overview-clean-icon circle
 *  without looking out of place. */
function CameraIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function BookmarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

/** Small sparkle / AI marker. Used by the "AI-suggested findings" note in
 *  the findings panel header so the row reads as an AI tag, not a warning. */
function SparkleIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v3" />
      <path d="M12 18v3" />
      <path d="M3 12h3" />
      <path d="M18 12h3" />
      <path d="m5.6 5.6 2.1 2.1" />
      <path d="m16.3 16.3 2.1 2.1" />
      <path d="m5.6 18.4 2.1-2.1" />
      <path d="m16.3 7.7 2.1-2.1" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

function ShotUnavailable() {
  return (
    <div className="cv-shot-unavailable" aria-label="Screenshot unavailable">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
      <p>Screenshot unavailable</p>
      <span>This page was indexed without a captured screenshot.</span>
    </div>
  );
}

function CopyIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

/**
 * Action bar for the expanded finding card. Two-row layout:
 *   1. Wide primary "Mark as solved" CTA (full width).
 *   2. Inline secondary actions row — Save for later, Copy finding,
 *      and (custom only) Edit / Delete.
 *
 * Viewer mode collapses to a single "Copy finding" button. All
 * interactive elements carry data-canvas-interactive so the canvas
 * pan handler doesn't swallow their pointer events.
 */
function FindingActions({
  isOwner,
  isBookmarked,
  isSolved,
  isCustom,
  copyText,
  onToggleBookmark,
  onToggleSolved,
  onEdit,
  onDelete,
}: {
  isOwner: boolean;
  isBookmarked: boolean;
  isSolved: boolean;
  isCustom: boolean;
  copyText: string;
  onToggleBookmark: () => void;
  onToggleSolved: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  function copyToClipboard(e: React.MouseEvent) {
    e.stopPropagation();
    void navigator.clipboard.writeText(copyText);
  }

  // Viewer mode — single Copy action.
  if (!isOwner) {
    return (
      <div className="cv-finding-actions-bar">
        <button
          type="button"
          className="cv-finding-action-primary"
          data-canvas-interactive
          onClick={copyToClipboard}
        >
          <CopyIcon /> Copy finding
        </button>
      </div>
    );
  }

  return (
    <div className="cv-finding-actions-bar">
      <button
        type="button"
        className={`cv-finding-action-primary${isSolved ? " is-marked" : ""}`}
        data-canvas-interactive
        onClick={(e) => {
          e.stopPropagation();
          onToggleSolved();
        }}
        aria-pressed={isSolved}
        aria-label={
          isSolved
            ? "Mark this finding as not yet solved"
            : "Mark this finding as solved — it will move to the Solved section"
        } data-tip={
          isSolved
            ? "Mark this finding as not yet solved"
            : "Mark this finding as solved — it will move to the Solved section"
        }
      >
        <CheckIcon /> {isSolved ? "Unmark" : "Mark as solved"}
      </button>

      {/* Secondary row. Save (bookmark) used to live here, but
          the row header above already has a bookmark toggle —
          duplicating it inside the expanded card was noise. Left
          side: Copy finding (one-shot ghost action). Right side
          (custom findings only): Edit + Delete, with Delete
          quietly red-tinted so destructive intent reads at a
          glance without screaming. */}
      <div className="cv-finding-actions-secondary">
        <button
          type="button"
          className="cv-finding-action-secondary cv-finding-action-secondary--ghost"
          data-canvas-interactive
          onClick={copyToClipboard}
          aria-label="Copy finding title, severity, and description to clipboard" data-tip="Copy finding title, severity, and description to clipboard"
        >
          <CopyIcon />
          Copy finding
        </button>

        {(isCustom && (onEdit || onDelete)) ? (
          <div className="cv-finding-actions-owner">
            {onEdit ? (
              <button
                type="button"
                className="cv-finding-action-icon"
                data-canvas-interactive
                data-tip="Edit this custom finding"
                aria-label="Edit this custom finding"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <EditIcon />
              </button>
            ) : null}
            {onDelete ? (
              <button
                type="button"
                className="cv-finding-action-icon is-danger"
                data-canvas-interactive
                data-tip="Delete this custom finding"
                aria-label="Delete this custom finding"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <TrashIcon />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
